import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { createPortal, flushSync } from 'react-dom'
import { TextSelection } from '@tiptap/pm/state'
import useStore from 'state/knovStore'
import styles from './ai-magic-wand-menu-button.module.scss'
import cn from 'classnames'
import { debounce } from 'lodash'
import {
    Image as ImageIcon,
    Video as VideoIcon,
    Music as MusicIcon,
    ArrowRight,
    AudioLines,
    ChevronDown,
    Clock,
    Film,
    Gauge,
    Mic2,
    MoveHorizontal,
    Paperclip,
    PenLine,
    Plus,
    Ratio,
    Scissors,
    Sparkles,
    Type,
    Wrench,
    Terminal,
    X,
    Camera,
    Search,
} from 'lucide-react'
import {
    buildActionModalDefinitions,
    buildActionDefinitions,
    isHiddenActionCommandTag,
} from 'state/actionDefinitions'
import type { ActionDefinition, ActionSectionId } from 'state/actionDefinitions'
import { DEFAULT_VIDEO_MODEL } from 'state/defaultAiModels'
import { partitionActionDefinitionsByFold } from 'components/quests/aiModelMenuOrdering'
import {
    AiModelPricingContext,
    IAiModel,
    actionDisplayNameForModel,
    canonicalActionTagFor,
    findEffectiveModelForAction,
    getActionPricingContext,
    getPrivateModelCostWithDuration,
    getPublicModelCostWithDuration,
} from 'state/aiModels'
import WithTooltip from 'components/shared/WithTooltip'
import AiModelBrandAvatar from 'components/quests/AiModelBrandAvatar'
import { aiModelBrandNameFor } from 'state/aiModelBrands'
import PromptEditor, { type PromptReferenceMention } from 'components/shared/PromptEditor'
import AiMediaPreviewModal, {
    type AiMediaPreviewTarget,
} from 'components/shared/AiMediaPreviewModal'
import { promptCharacterLimitForAiAction } from 'components/shared/aiActionPromptValidation'
import {
    buildDraftInputAttrsForModel,
    type AiMagicWandCurrentDraftInputs,
} from 'components/quests/aiMagicWandDraftInputs'
import { musicDurationControlPresentation } from 'components/quests/musicDurationControl'
import {
    getAiModelDefaultMetadataParts,
    getDefaultAspectRatioForActionCommand,
} from 'utils/actionCommandMetadata'
import {
    deserializePromptReferenceMentions,
    promptReferenceLabelsForAttrs,
    serializePromptReferenceMentions,
} from 'utils/aiPromptReferences'
import { dedupeMediaInputsByKeys } from 'utils/mediaInputDedupe'
import { haveMatchingMediaIdentity, mediaInputIdentityValues } from 'utils/mediaInputIdentity'
import { sourceAwareVideoAspectRatio } from 'utils/videoSourceAspectRatio'
import {
    endFrameRequiresStartFrame,
    explicitVideoKeyframePayload,
    supportsExplicitVideoKeyframes,
    videoKeyframeInputs,
    videoKeyframeInputsFromFormState,
    videoKeyframeModeForFormState,
} from 'utils/aiVideoKeyframes'
import { v4 as uuid } from 'uuid'
import { isMobile } from 'lib/device'

const ACTION_PROMPT_SENTINEL = '\u00a0'
const MAX_ACTION_REQUEST_SELECTIONS = 4

// A phone tap in the Models dialog (a card, a filter, Cancel, Add) leaves the
// composer's editor focused, so the keyboard it was writing with stays up and
// the pill does not fold under it. Only the dialog's own text fields take focus.
const keepComposerFocusOnPhoneTap = (event: React.MouseEvent) => {
    if (!isMobile) return
    const target = event.target as Element | null
    if (target?.closest?.('input, textarea, select, [contenteditable]')) return
    event.preventDefault()
}

export function AiImageButtonIconSvg({
    color = 'currentColor',
    style = {},
    className = '',
    width = 24,
    height = 24,
}) {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={style}
            className={className}
        >
            <defs />
            <g transform="matrix(-1,0,0,1,24.00387,0)">
                <path d="M 21.64,3.64 20.36,2.36 a 1.21,1.21 0 0 0 -1.72,0 L 2.36,18.64 a 1.21,1.21 0 0 0 0,1.72 l 1.28,1.28 a 1.2,1.2 0 0 0 1.72,0 L 21.64,5.36 a 1.2,1.2 0 0 0 0,-1.72" />
                <path d="m 14,7 3,3" />
                <path d="m 5,6 v 4" />
                <path d="m 19,14 v 4" />
                <path d="M 10,2 V 4" />
                <path d="M 7,8 H 3" />
                <path d="M 21,16 H 17" />
                <path d="M 11,3 H 9" />
            </g>
        </svg>
    )
}

interface AiMagicWandMenuButtonProps {
    getEditor: () => any
    getCurrentTextInput?: () => string
    getCurrentImageInput?: () => AiInputImage | null
    getCurrentVideoInput?: () => AiInputVideo | null
    getCurrentAudioInput?: () => AiInputAudio | null
    getThreadTextInputs?: () => AiInputText[]
    getCraftedPromptInputs?: () => AiInputText[]
    getThreadImageInputs?: () => AiInputImage[]
    getThreadVideoInputs?: () => AiInputVideo[]
    getThreadAudioInputs?: () => AiInputAudio[]
    getThreadTreeMediaInputs?: () => Promise<{
        texts?: AiInputText[]
        images?: AiInputImage[]
        videos?: AiInputVideo[]
        audios?: AiInputAudio[]
    }>
    onImageInputAttached?: (event: React.ChangeEvent<HTMLInputElement>) => AiInputImage | null
    onVideoInputAttached?: (event: React.ChangeEvent<HTMLInputElement>) => AiInputVideo | null
    onAudioInputAttached?: (event: React.ChangeEvent<HTMLInputElement>) => AiInputAudio | null
    questIsPublic: boolean
    contextStyles?: string
    imageStyles?: string
    triggerTestId?: string
    menuTestId?: string
    onMenuStateChange?: (isOpen: boolean) => void
    onActionRequestSelected?: (
        actionRequest: string | ActionRequestSelection | Array<string | ActionRequestSelection>,
    ) => void
    // The composer's palette menu carries the "Models" row that opens this
    // menu, so the wand renders no trigger of its own there and is opened
    // through the ref instead. The menu still portals to the body.
    hideTrigger?: boolean
}

export interface AiMagicWandMenuHandle {
    open: () => void
    close: () => void
    isOpen: () => boolean
}

type EditActionRequestCandidate = {
    handle: () => void
    order: number
    score: number
}

type EditActionRequestDetail = Record<string, any> & {
    __treechatAiActionEditCandidates?: EditActionRequestCandidate[]
    __treechatAiActionEditHandled?: boolean
    __treechatAiActionEditQueued?: boolean
    editSourceElement?: Element | null
}

let aiActionEditHostSequence = 0

function elementCenterDistance(left: Element | null, right: Element | null) {
    if (!left || !right) return Number.MAX_SAFE_INTEGER

    const leftRect = left.getBoundingClientRect()
    const rightRect = right.getBoundingClientRect()
    if (!leftRect.width && !leftRect.height) return Number.MAX_SAFE_INTEGER
    if (!rightRect.width && !rightRect.height) return Number.MAX_SAFE_INTEGER

    const leftX = leftRect.left + leftRect.width / 2
    const leftY = leftRect.top + leftRect.height / 2
    const rightX = rightRect.left + rightRect.width / 2
    const rightY = rightRect.top + rightRect.height / 2

    return Math.hypot(leftX - rightX, leftY - rightY)
}

function queueEditActionRequestCandidate(
    detail: EditActionRequestDetail,
    candidate: EditActionRequestCandidate,
) {
    detail.__treechatAiActionEditCandidates ||= []
    detail.__treechatAiActionEditCandidates.push(candidate)

    if (detail.__treechatAiActionEditQueued) return
    detail.__treechatAiActionEditQueued = true

    queueMicrotask(() => {
        if (detail.__treechatAiActionEditHandled) return

        const candidates = detail.__treechatAiActionEditCandidates || []
        const selected = candidates.sort(
            (left, right) => left.score - right.score || right.order - left.order,
        )[0]

        if (!selected) return
        detail.__treechatAiActionEditHandled = true
        selected.handle()
    })
}

export interface AiInputImage {
    id: string
    url: string
    label?: string
    file?: File | null
    contentType?: string
    source?: string | null
    uploadId?: string | null
    signedId?: string | null
    attachmentId?: string | null
    blobId?: string | null
    answerId?: string | null
    runId?: string | null
    rootRemixAiRunId?: string | null
    actionId?: string | null
}

const imageInputFileKey = (file?: File | null) => {
    if (!file) return ''
    return [file.name, file.size, file.lastModified, file.type].join(':')
}

const normalizeImageInputUrl = (url?: string | null) => {
    if (!url) return ''
    if (url.startsWith('data:') || url.startsWith('blob:')) return url

    try {
        const parsedUrl = new URL(url, window.location.origin)
        parsedUrl.search = ''
        parsedUrl.hash = ''
        return parsedUrl.toString()
    } catch (e) {
        return url.split('?')[0].split('#')[0]
    }
}

type DedupeableMediaInput = NonNullable<Parameters<typeof mediaInputIdentityValues>[0]> & {
    file?: File | null
    url?: string | null
}

const dedupeMediaInputs = <T extends DedupeableMediaInput>(inputs: T[]) =>
    dedupeMediaInputsByKeys(inputs, input => ({
        identityValues: mediaInputIdentityValues(input),
        fileKey: imageInputFileKey(input.file),
        urlKey: normalizeImageInputUrl(input.url),
    }))

const areSameImageInput = (left: AiInputImage, right: AiInputImage) => {
    if (left === right) return true
    if (left.id && right.id && left.id === right.id) return true
    if (haveMatchingMediaIdentity(left, right)) return true

    const leftFileKey = imageInputFileKey(left.file)
    const rightFileKey = imageInputFileKey(right.file)
    if (leftFileKey && rightFileKey && leftFileKey === rightFileKey) return true

    if (left.url && right.url) {
        if (left.url === right.url) return true
        return normalizeImageInputUrl(left.url) === normalizeImageInputUrl(right.url)
    }

    return false
}

const dedupeImageInputs = (images: AiInputImage[]) => dedupeMediaInputs(images)

const mediaInputFileKey = (file?: File | null) => {
    if (!file) return ''
    return [file.name, file.size, file.lastModified, file.type].join(':')
}

const normalizeMediaInputUrl = normalizeImageInputUrl

const areSameVideoInput = (left: AiInputVideo, right: AiInputVideo) => {
    if (left === right) return true
    if (left.id && right.id && left.id === right.id) return true
    if (haveMatchingMediaIdentity(left, right)) return true

    const leftFileKey = mediaInputFileKey(left.file)
    const rightFileKey = mediaInputFileKey(right.file)
    if (leftFileKey && rightFileKey && leftFileKey === rightFileKey) return true

    if (left.url && right.url) {
        if (left.url === right.url) return true
        return normalizeMediaInputUrl(left.url) === normalizeMediaInputUrl(right.url)
    }

    return false
}

const dedupeVideoInputs = (videos: AiInputVideo[]) => dedupeMediaInputs(videos)

const areSameAudioInput = (left: AiInputAudio, right: AiInputAudio) => {
    if (left === right) return true
    if (left.id && right.id && left.id === right.id) return true
    if (haveMatchingMediaIdentity(left, right)) return true

    const leftFileKey = mediaInputFileKey(left.file)
    const rightFileKey = mediaInputFileKey(right.file)
    if (leftFileKey && rightFileKey && leftFileKey === rightFileKey) return true

    if (left.url && right.url) {
        if (left.url === right.url) return true
        return normalizeMediaInputUrl(left.url) === normalizeMediaInputUrl(right.url)
    }

    return false
}

const dedupeAudioInputs = (audios: AiInputAudio[]) => dedupeMediaInputs(audios)

const excludeImageInputs = (images: AiInputImage[], excludedImages: AiInputImage[]) =>
    images.filter(image => !excludedImages.some(excluded => areSameImageInput(image, excluded)))

const excludeVideoInputs = (videos: AiInputVideo[], excludedVideos: AiInputVideo[]) =>
    videos.filter(video => !excludedVideos.some(excluded => areSameVideoInput(video, excluded)))

const excludeAudioInputs = (audios: AiInputAudio[], excludedAudios: AiInputAudio[]) =>
    audios.filter(audio => !excludedAudios.some(excluded => areSameAudioInput(audio, excluded)))

const excludeTextInputs = (texts: AiInputText[], excludedTexts: AiInputText[]) => {
    const excludedTextValues = new Set(
        excludedTexts.map(input => input.text?.trim()).filter((text): text is string => !!text),
    )
    return texts.filter(input => {
        const text = input.text?.trim()
        return !!text && !excludedTextValues.has(text)
    })
}

export interface AiInputText {
    id: string
    text: string
    label?: string | null
}

export interface AiInputVideo {
    id: string
    url: string
    label?: string
    file?: File | null
    contentType?: string
    source?: string | null
    uploadId?: string | null
    signedId?: string | null
    previewImageUrl?: string | null
    actionTag?: string | null
    durationSeconds?: number | null
    width?: number | null
    height?: number | null
    attachmentId?: string | null
    blobId?: string | null
    answerId?: string | null
    runId?: string | null
    rootRemixAiRunId?: string | null
    actionId?: string | null
}

function aiInputVideoWithMetadata(input: AiInputVideo): Promise<AiInputVideo> {
    if (input.durationSeconds && input.durationSeconds > 0) return Promise.resolve(input)
    if (!input.url || typeof document === 'undefined') return Promise.resolve(input)

    return new Promise(resolve => {
        const video = document.createElement('video')
        let settled = false
        const finish = (metadata: Partial<AiInputVideo> = {}) => {
            if (settled) return
            settled = true
            window.clearTimeout(timeoutId)
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('error', handleError)
            video.removeAttribute('src')
            video.load()
            resolve({ ...input, ...metadata })
        }
        const handleLoadedMetadata = () =>
            finish({
                durationSeconds:
                    Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null,
                width:
                    Number.isFinite(video.videoWidth) && video.videoWidth > 0
                        ? video.videoWidth
                        : null,
                height:
                    Number.isFinite(video.videoHeight) && video.videoHeight > 0
                        ? video.videoHeight
                        : null,
            })
        const handleError = () => finish()
        const timeoutId = window.setTimeout(() => finish(), 5000)

        video.preload = 'metadata'
        video.muted = true
        video.playsInline = true
        video.addEventListener('loadedmetadata', handleLoadedMetadata)
        video.addEventListener('error', handleError)
        video.src = input.url
        video.load()
    })
}

export interface AiInputAudio {
    id: string
    url: string
    label?: string
    metaLabel?: string | null
    sortTimestamp?: number | null
    file?: File | null
    contentType?: string
    source?: string | null
    attachmentId?: string | null
    blobId?: string | null
    answerId?: string | null
    runId?: string | null
    rootRemixAiRunId?: string | null
    actionId?: string | null
    uploadId?: string | null
    signedId?: string | null
}

interface ModelSection {
    id: ActionSectionId
    label: string
    icon: React.ElementType
    items: ActionDefinition[]
}

type ModelFilterTab = ActionSectionId

function modelFilterTabForModelType(
    modelType: IAiModel['modelType'] | null | undefined,
): ModelFilterTab | null {
    if (modelType === 'image' || modelType === 'video' || modelType === 'audio') {
        return modelType
    }

    return null
}

type AiMediaInputLayout = 'list' | 'grid'

interface ModelSettings {
    durationSeconds?: number
    aspectRatio?: string
    generationCount?: number
    lyrics?: string
    gifFrameRate?: number
    gifMaxWidth?: number
    extendTimestamp?: string
    extendAiModelId?: string | null
    extendActionTag?: string
    smartStitch?: boolean
    screenshotTimestamp?: string
}

interface ActionRequestFormState {
    tag: string
    ai_action_id?: string | null
    actionId?: string | null
    promptContent?: string | null
    promptPlacement?: string | null
    prompt_placement?: string | null
    supportsPromptInput?: boolean
    displayName?: string | null
    actionDefinitionId?: string | null
    kind?: 'model' | 'tool'
    aiModelId?: string | null
    toolKey?: string | null
    aiRunId?: string | null
    aiRunIds?: string[] | null
    remixAiRunId?: string | null
    rootRemixAiRunId?: string | null
    remixKind?: 'edit' | 'animate' | 'extend' | 'remix' | null
    type?: 'model' | 'tool'
    modelType?: IAiModel['modelType'] | null
    defaultDurationSeconds?: number | null
    defaultAspectRatio?: string | null
    durationSeconds?: number | null
    aspectRatio?: string | null
    generationCount?: number | null
    gifFrameRate?: number | null
    gifMaxWidth?: number | null
    screenshotTimestamp?: string | null
    extendAiModelId?: string | null
    extendActionTag?: string | null
    extendTimestamp?: string | null
    smartStitch?: boolean | null
    hasTextInput?: boolean
    textInputValue?: string | null
    hasImageInput?: boolean
    imageInputUrl?: string | null
    imageInput?: AiInputImage | null
    referenceImageInputs?: AiInputImage[] | null
    videoKeyframeMode?: boolean | null
    hasVideoInput?: boolean
    videoInputUrl?: string | null
    videoPreviewImageUrl?: string | null
    videoInputActionTag?: string | null
    videoInput?: AiInputVideo | null
    videoInputs?: AiInputVideo[] | null
    referenceVideoInputs?: AiInputVideo[] | null
    hasAudioInput?: boolean
    audioInputUrl?: string | null
    audioInputSource?: string | null
    audioInputUploadId?: string | null
    audioInputSignedId?: string | null
    audioInputLabel?: string | null
    audioInputContentType?: string | null
    audioInputFile?: File | null
    audioInput?: AiInputAudio | null
    referenceAudioInputs?: AiInputAudio[] | null
    hasLyrics?: boolean
    lyrics?: string | null
    inputOrder?: string[] | null
    syncWithDraft?: boolean | null
    commandText?: string | null
    analyticsSource?: string | null
}

type AiActionPayload = Record<string, any>

type DirectPromptDraftCommitPayload = {
    actionDefinitionId: string
    attrs: Partial<ActionRequestFormState>
    existingAction: AiActionPayload | null
    nextText: string
    updater: (action: AiActionPayload) => void
}

interface ActionRequestSelection {
    tag: string
    commandText: string
    aiAction?: AiActionPayload
    attrs: ActionRequestFormState
    buildOptions?: {
        syncDraftInputs?: boolean
        useComposerPromptFallback?: boolean
        focusPromptOnInsert?: boolean
    }
}

interface ModelSettingOption<TValue extends string | number> {
    label: string
    value: TValue
    detail?: string
    disabled?: boolean
    disabledReason?: string
}

type ModelSettingTileValue = string | { value: string; unit?: string }

const FIRST_COMMAND_TAGS_BY_SECTION: Partial<Record<ModelSection['id'], string[]>> = {
    audio: ['minimaxmusic', 'suno', 'elevenmusic', 'stableaudio'],
}
const MODEL_DISPLAY_NAME_OVERRIDES: Record<string, string> = {
    illustrate: 'Illustrate',
    film: 'Film',
    nb: 'Nano Banana 2',
    nbpro: 'Nano Banana Pro',
    gptimage2: 'OpenAI Image 2',
    'gptimage2.5': 'OpenAI Image 2.5 Flare',
    'gptimage2.5sunburst': 'OpenAI Image 2.5 Sunburst',
    flux2: 'Flux 2',
    grokimage: 'Grok Imagine 2',
    seedance: 'Seedance 2',
    seedance2: 'Seedance 2',
    h3: 'MiniMax H3',
    kling: 'Kling 4',
    kling3: 'Kling 3',
    kling4: 'Kling 4',
    grokvideo: 'Grok Imagine Video',
    'grokvideo1.5': 'Grok Imagine Video 1.5',
    gif: 'GIF',
    screenshot: 'Screenshot',
    stitch: 'Smart Stitch',
    extend: 'Extend',
    elevenmusic: 'Eleven Music',
    minimaxmusic: 'MiniMax Music 3.0',
    stableaudio: 'Stable Audio 2.5',
}
const MUSIC_ACTION_TAGS = new Set(['minimaxmusic', 'suno'])
const PUBLIC_PRICE_FALLBACK_COMMAND_TAGS = new Set(['nb'])
const GENERATION_COUNT_OPTIONS: ModelSettingOption<number>[] = [1, 2, 3, 4].map(count => ({
    label: String(count),
    value: count,
}))
const UTILITY_COMMAND_TAGS = new Set([
    'illustrate',
    'film',
    'gif',
    'extend',
    'screenshot',
    'stitch',
])
const FALLBACK_EXTEND_COMMAND_TAG = DEFAULT_VIDEO_MODEL.actionTag
const DEFAULT_SCREENSHOT_TIMESTAMP = '00:00'
const GIF_FRAME_RATE_OPTIONS: ModelSettingOption<number>[] = [
    { label: '10 fps', value: 10 },
    { label: '12 fps', value: 12 },
    { label: '15 fps', value: 15 },
]
const GIF_MAX_WIDTH_OPTIONS: ModelSettingOption<number>[] = [
    { label: '480 px', value: 480 },
    { label: '600 px', value: 600 },
    { label: '720 px', value: 720 },
]
const DEFAULT_GIF_FRAME_RATE = 15
const DEFAULT_GIF_MAX_WIDTH = 720
const GIF_MAX_DURATION_SECONDS = 12
const GIF_MAX_PIXELS_PER_FRAME = 600 * 720
const GIF_MAX_TOTAL_PIXELS = GIF_MAX_DURATION_SECONDS * 12 * GIF_MAX_PIXELS_PER_FRAME
const DEFAULT_GIF_SOURCE_WIDTH = 1280
const DEFAULT_GIF_SOURCE_HEIGHT = 720
const IMAGE_ASPECT_RATIO_OPTIONS: ModelSettingOption<string>[] = [
    { label: 'Square', value: '1:1' },
    { label: 'Landscape', value: '16:9' },
    { label: 'Portrait', value: '9:16' },
    { label: 'Classic', value: '4:3' },
    { label: 'Tall', value: '3:4' },
]
const VIDEO_ASPECT_RATIO_OPTIONS: ModelSettingOption<string>[] = [
    { label: 'Landscape', value: '16:9' },
    { label: 'Portrait', value: '9:16' },
    { label: 'Square', value: '1:1' },
]
const FALLBACK_VIDEO_DURATION_OPTIONS = [5, 10, 15]
const MIN_VIDEO_DURATION_SECONDS = 5
const SCREENSHOT_SLIDER_STEP_SECONDS = 0.1
const VIDEO_FRAME_END_GUARD_SECONDS = 0.5
function sortByPriority<T extends { sortPriority?: number }>(
    items: T[],
    fallbackSort: (a: T, b: T) => number,
): T[] {
    return [...items].sort((a, b) => {
        if (a.sortPriority !== undefined && b.sortPriority !== undefined) {
            if (a.sortPriority !== b.sortPriority) return b.sortPriority - a.sortPriority
        } else if (a.sortPriority !== undefined) {
            return -1
        } else if (b.sortPriority !== undefined) {
            return 1
        }

        return fallbackSort(a, b)
    })
}

function priceNumber(price: number | string | null | undefined): number {
    const numericPrice = Number(price)
    return Number.isFinite(numericPrice) ? numericPrice : 0
}

function modelPrice(
    model: IAiModel,
    isPublic: boolean,
    pricingContext?: AiModelPricingContext | null,
): number {
    return priceNumber(
        isPublic
            ? getPublicModelCostWithDuration(model, null, pricingContext)
            : getPrivateModelCostWithDuration(model, null, pricingContext),
    )
}

function displayModelPrice(
    model: IAiModel,
    isPublic: boolean,
    pricingContext?: AiModelPricingContext | null,
): number {
    const price = modelPrice(model, isPublic, pricingContext)
    if (
        isPublic &&
        price <= 0 &&
        PUBLIC_PRICE_FALLBACK_COMMAND_TAGS.has(model.actionTagName) &&
        getPrivateModelCostWithDuration(model, null) > 0
    ) {
        return getPrivateModelCostWithDuration(model, null)
    }
    return price
}

function formatPrice(price: number | string | null | undefined): string {
    const numericPrice = priceNumber(price)
    if (!numericPrice || numericPrice <= 0) return 'Free'
    return `$${numericPrice.toFixed(2)}`
}

// The chooser row shows the figure with a quiet unit line under it: "$0.04" / "per image".
function modelChooserPricePresentation(
    model: IAiModel,
    isPublic: boolean,
): { amount: string; unit: string | null; free: boolean } {
    const pricePerSecond = model.modelType === 'video' ? modelPricePerSecond(model, isPublic) : null
    if (pricePerSecond) {
        return { amount: formatPrice(pricePerSecond), unit: 'per second', free: false }
    }

    const price = displayModelPrice(model, isPublic)
    if (!price || price <= 0) return { amount: 'Free', unit: null, free: true }

    const unit =
        model.modelType === 'image'
            ? 'per image'
            : model.modelType === 'video'
            ? 'per video'
            : model.modelType === 'audio'
            ? 'per track'
            : 'per run'
    return { amount: formatPrice(price), unit, free: false }
}

function modelDisplayName(model: IAiModel): string {
    return MODEL_DISPLAY_NAME_OVERRIDES[model.actionTagName] || model.humanName
}

function actionTokenDisplayName(model: ActionDefinition): string | null {
    return (
        model.displayName ||
        model.aiModel?.displayName ||
        actionDisplayNameForModel(model.aiModel || model, model.actionTagName) ||
        null
    )
}

function modelDefaultsParts(model: IAiModel): string[] {
    return getAiModelDefaultMetadataParts(model)
}

function modelSettingsParts(settings: ModelSettings | undefined): string[] {
    if (!settings) return []

    return [
        settings.generationCount && settings.generationCount > 1
            ? `x${settings.generationCount}`
            : null,
        settings.durationSeconds ? `${settings.durationSeconds}s` : null,
        settings.aspectRatio ? `AR ${settings.aspectRatio}` : null,
        settings.smartStitch ? 'Smart Stitch' : null,
        settings.gifFrameRate ? `${settings.gifFrameRate}fps` : null,
        settings.gifMaxWidth ? `${settings.gifMaxWidth}px` : null,
        settings.extendTimestamp?.trim() ? settings.extendTimestamp.trim() : null,
        settings.screenshotTimestamp?.trim() ? settings.screenshotTimestamp.trim() : null,
    ].filter(Boolean) as string[]
}

function aspectRatioOptionsForModel(model: IAiModel): ModelSettingOption<string>[] {
    if (model.modelType === 'audio') return []
    if (
        model.actionTagName === 'gif' ||
        model.actionTagName === 'screenshot' ||
        model.actionTagName === 'extend'
    )
        return []
    return model.modelType === 'video' ? VIDEO_ASPECT_RATIO_OPTIONS : IMAGE_ASPECT_RATIO_OPTIONS
}

function durationValuesForModel(model: IAiModel): number[] {
    if (model.actionTagName === 'gif' || model.actionTagName === 'screenshot') return []

    const rawDurations =
        model.allowedDurations && model.allowedDurations.length > 0
            ? [...model.allowedDurations, 15]
            : model.minDurationSeconds && model.maxDurationSeconds
            ? [model.minDurationSeconds, model.defaultDurationSeconds, model.maxDurationSeconds, 15]
            : model.modelType === 'video'
            ? [model.defaultDurationSeconds, ...FALLBACK_VIDEO_DURATION_OPTIONS]
            : []
    const durationsWithVideoMinimum =
        model.modelType === 'video' ? [MIN_VIDEO_DURATION_SECONDS, ...rawDurations] : rawDurations

    return Array.from(
        new Set(
            durationsWithVideoMinimum.filter(
                (duration): duration is number =>
                    typeof duration === 'number' &&
                    Number.isFinite(duration) &&
                    duration > 0 &&
                    (model.modelType !== 'video' || duration >= MIN_VIDEO_DURATION_SECONDS),
            ),
        ),
    ).sort((a, b) => a - b)
}

function modelPricePerSecond(model: IAiModel, isPublic: boolean): number | null {
    const duration = model.defaultDurationSeconds || 1
    const price = isPublic
        ? getPublicModelCostWithDuration(model, duration)
        : getPrivateModelCostWithDuration(model, duration)
    if (model.modelType === 'video' && duration > 0 && price > 0) {
        return price / duration
    }

    return null
}

function durationPriceForModel(
    model: IAiModel,
    isPublic: boolean,
    duration: number,
    pricingContext?: AiModelPricingContext | null,
): number {
    return isPublic
        ? getPublicModelCostWithDuration(model, duration, pricingContext)
        : getPrivateModelCostWithDuration(model, duration, pricingContext)
}

function durationOptionsForModel(
    model: IAiModel,
    isPublic: boolean,
    pricingContext?: AiModelPricingContext | null,
): ModelSettingOption<number>[] {
    return durationValuesForModel(model).map(duration => ({
        label: `${duration}s`,
        value: duration,
        detail: formatPrice(durationPriceForModel(model, isPublic, duration, pricingContext)),
    }))
}

function nearestNumberValue(
    values: Array<number | null | undefined>,
    preferred: number | null | undefined,
    fallback: number | null | undefined,
): number | undefined {
    const availableValues = values.filter(
        (value): value is number => typeof value === 'number' && Number.isFinite(value),
    )
    if (availableValues.length === 0) return undefined

    const preferredValue =
        typeof preferred === 'number' && Number.isFinite(preferred) ? preferred : fallback
    if (typeof preferredValue !== 'number' || !Number.isFinite(preferredValue)) {
        return availableValues[0]
    }

    return [...availableValues].sort(
        (left, right) =>
            Math.abs(left - preferredValue) - Math.abs(right - preferredValue) || left - right,
    )[0]
}

function aspectRatioNumber(value: string | null | undefined): number | null {
    const match = value?.match(/^(\d+(?:\.\d+)?):(\d+(?:\.\d+)?)$/)
    if (!match) return null

    const width = Number(match[1])
    const height = Number(match[2])
    if (!Number.isFinite(width) || !Number.isFinite(height) || height <= 0) return null

    return width / height
}

function nearestAspectRatioValue(
    options: ModelSettingOption<string>[],
    preferred: string | null | undefined,
    fallback: string | null | undefined,
): string | undefined {
    const values = options.map(option => option.value)
    if (values.length === 0) return undefined
    if (preferred && values.includes(preferred)) return preferred
    if (fallback && values.includes(fallback)) return fallback

    const preferredRatio = aspectRatioNumber(preferred) ?? aspectRatioNumber(fallback)
    if (!preferredRatio) return values[0]

    return [...values].sort((left, right) => {
        const leftRatio = aspectRatioNumber(left)
        const rightRatio = aspectRatioNumber(right)
        if (!leftRatio || !rightRatio) return 0
        return Math.abs(leftRatio - preferredRatio) - Math.abs(rightRatio - preferredRatio)
    })[0]
}

function preferredExtendModels(aiModels: IAiModel[], defaultAiModelId?: string | null): IAiModel[] {
    const videoModels = aiModels.filter(isSelectableExtendModel)
    const defaultModel =
        (defaultAiModelId ? videoModels.find(model => model.id === defaultAiModelId) : undefined) ||
        videoModels.find(model => model.actionTagName === FALLBACK_EXTEND_COMMAND_TAG)
    const sortedModels = sortByPriority(videoModels, (a, b) =>
        a.humanName.localeCompare(b.humanName),
    )

    return defaultModel
        ? [defaultModel, ...sortedModels.filter(model => model.id !== defaultModel.id)]
        : sortedModels
}

function latestExtendModelCommandTag(
    commandTag: string | null | undefined,
    aiModels: IAiModel[],
): string | null {
    const canonical = canonicalActionTagFor(commandTag)
    if (!canonical) return null

    return aiModels.some(
        model => model.actionTagName === canonical && isSelectableExtendModel(model),
    )
        ? canonical
        : null
}

function extendModelForModelId(modelId: string | null | undefined, aiModels: IAiModel[]) {
    if (!modelId) return null
    return aiModels.find(model => model.id === modelId) || null
}

function isSelectableExtendModel(model: IAiModel): boolean {
    return (
        model.modelType === 'video' &&
        !UTILITY_COMMAND_TAGS.has(model.actionTagName) &&
        !isHiddenActionCommandTag(model.actionTagName)
    )
}

function normalizeActionCommandTag(commandTag: string | null | undefined): string | null {
    const normalized = commandTag?.trim().replace(/^!+/, '').toLowerCase()
    return normalized || null
}

function isMusicActionTag(commandTag: string | null | undefined): boolean {
    return MUSIC_ACTION_TAGS.has(normalizeActionCommandTag(commandTag) || '')
}

function extendModelCommandTagForSourceCommandTag(
    sourceCommandTag: string | null | undefined,
    aiModels: IAiModel[],
): string | null {
    const canonicalSourceTag = canonicalActionTagFor(sourceCommandTag)
    if (!canonicalSourceTag) return null

    const sourceModel = aiModels.find(
        model => model.actionTagName === canonicalSourceTag && isSelectableExtendModel(model),
    )
    if (sourceModel) return sourceModel.actionTagName

    return preferredExtendModels(aiModels)[0]?.actionTagName || FALLBACK_EXTEND_COMMAND_TAG
}

function timestampToSeconds(timestamp: string | undefined): number {
    const value = timestamp?.trim()
    if (!value) return 0

    if (/^\d+(\.\d+)?$/.test(value)) {
        const seconds = Number(value)
        return Number.isFinite(seconds) ? Math.max(seconds, 0) : 0
    }

    const rawParts = value.split(':')
    if (
        rawParts.length < 2 ||
        rawParts.length > 3 ||
        rawParts.some((part, index) => {
            const isLastPart = index === rawParts.length - 1
            return isLastPart ? !/^\d+(\.\d+)?$/.test(part) : !/^\d+$/.test(part)
        })
    ) {
        return 0
    }
    const parts = rawParts.map(part => Number(part))

    if (parts.length === 2) {
        return Math.max(parts[0] * 60 + parts[1], 0)
    }
    if (parts.length === 3) {
        return Math.max(parts[0] * 3600 + parts[1] * 60 + parts[2], 0)
    }

    return 0
}

function clampVideoFrameTimestamp(secondsValue: number, durationSeconds?: number | null): number {
    const seconds = Number.isFinite(secondsValue) ? Math.max(secondsValue, 0) : 0
    if (!durationSeconds || durationSeconds <= 0) return seconds

    return Math.min(seconds, Math.max(durationSeconds - VIDEO_FRAME_END_GUARD_SECONDS, 0))
}

function timestampForActionCommand(timestamp: string | undefined): string {
    const hasDecimal = !!timestamp?.trim().match(/\.\d+/)
    return secondsToTimestamp(timestampToSeconds(timestamp), hasDecimal ? 1 : 0)
}

function secondsToTimestamp(secondsValue: number, decimalPlaces = 0): string {
    const multiplier = 10 ** decimalPlaces
    const totalSeconds = Math.max(Math.round(secondsValue * multiplier) / multiplier, 0)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const paddedMinutes = String(minutes).padStart(2, '0')
    const secondsText =
        decimalPlaces > 0
            ? seconds.toFixed(decimalPlaces).padStart(3 + decimalPlaces, '0')
            : String(Math.floor(seconds)).padStart(2, '0')

    return hours > 0
        ? `${hours}:${paddedMinutes}:${secondsText}`
        : `${paddedMinutes}:${secondsText}`
}

function defaultSettingsForModel(model: IAiModel): ModelSettings {
    const actionDefinition = model as Partial<ActionDefinition>
    const configuredDefaults = actionDefinition.defaultSettings || {}
    const defaultNumber = (key: string, fallback: number | undefined): number | undefined => {
        const value = configuredDefaults[key]
        const numericValue = Number(value)
        return Number.isFinite(numericValue) ? numericValue : fallback
    }
    const defaultString = (key: string, fallback: string | undefined): string | undefined => {
        const value = configuredDefaults[key]
        return typeof value === 'string' && value.trim() ? value : fallback
    }
    const defaultBoolean = (key: string, fallback: boolean): boolean => {
        const value = configuredDefaults[key]
        return typeof value === 'boolean' ? value : fallback
    }
    const durationValues = durationValuesForModel(model)
    const aspectRatioOptions = aspectRatioOptionsForModel(model)
    const defaultAspectRatio = defaultString(
        'aspectRatio',
        getDefaultAspectRatioForActionCommand(model.actionTagName) || aspectRatioOptions[0]?.value,
    )
    const defaultDurationSeconds = defaultNumber(
        'durationSeconds',
        model.defaultDurationSeconds || durationValues[0],
    )

    if (model.actionTagName === 'gif') {
        return {
            generationCount: defaultNumber('generationCount', 1),
            gifFrameRate: defaultNumber('gifFrameRate', DEFAULT_GIF_FRAME_RATE),
            gifMaxWidth: defaultNumber('gifMaxWidth', DEFAULT_GIF_MAX_WIDTH),
        }
    }

    if (model.actionTagName === 'extend') {
        const defaultAiModelId = actionDefinition.defaultAiModelId || null

        return {
            generationCount: defaultNumber('generationCount', 1),
            durationSeconds: defaultDurationSeconds,
            extendAiModelId: defaultAiModelId,
            extendActionTag: defaultAiModelId ? undefined : FALLBACK_EXTEND_COMMAND_TAG,
            smartStitch: defaultBoolean('smartStitch', true),
        }
    }

    if (model.actionTagName === 'screenshot') {
        return {
            generationCount: defaultNumber('generationCount', 1),
            screenshotTimestamp: defaultString('screenshotTimestamp', DEFAULT_SCREENSHOT_TIMESTAMP),
        }
    }

    return {
        generationCount: defaultNumber('generationCount', 1),
        durationSeconds: defaultDurationSeconds,
        aspectRatio: defaultAspectRatio,
    }
}

function normalizeModelSettingsForModel(
    model: IAiModel,
    settings: ModelSettings | undefined,
): ModelSettings {
    const defaults = defaultSettingsForModel(model)
    const merged = {
        ...defaults,
        ...(settings || {}),
    }
    const durationSeconds = nearestNumberValue(
        durationValuesForModel(model),
        merged.durationSeconds,
        defaults.durationSeconds,
    )
    const aspectRatio = nearestAspectRatioValue(
        aspectRatioOptionsForModel(model),
        merged.aspectRatio,
        defaults.aspectRatio,
    )
    const generationCount = nearestNumberValue(
        GENERATION_COUNT_OPTIONS.map(option => option.value),
        merged.generationCount,
        defaults.generationCount || 1,
    )
    const gifFrameRate =
        model.actionTagName === 'gif'
            ? nearestNumberValue(
                  GIF_FRAME_RATE_OPTIONS.map(option => option.value),
                  merged.gifFrameRate,
                  defaults.gifFrameRate,
              )
            : undefined
    const gifMaxWidth =
        model.actionTagName === 'gif'
            ? nearestNumberValue(
                  GIF_MAX_WIDTH_OPTIONS.map(option => option.value),
                  merged.gifMaxWidth,
                  defaults.gifMaxWidth,
              )
            : undefined

    return {
        generationCount,
        durationSeconds,
        aspectRatio,
        gifFrameRate,
        gifMaxWidth,
        lyrics: isMusicActionTag(model.actionTagName) ? merged.lyrics : undefined,
        extendTimestamp: model.actionTagName === 'extend' ? merged.extendTimestamp : undefined,
        extendAiModelId: model.actionTagName === 'extend' ? merged.extendAiModelId : undefined,
        extendActionTag: model.actionTagName === 'extend' ? merged.extendActionTag : undefined,
        smartStitch: model.actionTagName === 'extend' ? merged.smartStitch : undefined,
        screenshotTimestamp:
            model.actionTagName === 'screenshot' ? merged.screenshotTimestamp : undefined,
    }
}

function settingsWithNearestDurationForModel(
    settings: ModelSettings,
    model: IAiModel | null | undefined,
): ModelSettings {
    if (!model) return settings

    return {
        ...settings,
        durationSeconds: nearestNumberValue(
            durationValuesForModel(model),
            settings.durationSeconds,
            model.defaultDurationSeconds,
        ),
    }
}

function configuredActionCommand(
    model: IAiModel,
    settings: ModelSettings | undefined,
    hasImageInput = false,
): string {
    const parts = [`!${model.actionTagName}`]

    if (model.actionTagName === 'gif') {
        return parts.join(' ')
    }

    if (model.actionTagName === 'screenshot') {
        const timestamp = settings?.screenshotTimestamp?.trim()
        if (timestamp) parts.push(timestampForActionCommand(timestamp))
        return parts.join(' ')
    }

    if (model.actionTagName === 'extend') {
        if (settings?.durationSeconds) parts.push(`${settings.durationSeconds}s`)
        return parts.join(' ')
    }

    if (settings?.durationSeconds) {
        parts.push(`${settings.durationSeconds}s`)
    }
    const aspectRatio = sourceAwareVideoAspectRatio(
        model.modelType,
        hasImageInput,
        settings?.aspectRatio,
    )
    if (aspectRatio) {
        parts.push(aspectRatio)
    }
    return parts.join(' ')
}

function settingsFromActionRequestAttrs(
    model: IAiModel,
    attrs: ActionRequestFormState,
): ModelSettings {
    const defaults = defaultSettingsForModel(model)

    return normalizeModelSettingsForModel(model, {
        ...defaults,
        durationSeconds:
            attrs.durationSeconds ?? attrs.defaultDurationSeconds ?? defaults.durationSeconds,
        aspectRatio: attrs.aspectRatio ?? attrs.defaultAspectRatio ?? defaults.aspectRatio,
        generationCount: attrs.generationCount ?? defaults.generationCount,
        lyrics: attrs.lyrics ?? defaults.lyrics,
        gifFrameRate: attrs.gifFrameRate ?? defaults.gifFrameRate,
        gifMaxWidth: attrs.gifMaxWidth ?? defaults.gifMaxWidth,
        extendTimestamp: attrs.extendTimestamp ?? defaults.extendTimestamp,
        extendAiModelId: attrs.extendAiModelId ?? defaults.extendAiModelId,
        extendActionTag: attrs.extendActionTag ?? defaults.extendActionTag,
        smartStitch: attrs.smartStitch ?? defaults.smartStitch,
        screenshotTimestamp: attrs.screenshotTimestamp ?? defaults.screenshotTimestamp,
    })
}

type GifVideoMetadata = {
    durationSeconds: number | null
    width: number | null
    height: number | null
}

function estimateGifWork(metadata: GifVideoMetadata | null, frameRate: number, maxWidth: number) {
    const sourceWidth =
        metadata?.width && metadata.width > 0 ? metadata.width : DEFAULT_GIF_SOURCE_WIDTH
    const sourceHeight =
        metadata?.height && metadata.height > 0 ? metadata.height : DEFAULT_GIF_SOURCE_HEIGHT
    const durationSeconds =
        metadata?.durationSeconds && metadata.durationSeconds > 0
            ? metadata.durationSeconds
            : GIF_MAX_DURATION_SECONDS
    const scale = Math.min(1, maxWidth / sourceWidth)
    const outputWidth = Math.max(1, Math.round(sourceWidth * scale))
    const outputHeight = Math.max(1, Math.round(sourceHeight * scale))
    const frames = Math.ceil(durationSeconds * frameRate)
    const pixelsPerFrame = outputWidth * outputHeight
    const totalPixels = frames * pixelsPerFrame

    return {
        outputWidth,
        outputHeight,
        frames,
        pixelsPerFrame,
        totalPixels,
        exceedsBudget:
            pixelsPerFrame > GIF_MAX_PIXELS_PER_FRAME || totalPixels > GIF_MAX_TOTAL_PIXELS,
    }
}

function gifOptionAllowed(metadata: GifVideoMetadata | null, frameRate: number, maxWidth: number) {
    return !estimateGifWork(metadata, frameRate, maxWidth).exceedsBudget
}

function downgradedGifSettings(
    metadata: GifVideoMetadata | null,
    preferredFrameRate: number | null | undefined = DEFAULT_GIF_FRAME_RATE,
    preferredMaxWidth: number | null | undefined = DEFAULT_GIF_MAX_WIDTH,
) {
    const frameRateValues = GIF_FRAME_RATE_OPTIONS.map(option => option.value).sort((a, b) => b - a)
    const maxWidthValues = GIF_MAX_WIDTH_OPTIONS.map(option => option.value).sort((a, b) => b - a)
    const preferredFrameRateValue = nearestNumberValue(
        frameRateValues,
        preferredFrameRate,
        DEFAULT_GIF_FRAME_RATE,
    )
    const preferredMaxWidthValue = nearestNumberValue(
        maxWidthValues,
        preferredMaxWidth,
        DEFAULT_GIF_MAX_WIDTH,
    )
    const orderedCandidates = [
        ...frameRateValues
            .filter(value => value <= preferredFrameRateValue)
            .flatMap(frameRate =>
                maxWidthValues
                    .filter(value => value <= preferredMaxWidthValue)
                    .map(maxWidth => ({ frameRate, maxWidth })),
            ),
        ...frameRateValues.flatMap(frameRate =>
            maxWidthValues.map(maxWidth => ({ frameRate, maxWidth })),
        ),
    ]
    const seen = new Set<string>()
    const candidates = orderedCandidates.filter(candidate => {
        const key = `${candidate.frameRate}:${candidate.maxWidth}`
        if (seen.has(key)) return false
        seen.add(key)
        return true
    })
    return (
        candidates.find(candidate =>
            gifOptionAllowed(metadata, candidate.frameRate, candidate.maxWidth),
        ) ||
        candidates[candidates.length - 1] || {
            frameRate: preferredFrameRateValue,
            maxWidth: preferredMaxWidthValue,
        }
    )
}

type AiInputKind = 'text' | 'lyrics' | 'image' | 'video' | 'audio'
type AiReferenceInputKind = 'image' | 'video' | 'audio'
const REFERENCE_INPUT_KINDS: AiReferenceInputKind[] = ['image', 'video', 'audio']
const REFERENCE_INPUT_LIMIT_FIELDS: Record<AiReferenceInputKind, keyof IAiModel> = {
    image: 'referenceImageLimit',
    video: 'referenceVideoLimit',
    audio: 'referenceAudioLimit',
}
const NB_REFERENCE_INPUT_LIMITS: Record<AiReferenceInputKind, number> = {
    // Nano Banana accepts fourteen images total; the first image occupies the direct input.
    image: 13,
    video: 14,
    audio: 14,
}

function supportedInputKindsForModel(model: IAiModel): AiInputKind[] {
    if (model.actionTagName === 'stitch') return []
    if (isMusicActionTag(model.actionTagName)) return ['text', 'lyrics', 'audio']
    if (normalizeActionCommandTag(model.actionTagName) === 'nb') return ['text', 'image', 'audio']

    const configuredInputs = (model as Partial<ActionDefinition>).supportedInputs
    if (configuredInputs?.length) {
        return configuredInputs.filter((input): input is AiInputKind =>
            ['text', 'lyrics', 'image', 'video', 'audio'].includes(input),
        )
    }

    if (model.actionTagName === 'gif') return ['video']
    if (model.actionTagName === 'screenshot') return ['video']
    if (model.actionTagName === 'extend') return ['text', 'video']
    if (model.modelType === 'image' || model.modelType === 'video') return ['text', 'image']

    const inputKinds = new Set<AiInputKind>()

    if (model.canRemixVideo) inputKinds.add('video')
    if (model.canRemixImage) inputKinds.add('image')
    if (model.canRemixAudio) inputKinds.add('audio')

    if (!model.canRemixVideo && !model.canRemixAudio) {
        inputKinds.add('text')
    }

    return Array.from(inputKinds)
}

function referenceInputKindsForModel(model: IAiModel): AiReferenceInputKind[] {
    if (normalizeActionCommandTag(model.actionTagName) === 'nb') return REFERENCE_INPUT_KINDS
    if (model.modelType !== 'video') return []

    return REFERENCE_INPUT_KINDS.filter(kind => referenceInputLimitForModel(model, kind) > 0)
}

function supportsReferenceInputs(model: IAiModel): boolean {
    return referenceInputKindsForModel(model).length > 0
}

function referenceInputLimitForModel(
    model: IAiModel | null | undefined,
    kind: AiReferenceInputKind,
) {
    if (model && normalizeActionCommandTag(model.actionTagName) === 'nb') {
        return NB_REFERENCE_INPUT_LIMITS[kind]
    }
    if (!model || model.modelType !== 'video') return 0
    if (endFrameRequiresStartFrame(model.actionTagName) && kind === 'image') return 1

    const rawConfiguredLimit = model[REFERENCE_INPUT_LIMIT_FIELDS[kind]]
    const configuredLimit = Number(rawConfiguredLimit)
    if (
        rawConfiguredLimit !== null &&
        rawConfiguredLimit !== undefined &&
        Number.isFinite(configuredLimit)
    ) {
        return Math.max(0, Math.trunc(configuredLimit))
    }

    return 0
}

function referenceKindLabel(kind: AiReferenceInputKind, count?: number) {
    const plural = count === 1 ? kind : `${kind}s`
    return plural.toUpperCase()
}

function iconForModelType(modelType: IAiModel['modelType'] | null | undefined) {
    switch (modelType) {
        case 'image':
            return ImageIcon
        case 'video':
            return VideoIcon
        case 'audio':
            return MusicIcon
        case 'text':
            return Terminal
        default:
            return Wrench
    }
}

function labelForModelType(model: IAiModel) {
    switch (model.actionTagName) {
        case 'gif':
            return 'Make GIF'
        case 'screenshot':
            return 'Screenshot'
        case 'stitch':
            return 'Smart Stitch'
        case 'extend':
            return 'Extend'
        default:
            break
    }

    switch (model.modelType) {
        case 'image':
            return 'Gen Image'
        case 'video':
            return 'Gen Video'
        case 'audio':
            return 'Gen Audio'
        case 'text':
            return 'Gen Text'
        default:
            return 'AI Model'
    }
}

function labelForInputKind(inputKind: AiInputKind) {
    switch (inputKind) {
        case 'text':
            return 'Prompt'
        case 'lyrics':
            return 'Lyrics'
        case 'image':
            return 'Image'
        case 'video':
            return 'Video'
        case 'audio':
            return 'Audio'
        default:
            return 'Input'
    }
}

// The output pill closes the capability sentence: "Prompt, Image → Video".
const SONG_OUTPUT_COMMAND_TAGS = new Set(['minimaxmusic', 'suno', 'elevenmusic'])

function outputForModel(model: IAiModel): { label: string; icon: React.ReactNode } {
    const commandTag = canonicalActionTagFor(model.actionTagName)
    switch (commandTag) {
        case 'gif':
            return { label: 'GIF', icon: <Film /> }
        case 'screenshot':
            return { label: 'Frame', icon: <Camera /> }
        case 'stitch':
        case 'extend':
            return { label: 'Video', icon: <VideoIcon /> }
        case 'tts':
        case 'clone':
        case 'eleven_tts':
            return { label: 'Speech', icon: <AudioLines /> }
        case 'video_sfx':
            return { label: 'Sound FX', icon: <AudioLines /> }
        default:
            break
    }
    if (SONG_OUTPUT_COMMAND_TAGS.has(commandTag)) return { label: 'Song', icon: <MusicIcon /> }

    switch (model.modelType) {
        case 'image':
            return { label: 'Image', icon: <ImageIcon /> }
        case 'video':
            return { label: 'Video', icon: <VideoIcon /> }
        case 'audio':
            return { label: 'Audio', icon: <AudioLines /> }
        default:
            return { label: 'Text', icon: <Type /> }
    }
}

interface ModelSettingSummary {
    key: string
    label: string
    values: ModelSettingTileValue[]
}

// "5–15s" for a run of numeric options, "16:9 · 9:16 · 1:1" otherwise.
function settingFactText(setting: ModelSettingSummary): string {
    const parts = setting.values.map(item =>
        typeof item === 'string'
            ? { value: item, unit: '' }
            : { value: item.value, unit: item.unit || '' },
    )
    const numbers = parts.map(part => Number(part.value))
    if (parts.length >= 3 && numbers.every(number => Number.isFinite(number))) {
        return `${Math.min(...numbers)}–${Math.max(...numbers)}${parts[0].unit}`
    }
    return parts.map(part => `${part.value}${part.unit}`).join(' · ')
}

function settingFact(setting: ModelSettingSummary): { icon: React.ReactNode; text: string } {
    switch (setting.key) {
        case 'duration':
            return { icon: <Clock />, text: settingFactText(setting) }
        case 'aspect-ratio':
            return { icon: <Ratio />, text: settingFactText(setting) }
        case 'frame-rate':
            return { icon: <Gauge />, text: `${settingFactText(setting)} fps` }
        case 'max-width':
            return { icon: <MoveHorizontal />, text: settingFactText(setting) }
        case 'extend-model':
            return { icon: <Sparkles />, text: `via ${settingFactText(setting)}` }
        case 'extend-output':
        case 'output':
            return { icon: <Scissors />, text: 'Smart stitch' }
        case 'time':
            return { icon: <Clock />, text: 'Pick a frame' }
        default:
            return { icon: null, text: `${setting.label} ${settingFactText(setting)}` }
    }
}

function modelInputDialogTitle(inputKind: AiInputKind) {
    return `Choose ${labelForInputKind(inputKind)} Input`
}

function referenceInputDialogTitle(inputKind: AiReferenceInputKind | null) {
    if (!inputKind) return 'Choose References'
    return `Choose ${labelForInputKind(inputKind)} Reference`
}

function compactAiActionObject<T extends Record<string, any>>(object: T): T {
    return Object.fromEntries(
        Object.entries(object).filter(([, value]) => value !== null && value !== undefined),
    ) as T
}

function createAiActionClientId() {
    const randomId =
        typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    return `ai-action-${randomId}`
}

function createAiRunId() {
    return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : uuid()
}

function aiModelIdForActionDefinition(model: ActionDefinition) {
    if ((model.kind || 'model') !== 'model' || model.provider === 'fallback') return null
    return model.aiModel?.id || model.defaultAiModelId || model.id || null
}

function aiActionGenerationCount(action: Partial<ActionRequestFormState> | AiActionPayload) {
    const actionPayload = action as any
    const rawCount =
        actionPayload?.generationCount ??
        actionPayload?.generation_count ??
        actionPayload?.settings?.generation_count
    const count = Number(rawCount || 1)
    if (!Number.isFinite(count)) return 1
    return Math.min(Math.max(Math.trunc(count), 1), 4)
}

function aiActionRunIds(action: Partial<ActionRequestFormState> | AiActionPayload) {
    const actionPayload = action as any
    const generationCount = aiActionGenerationCount(action)
    const existingIds = [
        ...(Array.isArray(actionPayload?.ai_run_ids) ? actionPayload.ai_run_ids : []),
        ...(Array.isArray(actionPayload?.aiRunIds) ? actionPayload.aiRunIds : []),
    ].filter(Boolean)
    const firstRunId = actionPayload?.ai_run_id || actionPayload?.aiRunId
    if (firstRunId && !existingIds.includes(firstRunId)) existingIds.unshift(firstRunId)

    return Array.from(
        { length: generationCount },
        (_, index) => existingIds[index] || createAiRunId(),
    )
}

function defaultPromptForActionCommand(commandTag: string | null | undefined) {
    const normalizedCommandTag = normalizeActionCommandTag(commandTag)
    if (/^animate(?:_|$)/.test(normalizedCommandTag)) return 'animate'
    if (/^edit(?:_|$)/.test(normalizedCommandTag)) return 'edit'
    return ''
}

function activeStorageBlobPreviewUrl(signedId?: string | null, filename?: string | null) {
    if (!signedId) return null
    const baseUrl = typeof window !== 'undefined' && window.knovApiUrl ? window.knovApiUrl : ''
    const safeFilename = encodeURIComponent((filename || 'upload').split('/').pop() || 'upload')
    return `${baseUrl}/rails/active_storage/blobs/redirect/${encodeURIComponent(
        signedId,
    )}/${safeFilename}`
}

function previewableAiMediaUrl(media?: any | null) {
    if (!media || typeof media !== 'object') return ''
    const url = media.url || ''
    if (url) return url
    return (
        activeStorageBlobPreviewUrl(
            media.signed_id || media.signedId || null,
            media.label || media.source_label || media.sourceLabel || null,
        ) || ''
    )
}

function aiMediaInputPayload(media?: any | null) {
    if (!media || typeof media !== 'object') return null

    return compactAiActionObject({
        id: media.id || null,
        url: previewableAiMediaUrl(media) || null,
        preview_image_url: media.preview_image_url || media.previewImageUrl || null,
        signed_id: media.signed_id || media.signedId || null,
        upload_id: media.upload_id || media.uploadId || null,
        label: media.label || null,
        source_label: media.source_label || media.sourceLabel || null,
        source: media.source || null,
        content_type: media.content_type || media.contentType || media.mime || null,
        duration_seconds: media.duration_seconds ?? media.durationSeconds ?? media.duration ?? null,
        action_tag: media.action_tag || media.actionTag || null,
        answer_id: media.answer_id || media.answerId || media.source_answer_id || null,
        attachment_id: media.attachment_id || media.attachmentId || null,
        blob_id: media.blob_id || media.blobId || null,
        run_id: media.run_id || media.runId || media.source_run_id || null,
        root_remix_ai_run_id: media.root_remix_ai_run_id || media.rootRemixAiRunId || null,
        action_id: media.action_id || media.actionId || media.source_action_id || null,
        index: media.index ?? null,
    })
}

function aiMediaInputFromPayload(media: any, fallbackLabel: string) {
    if (!media || typeof media !== 'object') return null

    return {
        id:
            media.id ||
            media.attachment_id ||
            media.attachmentId ||
            media.blob_id ||
            media.blobId ||
            media.signed_id ||
            media.signedId ||
            media.url ||
            fallbackLabel,
        url: previewableAiMediaUrl(media),
        label: media.label || media.source_label || media.sourceLabel || fallbackLabel,
        previewImageUrl: media.preview_image_url || media.previewImageUrl || null,
        actionTag: media.action_tag || media.actionTag || null,
        source: media.source || null,
        uploadId: media.upload_id || media.uploadId || null,
        signedId: media.signed_id || media.signedId || null,
        contentType: media.content_type || media.contentType || null,
        durationSeconds: media.duration_seconds ?? media.durationSeconds ?? media.duration ?? null,
        attachmentId: media.attachment_id || media.attachmentId || null,
        blobId: media.blob_id || media.blobId || null,
        answerId: media.answer_id || media.answerId || null,
        runId: media.run_id || media.runId || null,
        rootRemixAiRunId: media.root_remix_ai_run_id || media.rootRemixAiRunId || null,
        actionId: media.action_id || media.actionId || null,
        index: media.index ?? null,
    }
}

function aiActionFormStateFromAiAction(action: AiActionPayload | null | undefined) {
    if (!action || typeof action !== 'object') return null

    const tag = normalizeActionCommandTag(action.tag)
    if (!tag) return null

    const settings = action.settings || {}
    const inputs = action.inputs || {}
    const references = inputs.references || {}
    const keyframes = videoKeyframeInputs(tag, inputs)
    const displayImageInput = keyframes.startImage || inputs.image
    const displayReferenceImages = keyframes.frameMode
        ? keyframes.endImage
            ? [keyframes.endImage]
            : []
        : references.images || []
    const serializedTextValue = inputs.text?.value ?? action.prompt ?? ''
    const textValue = deserializePromptReferenceMentions(serializedTextValue, {
        tag,
        settings,
        inputs,
    })
    const imageInput = aiMediaInputFromPayload(
        displayImageInput,
        keyframes.frameMode ? 'Start frame' : 'Image input',
    )
    const videoInput = aiMediaInputFromPayload(inputs.video, 'Video input')
    const audioInput = aiMediaInputFromPayload(inputs.audio, 'Audio input')

    return {
        tag,
        ai_action_id: action.id || action.client_id || action.ai_action_id || null,
        actionId: action.id || action.client_id || action.actionId || null,
        promptContent: textValue || null,
        promptPlacement: action.prompt_placement || action.promptPlacement || null,
        actionDefinitionId: action.action_definition_id || action.actionDefinitionId || null,
        kind: action.kind || action.type || undefined,
        aiModelId: action.ai_model_id || action.aiModelId || null,
        toolKey: action.tool_key || action.toolKey || null,
        aiRunId: action.ai_run_id || action.aiRunId || null,
        aiRunIds: action.ai_run_ids || action.aiRunIds || null,
        remixAiRunId: action.remix_ai_run_id || action.remixAiRunId || null,
        rootRemixAiRunId: action.root_remix_ai_run_id || action.rootRemixAiRunId || null,
        remixKind: action.remix_kind || action.remixKind || null,
        type: action.type || 'model',
        modelType: action.model_type || action.modelType || null,
        defaultDurationSeconds: action.default_duration_seconds || null,
        defaultAspectRatio: action.default_aspect_ratio || null,
        durationSeconds: settings.duration_seconds ?? null,
        aspectRatio: settings.aspect_ratio ?? null,
        generationCount: settings.generation_count ?? 1,
        gifFrameRate: settings.gif_frame_rate ?? null,
        gifMaxWidth: settings.gif_max_width ?? null,
        screenshotTimestamp: settings.screenshot_timestamp ?? null,
        extendAiModelId: settings.extend_ai_model_id ?? settings.extendAiModelId ?? null,
        extendActionTag: settings.extend_action_tag ?? null,
        extendTimestamp: settings.extend_timestamp ?? null,
        smartStitch: settings.smart_stitch === true,
        hasTextInput: !!textValue,
        textInputValue: textValue || null,
        hasImageInput: !!(
            displayImageInput?.url ||
            displayImageInput?.id ||
            displayImageInput?.attachment_id ||
            displayImageInput?.blob_id ||
            displayImageInput?.signed_id
        ),
        imageInputUrl: displayImageInput?.url || null,
        imageInput,
        referenceImageInputs: displayReferenceImages
            .map((image: any, index: number) =>
                aiMediaInputFromPayload(
                    image,
                    keyframes.frameMode ? 'End frame' : `Reference image ${index + 1}`,
                ),
            )
            .filter(input => !!input?.url),
        videoKeyframeMode: keyframes.frameMode,
        hasVideoInput: !!inputs.video,
        videoInputUrl: inputs.video?.url || null,
        videoPreviewImageUrl: inputs.video?.preview_image_url || null,
        videoInputActionTag: inputs.video?.action_tag || null,
        videoInput,
        videoInputs: (inputs.videos || [])
            .map((video: any, index: number) =>
                aiMediaInputFromPayload(video, `Video ${index + 1}`),
            )
            .filter(Boolean),
        referenceVideoInputs: (keyframes.frameMode ? [] : references.videos || [])
            .map((video: any, index: number) =>
                aiMediaInputFromPayload(video, `Reference video ${index + 1}`),
            )
            .filter(input => !!input?.url),
        hasAudioInput: !!inputs.audio,
        audioInputUrl: inputs.audio?.url || null,
        audioInputSource: inputs.audio?.source || null,
        audioInputUploadId: inputs.audio?.upload_id || null,
        audioInputSignedId: inputs.audio?.signed_id || null,
        audioInputLabel: inputs.audio?.label || null,
        audioInputContentType: inputs.audio?.content_type || null,
        audioInput,
        referenceAudioInputs: (keyframes.frameMode ? [] : references.audios || [])
            .map((audio: any, index: number) =>
                aiMediaInputFromPayload(audio, `Reference audio ${index + 1}`),
            )
            .filter(input => !!input?.url),
        hasLyrics: !!settings.lyrics,
        lyrics: settings.lyrics || null,
        inputOrder: action.input_order || [],
        syncWithDraft: action.sync_with_draft === true,
        commandText: action.command_text || `!${tag}`,
        displayName: action.display_name || action.displayName || null,
    } as ActionRequestFormState
}

function aiActionFromFormState(
    attrs: Partial<ActionRequestFormState>,
    existingAction: AiActionPayload | null = null,
) {
    const tag = normalizeActionCommandTag(attrs.tag)
    if (!tag) return null

    const generationCount = aiActionGenerationCount(attrs)
    const aiRunIds = aiActionRunIds({
        ...existingAction,
        ...attrs,
        generation_count: generationCount,
    })
    const hasExplicitTextInputValue = Object.prototype.hasOwnProperty.call(attrs, 'textInputValue')
    const hasExplicitPromptContent = Object.prototype.hasOwnProperty.call(attrs, 'promptContent')
    const rawTextInputValue =
        typeof attrs.textInputValue === 'string'
            ? attrs.textInputValue
            : typeof attrs.promptContent === 'string'
            ? attrs.promptContent
            : hasExplicitTextInputValue || hasExplicitPromptContent
            ? ''
            : null
    const textInputValue = rawTextInputValue?.trim() || ''
    const existingPrompt =
        rawTextInputValue === null
            ? existingAction?.inputs?.text?.value || existingAction?.prompt || ''
            : ''
    const defaultPrompt = defaultPromptForActionCommand(tag)
    const displayActionPrompt = textInputValue || existingPrompt || defaultPrompt || ''
    const actionPrompt = serializePromptReferenceMentions(
        displayActionPrompt,
        attrs,
        promptReferenceLabelsForAttrs(attrs),
    )
    const usesDefaultPrompt = !textInputValue && !existingPrompt && !!defaultPrompt
    const imageInput = attrs.imageInput || { url: attrs.imageInputUrl || null }
    const referenceImagePayloads = (attrs.referenceImageInputs || []).map((image, index) => ({
        ...aiMediaInputPayload(image),
        label: `[Image${index + 1}]`,
        source_label: image.label || null,
    }))
    const genericImagePayload = attrs.hasImageInput ? aiMediaInputPayload(imageInput) : null
    const keyframes = videoKeyframeInputsFromFormState(
        tag,
        genericImagePayload,
        referenceImagePayloads,
        attrs.videoKeyframeMode === true,
    )
    const keyframePayload = keyframes.frameMode
        ? explicitVideoKeyframePayload(tag, keyframes.startImage, keyframes.endImage)
        : null
    const videoInput = attrs.videoInput || {
        url: attrs.videoInputUrl || null,
        preview_image_url: attrs.videoPreviewImageUrl || null,
        action_tag: attrs.videoInputActionTag || null,
    }
    const audioInput = attrs.audioInput || {
        url: attrs.audioInputUrl || null,
        source: attrs.audioInputSource || null,
        upload_id: attrs.audioInputUploadId || null,
        signed_id: attrs.audioInputSignedId || null,
        label: attrs.audioInputLabel || null,
        content_type: attrs.audioInputContentType || null,
        file: attrs.audioInputFile || null,
    }
    const actionId =
        attrs.ai_action_id ||
        attrs.actionId ||
        existingAction?.id ||
        existingAction?.client_id ||
        createAiActionClientId()
    const kind = attrs.kind || attrs.type || existingAction?.kind || existingAction?.type || 'model'

    return compactAiActionObject({
        ...(existingAction || {}),
        id: actionId,
        client_id: actionId,
        action_definition_id:
            attrs.actionDefinitionId ||
            existingAction?.action_definition_id ||
            existingAction?.actionDefinitionId ||
            null,
        ai_run_id: aiRunIds[0],
        ai_run_ids: aiRunIds,
        remix_ai_run_id: attrs.remixAiRunId || existingAction?.remix_ai_run_id || null,
        root_remix_ai_run_id:
            attrs.rootRemixAiRunId || existingAction?.root_remix_ai_run_id || null,
        remix_kind: attrs.remixKind || existingAction?.remix_kind || null,
        kind,
        tag,
        action_tag: tag,
        actionTag: tag,
        type: kind,
        ai_model_id:
            attrs.aiModelId || existingAction?.ai_model_id || existingAction?.aiModelId || null,
        tool_key: attrs.toolKey || existingAction?.tool_key || existingAction?.toolKey || null,
        model_type: attrs.modelType || existingAction?.model_type || null,
        command_text: attrs.commandText || existingAction?.command_text || `!${tag}`,
        prompt: actionPrompt,
        prompt_placement:
            attrs.promptPlacement ||
            attrs.prompt_placement ||
            existingAction?.prompt_placement ||
            existingAction?.promptPlacement ||
            null,
        prompt_is_default: usesDefaultPrompt,
        sync_with_draft: false,
        analytics_source: attrs.analyticsSource || existingAction?.analytics_source || null,
        settings: compactAiActionObject({
            duration_seconds: attrs.durationSeconds ?? null,
            aspect_ratio: sourceAwareVideoAspectRatio(
                attrs.modelType,
                attrs.hasImageInput === true,
                attrs.aspectRatio,
            ),
            generation_count: generationCount,
            gif_frame_rate: attrs.gifFrameRate ?? null,
            gif_max_width: attrs.gifMaxWidth ?? null,
            screenshot_timestamp: attrs.screenshotTimestamp ?? null,
            extend_ai_model_id: attrs.extendAiModelId ?? null,
            extend_action_tag: attrs.extendAiModelId ? null : attrs.extendActionTag ?? null,
            extend_timestamp: attrs.extendTimestamp ?? null,
            smart_stitch: attrs.smartStitch === true,
            lyrics: attrs.lyrics ?? null,
        }),
        inputs: compactAiActionObject({
            ...(attrs.hasTextInput || textInputValue ? { text: { value: actionPrompt } } : {}),
            ...(keyframePayload || {}),
            ...(!keyframePayload && attrs.hasImageInput ? { image: genericImagePayload } : {}),
            ...(attrs.hasVideoInput ? { video: aiMediaInputPayload(videoInput) } : {}),
            ...(attrs.videoInputs?.length
                ? {
                      videos: attrs.videoInputs.map((video, index) => ({
                          ...aiMediaInputPayload(video),
                          label: video.label || `[Video${index + 1}]`,
                          source_label: video.label || null,
                      })),
                  }
                : {}),
            ...(attrs.hasAudioInput ? { audio: aiMediaInputPayload(audioInput) } : {}),
            ...((!keyframePayload && attrs.referenceImageInputs?.length) ||
            (!keyframePayload && attrs.referenceVideoInputs?.length) ||
            (!keyframePayload && attrs.referenceAudioInputs?.length)
                ? {
                      references: compactAiActionObject({
                          ...(!keyframePayload && referenceImagePayloads.length
                              ? {
                                    images: referenceImagePayloads,
                                }
                              : {}),
                          ...(!keyframePayload && attrs.referenceVideoInputs?.length
                              ? {
                                    videos: attrs.referenceVideoInputs.map((video, index) => ({
                                        ...aiMediaInputPayload(video),
                                        label: `[Video${index + 1}]`,
                                        source_label: video.label || null,
                                    })),
                                }
                              : {}),
                          ...(!keyframePayload && attrs.referenceAudioInputs?.length
                              ? {
                                    audios: attrs.referenceAudioInputs.map((audio, index) => ({
                                        ...aiMediaInputPayload(audio),
                                        label: `[Audio${index + 1}]`,
                                        source_label: audio.label || null,
                                    })),
                                }
                              : {}),
                      }),
                  }
                : {}),
        }),
        input_order: attrs.inputOrder || [],
    })
}

function insertActionRequestIntoEditor(
    getEditor: () => any,
    actionRequest: string | ActionRequestSelection | Array<string | ActionRequestSelection>,
) {
    const editor = getEditor()
    if (!editor) return
    const actionRequests = Array.isArray(actionRequest) ? actionRequest : [actionRequest]
    const insertionItems = actionRequests.filter(Boolean).map(item => ({
        commandText: typeof item === 'string' ? item : item.commandText,
        actionTokenAttrs: typeof item === 'string' ? null : item.attrs,
    }))
    if (insertionItems.length === 0) return

    const tokenActionId = (attrs: Record<string, any> | null | undefined) =>
        typeof attrs?.ai_action_id === 'string'
            ? attrs.ai_action_id
            : typeof attrs?.actionId === 'string'
            ? attrs.actionId
            : null

    const actionTokenPromptContent = (attrs: Record<string, any> | null | undefined) =>
        typeof attrs?.promptContent === 'string'
            ? attrs.promptContent
                  .replace(new RegExp(ACTION_PROMPT_SENTINEL, 'g'), '')
                  .replace(/\u200b/g, '')
                  .replace(/\u00a0/g, ' ')
                  .replace(/[\r\n]+/g, ' ')
                  .trim()
            : ''

    const actionTokenEditorPromptText = (attrs: Record<string, any> | null | undefined) => {
        const promptContent = actionTokenPromptContent(attrs)
        if (promptContent) return `${promptContent}${ACTION_PROMPT_SENTINEL}`
        return attrs?.supportsPromptInput === false ? '' : ACTION_PROMPT_SENTINEL
    }

    const focusInsertedActionTokenPrompt = (editorInstance: any, attrs: Record<string, any>) => {
        const focusPrompt = () => {
            const actionId = tokenActionId(attrs)
            let match: { pos: number; node: any } | null = null

            editorInstance?.state?.doc?.descendants?.((node, pos) => {
                if (node.type?.name !== 'actionTag') return true
                const nodeActionId = tokenActionId(node.attrs)
                if (!actionId || nodeActionId === actionId) {
                    match = { pos, node }
                }
                return false
            })

            if (!match) return false

            const promptContent = actionTokenPromptContent({
                promptContent: match.node.textContent || match.node.attrs?.promptContent,
            })
            const targetPos = Math.min(
                Math.max(match.pos + 1 + promptContent.length, 0),
                editorInstance.state.doc.content.size,
            )
            if (typeof editorInstance.chain === 'function') {
                editorInstance.chain().focus().setTextSelection(targetPos).run()
                return true
            }

            if (editorInstance.view?.dom) {
                try {
                    editorInstance.view.dom.focus({ preventScroll: true })
                } catch (error) {
                    editorInstance.view.dom.focus()
                }
                const { selection } = editorInstance.state
                if (!selection?.empty || selection?.from !== targetPos) {
                    editorInstance.view.dispatch(
                        editorInstance.state.tr.setSelection(
                            TextSelection.create(editorInstance.state.doc, targetPos),
                        ),
                    )
                }
                return true
            }

            if (editorInstance.view && editorInstance.state) {
                editorInstance.view.dispatch(
                    editorInstance.state.tr.setSelection(
                        TextSelection.create(editorInstance.state.doc, targetPos),
                    ),
                )
                editorInstance.view.focus()
                return true
            }

            return false
        }

        focusPrompt()
        window.requestAnimationFrame(focusPrompt)
    }

    if (editor.commands) {
        try {
            const actualEditor = editor.getEditor ? editor.getEditor() : editor
            actualEditor?.commands?.focus?.('end')

            setTimeout(() => {
                const editorInstance = editor.getEditor ? editor.getEditor() : editor
                if (!editorInstance) return

                if (!editorInstance.isFocused || !editorInstance.state?.selection) {
                    editorInstance.commands.focus('end')
                }

                const { $from } = editorInstance.state.selection
                const pos = $from.pos
                let addSpaceBefore = false

                if (pos > 0) {
                    const textBefore = editorInstance.state.doc.textBetween(
                        Math.max(0, pos - 1),
                        pos,
                    )
                    addSpaceBefore = !!textBefore?.trim()
                }

                const contentToInsert = []
                if (addSpaceBefore) contentToInsert.push({ type: 'text', text: ' ' })
                let promptFocusAttrs: Record<string, any> | null = null
                insertionItems.forEach(({ commandText, actionTokenAttrs }) => {
                    if (actionTokenAttrs) {
                        const promptContent = actionTokenPromptContent(actionTokenAttrs)
                        const editorPromptText = actionTokenEditorPromptText(actionTokenAttrs)
                        const nodeAttrs = {
                            ...actionTokenAttrs,
                            ...(promptContent ? { promptContent } : {}),
                        }
                        if (!promptContent) {
                            delete nodeAttrs.promptContent
                            delete (nodeAttrs as Record<string, any>).prompt_content
                        }
                        contentToInsert.push({
                            type: 'actionTag',
                            attrs: nodeAttrs,
                            ...(editorPromptText
                                ? { content: [{ type: 'text', text: editorPromptText }] }
                                : {}),
                        })
                        if (
                            !promptFocusAttrs &&
                            (actionTokenAttrs as { supportsPromptInput?: boolean })
                                .supportsPromptInput !== false
                        ) {
                            promptFocusAttrs = actionTokenAttrs
                        }
                    } else {
                        contentToInsert.push({
                            type: 'text',
                            text: commandText,
                            marks: [{ type: 'bold' }],
                        })
                    }
                    contentToInsert.push({ type: 'text', text: ' ' })
                })

                editorInstance.commands.insertContent(contentToInsert)
                if (promptFocusAttrs) {
                    focusInsertedActionTokenPrompt(editorInstance, promptFocusAttrs)
                }
            }, 100)
        } catch (error) {
            console.error('Failed to insert action token in Tiptap:', error)
        }
        return
    }

    const cursorPosition = editor?.selection?.savedRange?.index ?? 0
    try {
        const textBefore =
            cursorPosition > 0 && typeof editor.getText === 'function'
                ? editor.getText(cursorPosition - 1, 1)
                : ''
        const prefix = textBefore?.trim() ? ' ' : ''
        const commandText = insertionItems.map(item => item.commandText).join(' ')
        editor.insertText(cursorPosition, `${prefix}${commandText} `, 'bold', true, 'user')
        editor.setSelection(cursorPosition + prefix.length + commandText.length + 1)
    } catch (error) {
        console.error('Failed to insert action token in Quill:', error)
        const currentLength = editor.getLength()
        const textBefore =
            currentLength > 0 && typeof editor.getText === 'function'
                ? editor.getText(currentLength - 1, 1)
                : ''
        const prefix = textBefore?.trim() ? ' ' : ''
        const commandText = insertionItems.map(item => item.commandText).join(' ')
        editor.insertText(currentLength, `${prefix}${commandText} `, 'bold', true, 'user')
        editor.setSelection(currentLength + prefix.length + commandText.length + 1)
    }
}

function VideoFramePreview({
    video,
    timestamp,
    fallbackTimestampSeconds = 0,
    emptyText = 'Choose a video to preview a frame',
    emptyActionLabel,
    onEmptyAction,
    onDurationChange,
}: {
    video: AiInputVideo | null
    timestamp?: string
    fallbackTimestampSeconds?: number
    emptyText?: string
    emptyActionLabel?: string
    onEmptyAction?: () => void
    onDurationChange?: (durationSeconds: number | null) => void
}) {
    const videoRef = useRef<HTMLVideoElement | null>(null)
    const [previewStatus, setPreviewStatus] = useState<'empty' | 'loading' | 'ready' | 'error'>(
        video ? 'loading' : 'empty',
    )
    const timestampSeconds = useMemo(() => {
        const trimmedTimestamp = timestamp?.trim()
        return trimmedTimestamp ? timestampToSeconds(trimmedTimestamp) : fallbackTimestampSeconds
    }, [fallbackTimestampSeconds, timestamp])

    useEffect(() => {
        const videoElement = videoRef.current
        if (!video || !videoElement) {
            setPreviewStatus('empty')
            onDurationChange?.(null)
            return
        }

        let isCancelled = false
        setPreviewStatus('loading')

        const seekToFrame = () => {
            if (isCancelled) return
            const duration = Number.isFinite(videoElement.duration) ? videoElement.duration : null
            onDurationChange?.(duration && duration > 0 ? duration : null)
            const targetSeconds = clampVideoFrameTimestamp(timestampSeconds, duration)

            try {
                videoElement.pause()
                videoElement.currentTime = targetSeconds
            } catch {
                setPreviewStatus('error')
            }
        }

        const handleSeeked = () => {
            if (!isCancelled) {
                videoElement.pause()
                setPreviewStatus('ready')
            }
        }
        const handleError = () => {
            if (!isCancelled) setPreviewStatus('error')
        }

        videoElement.addEventListener('loadedmetadata', seekToFrame)
        videoElement.addEventListener('seeked', handleSeeked)
        videoElement.addEventListener('error', handleError)

        if (videoElement.readyState >= 1) {
            seekToFrame()
        }

        return () => {
            isCancelled = true
            videoElement.removeEventListener('loadedmetadata', seekToFrame)
            videoElement.removeEventListener('seeked', handleSeeked)
            videoElement.removeEventListener('error', handleError)
        }
    }, [onDurationChange, timestampSeconds, video])

    return (
        <div className={styles.aiModelScreenshotPreview}>
            {video ? (
                <video
                    ref={videoRef}
                    className={styles.aiModelScreenshotPreviewVideo}
                    src={video.url}
                    muted
                    playsInline
                    preload="metadata"
                />
            ) : (
                <div className={styles.aiModelScreenshotPreviewEmpty}>
                    {onEmptyAction ? (
                        <button
                            className={styles.aiModelScreenshotPreviewEmptyButton}
                            type="button"
                            aria-label={emptyActionLabel || emptyText}
                            onClick={onEmptyAction}
                        >
                            <Plus size={22} />
                        </button>
                    ) : (
                        <Camera size={20} />
                    )}
                    <span>{emptyActionLabel || emptyText}</span>
                </div>
            )}
            {video && previewStatus !== 'ready' && (
                <div className={styles.aiModelScreenshotPreviewOverlay}>
                    {previewStatus === 'error' ? 'Preview unavailable' : 'Loading frame'}
                </div>
            )}
            <div className={styles.aiModelScreenshotPreviewCaption}>
                Frame at {secondsToTimestamp(timestampSeconds, timestampSeconds % 1 ? 1 : 0)}
            </div>
        </div>
    )
}

function VideoFramePicker({
    video,
    timestamp,
    onTimestampChange,
    placeholder,
    emptyText,
    emptyActionLabel,
    onEmptyAction,
    defaultFrameRatio = 0,
}: {
    video: AiInputVideo | null
    timestamp?: string
    onTimestampChange: (timestamp: string) => void
    placeholder: string
    emptyText: string
    emptyActionLabel?: string
    onEmptyAction?: () => void
    defaultFrameRatio?: number
}) {
    const [durationSeconds, setDurationSeconds] = useState<number | null>(null)
    const hasExplicitTimestamp = !!timestamp?.trim()
    const fallbackTimestampSeconds =
        !hasExplicitTimestamp && durationSeconds
            ? Math.max(durationSeconds * defaultFrameRatio, 0)
            : 0
    const timestampSeconds = hasExplicitTimestamp
        ? timestampToSeconds(timestamp)
        : fallbackTimestampSeconds
    const safeTimestampSeconds = clampVideoFrameTimestamp(timestampSeconds, durationSeconds)
    const sliderMax = Math.max(
        durationSeconds ? Math.max(durationSeconds - VIDEO_FRAME_END_GUARD_SECONDS, 0) : 0,
        safeTimestampSeconds,
        SCREENSHOT_SLIDER_STEP_SECONDS,
    )
    const sliderValue = Math.min(safeTimestampSeconds, sliderMax)

    return (
        <>
            <input
                className={styles.aiModelSettingsInput}
                value={timestamp || ''}
                placeholder={placeholder}
                onChange={event => onTimestampChange(event.target.value)}
            />
            <div className={styles.aiModelScreenshotTimeSliderRow}>
                <input
                    className={styles.aiModelScreenshotTimeSlider}
                    type="range"
                    min="0"
                    max={sliderMax}
                    step={SCREENSHOT_SLIDER_STEP_SECONDS}
                    value={sliderValue}
                    disabled={!durationSeconds}
                    aria-label="Source frame time"
                    onChange={event =>
                        onTimestampChange(
                            secondsToTimestamp(
                                clampVideoFrameTimestamp(
                                    Number(event.target.value),
                                    durationSeconds,
                                ),
                                1,
                            ),
                        )
                    }
                />
                <span>{durationSeconds ? secondsToTimestamp(durationSeconds, 0) : '--:--'}</span>
            </div>
            <VideoFramePreview
                video={video}
                timestamp={secondsToTimestamp(
                    safeTimestampSeconds,
                    safeTimestampSeconds % 1 ? 1 : 0,
                )}
                fallbackTimestampSeconds={fallbackTimestampSeconds}
                emptyText={emptyText}
                emptyActionLabel={emptyActionLabel}
                onEmptyAction={onEmptyAction}
                onDurationChange={setDurationSeconds}
            />
        </>
    )
}

function VideoInputPreview({ video, className }: { video: AiInputVideo; className?: string }) {
    return (
        <span className={cn(styles.aiInputVideoPreviewFrame, className)}>
            {video.previewImageUrl ? (
                <img src={video.previewImageUrl} alt="" loading="lazy" />
            ) : (
                <video src={video.url} muted playsInline preload="metadata" aria-hidden="true" />
            )}
        </span>
    )
}

function AiMagicWandMenuButtonWithRef(
    {
        getEditor,
        getCurrentTextInput,
        getCurrentImageInput,
        getCurrentVideoInput,
        getCurrentAudioInput,
        getThreadTextInputs,
        getCraftedPromptInputs,
        getThreadImageInputs,
        getThreadVideoInputs,
        getThreadAudioInputs,
        getThreadTreeMediaInputs,
        onImageInputAttached,
        onVideoInputAttached,
        onAudioInputAttached,
        questIsPublic,
        contextStyles,
        imageStyles,
        triggerTestId,
        menuTestId,
        onMenuStateChange,
        onActionRequestSelected,
        hideTrigger = false,
    }: AiMagicWandMenuButtonProps,
    ref: React.ForwardedRef<AiMagicWandMenuHandle>,
) {
    const aiModels = useStore(state => state.aiModels || [])
    const storeActionDefinitions = useStore(state => state.actionDefinitions || [])
    const editHostOrderRef = useRef(0)
    if (editHostOrderRef.current === 0) {
        editHostOrderRef.current = ++aiActionEditHostSequence
    }
    const [showMenu, setShowMenu] = useState(false)
    const [selectedActionDefinitionId, setSelectedActionDefinitionId] = useState<string | null>(
        null,
    )
    const [selectedActionDefinitionIds, setSelectedActionDefinitionIds] = useState<string[]>([])
    const [expandedMoreSections, setExpandedMoreSections] = useState<Record<string, boolean>>({})
    const [activeModelFilterTab, setActiveModelFilterTab] = useState<ModelFilterTab | null>(null)
    const [isModelSearchActive, setIsModelSearchActive] = useState(false)
    const [modelSearchQuery, setModelSearchQuery] = useState('')
    const [draftTextInput, setDraftTextInput] = useState('')
    const [promptEditorDefaultText, setPromptEditorDefaultText] = useState('')
    const [promptEditorHasText, setPromptEditorHasText] = useState(false)
    const [textInputEditorRevision, setTextInputEditorRevision] = useState(0)
    const [pendingTextInput, setPendingTextInput] = useState<AiInputText | null>(null)
    const [pendingImageInput, setPendingImageInput] = useState<AiInputImage | null>(null)
    const [pendingVideoInput, setPendingVideoInput] = useState<AiInputVideo | null>(null)
    const [pendingAudioInput, setPendingAudioInput] = useState<AiInputAudio | null>(null)
    const [isEditingTextInput, setIsEditingTextInput] = useState(false)
    const [isSelectingTextInput, setIsSelectingTextInput] = useState(false)
    const [isBrowsingTextPrompts, setIsBrowsingTextPrompts] = useState(false)
    const [isSelectingImageInput, setIsSelectingImageInput] = useState(false)
    const [isSelectingVideoInput, setIsSelectingVideoInput] = useState(false)
    const [isSelectingAudioInput, setIsSelectingAudioInput] = useState(false)
    const [mediaPreviewTarget, setMediaPreviewTarget] = useState<AiMediaPreviewTarget | null>(null)
    const [mediaInputLayouts, setMediaInputLayouts] = useState<
        Record<AiReferenceInputKind, AiMediaInputLayout>
    >({
        image: 'grid',
        video: 'grid',
        audio: 'list',
    })
    const [threadTreeMediaInputs, setThreadTreeMediaInputs] = useState<{
        texts: AiInputText[]
        images: AiInputImage[]
        videos: AiInputVideo[]
        audios: AiInputAudio[]
    }>({ texts: [], images: [], videos: [], audios: [] })
    const [deviceMediaInputs, setDeviceMediaInputs] = useState<{
        images: AiInputImage[]
        videos: AiInputVideo[]
        audios: AiInputAudio[]
    }>({ images: [], videos: [], audios: [] })
    const [threadTreeMediaLoading, setThreadTreeMediaLoading] = useState(false)
    const [settingsActionDefinitionId, setSettingsActionDefinitionId] = useState<string | null>(
        null,
    )
    const [focusedSettingsInputKind, setFocusedSettingsInputKind] = useState<AiInputKind | null>(
        null,
    )
    const [isSelectingExtendModel, setIsSelectingExtendModel] = useState(false)
    const [pendingExtendModelCommandTag, setPendingExtendModelCommandTag] = useState<string | null>(
        null,
    )
    const [settingsReadOnly, setSettingsReadOnly] = useState(false)
    const [directInputEdit, setDirectInputEdit] = useState(false)
    const [directSettingsEdit, setDirectSettingsEdit] = useState(false)
    const [directModelPickerEdit, setDirectModelPickerEdit] = useState(false)
    const [modelPickerScope, setModelPickerScope] = useState<ModelFilterTab | null>(null)
    const [directExtendModelEdit, setDirectExtendModelEdit] = useState(false)
    const [directReferencesEdit, setDirectReferencesEdit] = useState(false)
    const [referencesActionDefinitionId, setReferencesActionDefinitionId] = useState<string | null>(
        null,
    )
    const [referenceInputEdit, setReferenceInputEdit] = useState<{
        actionDefinitionId: string
        kind: AiReferenceInputKind
    } | null>(null)
    const [stitchVideoInputSlot, setStitchVideoInputSlot] = useState<number | null>(null)
    const [draftModelSettings, setDraftModelSettings] = useState<ModelSettings>({})
    const [gifVideoMetadata, setGifVideoMetadata] = useState<GifVideoMetadata | null>(null)
    const [modelSettingsByActionDefinitionId, setModelSettingsByActionDefinitionId] = useState<
        Record<string, ModelSettings>
    >({})
    const [draftAiActionAttrsByActionDefinitionId, setDraftAiActionAttrsByActionDefinitionId] =
        useState<Record<string, Partial<ActionRequestFormState>>>({})
    const [mobileViewportMetrics, setMobileViewportMetrics] = useState({
        keyboardInset: 0,
        visualHeight: 0,
    })
    const attachImageInputRef = useRef<HTMLInputElement | null>(null)
    const attachVideoInputRef = useRef<HTMLInputElement | null>(null)
    const attachAudioInputRef = useRef<HTMLInputElement | null>(null)
    const skipNextInputSyncRef = useRef(false)
    const modelSearchInputRef = useRef<HTMLInputElement | null>(null)
    const promptEditorRef = useRef<any>(null)
    const promptEditorContainerRef = useRef<HTMLDivElement | null>(null)
    const editingActionRequestAttrsRef = useRef<Partial<ActionRequestFormState> | null>(null)
    const editingAiActionRef = useRef<AiActionPayload | null>(null)
    const editingActionRequestUpdaterRef = useRef<((action: AiActionPayload) => void) | null>(null)
    const pendingExtendModelCommandTagRef = useRef<string | null>(null)
    const promptDraftCommitCallbackRef = useRef<(payload: DirectPromptDraftCommitPayload) => void>(
        () => {},
    )
    const pendingPromptDraftCommitPayloadRef = useRef<DirectPromptDraftCommitPayload | null>(null)
    const debouncedPromptDraftCommitRef = useRef<any>(null)
    if (!debouncedPromptDraftCommitRef.current) {
        debouncedPromptDraftCommitRef.current = debounce(
            (payload: DirectPromptDraftCommitPayload) => {
                promptDraftCommitCallbackRef.current(payload)
                if (pendingPromptDraftCommitPayloadRef.current === payload) {
                    pendingPromptDraftCommitPayloadRef.current = null
                }
            },
            500,
        )
    }

    const setPromptEditorSeedText = (
        nextText: string,
        { preserveDefault = false }: { preserveDefault?: boolean } = {},
    ) => {
        if (!preserveDefault) setPromptEditorDefaultText('')
        setDraftTextInput(nextText)
        setPromptEditorHasText(!!nextText.trim())
    }

    const readPromptEditorText = (fallback = '') => {
        const nextText = promptEditorRef.current?.getText?.()
        return typeof nextText === 'string' ? nextText : fallback
    }

    const isTextLikeSettingsFocused = () =>
        focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics'

    const readSelectedTextInput = () => {
        if (isSelectingTextInput && (isEditingTextInput || isTextLikeSettingsFocused())) {
            return readPromptEditorText(draftTextInput)
        }

        return pendingTextInput?.text || ''
    }

    const setCurrentEditingActionRequestUpdater = (
        updater: ((action: AiActionPayload) => void) | null,
    ) => {
        editingActionRequestUpdaterRef.current = updater
    }
    const focusPromptEditorElement = () => {
        promptEditorRef.current?.focus?.()
        const promptElement = promptEditorContainerRef.current?.querySelector<HTMLElement>(
            '[contenteditable="true"], .ProseMirror',
        )
        try {
            promptElement?.focus?.({ preventScroll: true })
        } catch (error) {
            promptElement?.focus?.()
        }
    }
    const focusPromptEditor = ({ immediate = false } = {}) => {
        const focus = () => focusPromptEditorElement()
        if (immediate) focus()
        window.requestAnimationFrame(() => {
            focus()
            window.setTimeout(focus, 0)
            window.setTimeout(focus, 80)
        })
    }

    const actionDefinitions = useMemo(
        () =>
            storeActionDefinitions.length > 0
                ? storeActionDefinitions
                : buildActionDefinitions(aiModels),
        [aiModels, storeActionDefinitions],
    )

    const modelSections = useMemo<ModelSection[]>(() => {
        const chooserActionDefinitions = buildActionModalDefinitions(actionDefinitions)
        const actionsBySection = chooserActionDefinitions.reduce<
            Record<ActionSectionId, ActionDefinition[]>
        >(
            (sections, actionDefinition) => {
                const sectionId =
                    actionDefinition.section ||
                    modelFilterTabForModelType(actionDefinition.modelType)
                if (sectionId) sections[sectionId].push(actionDefinition)
                return sections
            },
            { image: [], video: [], audio: [], tools: [] },
        )
        const sortModels = (models: ActionDefinition[]) =>
            sortByPriority(models, (a, b) => {
                const priceComparison = modelPrice(a, questIsPublic) - modelPrice(b, questIsPublic)
                if (priceComparison !== 0) return priceComparison
                return a.humanName.localeCompare(b.humanName)
            })
        const orderSectionModels = (sectionId: ModelSection['id'], models: ActionDefinition[]) => {
            const sortedModels = sortModels(models)
            const firstCommandTags = FIRST_COMMAND_TAGS_BY_SECTION[sectionId] || []
            if (firstCommandTags.length === 0) return sortedModels

            const firstModels = firstCommandTags
                .map(commandTag => sortedModels.find(model => model.actionTagName === commandTag))
                .filter(Boolean) as ActionDefinition[]

            return [
                ...firstModels,
                ...sortedModels.filter(model => !firstCommandTags.includes(model.actionTagName)),
            ]
        }

        return [
            {
                id: 'image',
                label: 'Image',
                icon: ImageIcon,
                items: orderSectionModels('image', actionsBySection.image),
            },
            {
                id: 'video',
                label: 'Video',
                icon: VideoIcon,
                items: orderSectionModels('video', actionsBySection.video),
            },
            {
                id: 'audio',
                label: 'Audio',
                icon: MusicIcon,
                items: orderSectionModels('audio', actionsBySection.audio),
            },
            {
                id: 'tools',
                label: 'Tools',
                icon: Wrench,
                items: orderSectionModels('tools', actionsBySection.tools),
            },
        ].filter(section => section.items.length > 0) as ModelSection[]
    }, [actionDefinitions, questIsPublic])

    const allChooserModels = useMemo(
        () => modelSections.flatMap(section => section.items),
        [modelSections],
    )
    const actionDefinitionForId = (actionDefinitionId: string | null | undefined) => {
        if (!actionDefinitionId) return null
        return allChooserModels.find(model => model.id === actionDefinitionId) || null
    }
    const selectedActionDefinitions = useMemo(
        () =>
            selectedActionDefinitionIds
                .map(actionDefinitionId =>
                    allChooserModels.find(model => model.id === actionDefinitionId),
                )
                .filter(Boolean) as ActionDefinition[],
        [allChooserModels, selectedActionDefinitionIds],
    )
    const actionDefinitionForAttrs = (
        attrs: Partial<ActionRequestFormState> | null | undefined,
    ) => {
        if (!attrs) return null
        return (
            actionDefinitionForId(attrs.actionDefinitionId) ||
            allChooserModels.find(model => {
                if (attrs.kind === 'tool' && attrs.toolKey) {
                    return model.kind === 'tool' && model.toolKey === attrs.toolKey
                }
                if (attrs.kind === 'model' && attrs.aiModelId) {
                    return (
                        model.kind === 'model' &&
                        (model.aiModel?.id === attrs.aiModelId ||
                            model.defaultAiModelId === attrs.aiModelId ||
                            model.id === attrs.aiModelId)
                    )
                }
                return model.actionTagName === attrs.tag
            }) ||
            null
        )
    }
    const extendActionDefinition = useMemo(
        () => allChooserModels.find(model => model.actionTagName === 'extend') || null,
        [allChooserModels],
    )
    const extendActionDefinitionId = extendActionDefinition?.id || null

    const selectedModel = useMemo(() => {
        return actionDefinitionForId(selectedActionDefinitionId)
    }, [allChooserModels, selectedActionDefinitionId])
    const visibleModelSections = useMemo(() => {
        const normalizedSearch = modelSearchQuery.trim().toLowerCase()
        const matchesSearch = (model: IAiModel) => {
            if (!normalizedSearch) return true
            return [
                modelDisplayName(model),
                model.humanName,
                model.name,
                model.actionTagName,
                model.description,
                model.provider,
            ]
                .filter(Boolean)
                .some(value => String(value).toLowerCase().includes(normalizedSearch))
        }

        return modelSections
            .filter(section => {
                if (modelPickerScope) return section.id === modelPickerScope
                return !activeModelFilterTab || section.id === activeModelFilterTab
            })
            .map(section => ({
                ...section,
                items: section.items.filter(matchesSearch),
            }))
            .filter(section => section.items.length > 0)
    }, [activeModelFilterTab, modelPickerScope, modelSearchQuery, modelSections])

    const settingsModel = useMemo(() => {
        return actionDefinitionForId(settingsActionDefinitionId)
    }, [allChooserModels, settingsActionDefinitionId])
    const isAnyAiActionModalOpen =
        !!settingsModel || isSelectingExtendModel || isBrowsingTextPrompts
    useEffect(() => {
        if (!isAnyAiActionModalOpen) {
            setMobileViewportMetrics({ keyboardInset: 0, visualHeight: 0 })
            return
        }
        if (typeof window === 'undefined') return

        const visualViewport = window.visualViewport
        const updateViewportMetrics = () => {
            const viewportHeight = Math.round(visualViewport?.height || window.innerHeight)
            const keyboardInset = visualViewport
                ? Math.max(
                      0,
                      Math.round(
                          window.innerHeight - visualViewport.height - visualViewport.offsetTop,
                      ),
                  )
                : 0

            setMobileViewportMetrics(previous => {
                if (
                    previous.keyboardInset === keyboardInset &&
                    previous.visualHeight === viewportHeight
                ) {
                    return previous
                }

                return { keyboardInset, visualHeight: viewportHeight }
            })
        }

        updateViewportMetrics()
        visualViewport?.addEventListener('resize', updateViewportMetrics)
        visualViewport?.addEventListener('scroll', updateViewportMetrics)
        window.addEventListener('orientationchange', updateViewportMetrics)

        return () => {
            visualViewport?.removeEventListener('resize', updateViewportMetrics)
            visualViewport?.removeEventListener('scroll', updateViewportMetrics)
            window.removeEventListener('orientationchange', updateViewportMetrics)
        }
    }, [isAnyAiActionModalOpen])
    const keyboardAwareModalStyle = useMemo(
        () =>
            ({
                '--ai-action-keyboard-inset': `${mobileViewportMetrics.keyboardInset}px`,
                '--ai-action-visual-viewport-height': mobileViewportMetrics.visualHeight
                    ? `${mobileViewportMetrics.visualHeight}px`
                    : '100dvh',
            } as React.CSSProperties),
        [mobileViewportMetrics.keyboardInset, mobileViewportMetrics.visualHeight],
    )
    const referencesModel = useMemo(() => {
        return actionDefinitionForId(referencesActionDefinitionId)
    }, [allChooserModels, referencesActionDefinitionId])
    const referenceLimitModel = useMemo(() => {
        if (!referencesModel) return null
        if (referencesModel.actionTagName !== 'extend') return referencesModel

        const extendAttrs = extendActionDefinitionId
            ? draftAiActionAttrsByActionDefinitionId[extendActionDefinitionId] || {}
            : {}
        const selectedExtendModel =
            extendModelForModelId(
                draftModelSettings.extendAiModelId ||
                    (extendActionDefinitionId
                        ? modelSettingsByActionDefinitionId[extendActionDefinitionId]
                              ?.extendAiModelId
                        : undefined) ||
                    extendAttrs.extendAiModelId,
                aiModels,
            ) ||
            (() => {
                const selectedExtendCommandTag = latestExtendModelCommandTag(
                    draftModelSettings.extendActionTag ||
                        (extendActionDefinitionId
                            ? modelSettingsByActionDefinitionId[extendActionDefinitionId]
                                  ?.extendActionTag
                            : undefined) ||
                        extendAttrs.extendActionTag,
                    aiModels,
                )
                return (
                    aiModels.find(model => model.actionTagName === selectedExtendCommandTag) || null
                )
            })()

        return selectedExtendModel || referencesModel
    }, [
        aiModels,
        draftAiActionAttrsByActionDefinitionId,
        draftModelSettings.extendAiModelId,
        draftModelSettings.extendActionTag,
        extendActionDefinitionId,
        modelSettingsByActionDefinitionId,
        referencesModel,
    ])

    const activeInputActionDefinitionId =
        referenceInputEdit?.actionDefinitionId ||
        settingsActionDefinitionId ||
        selectedActionDefinitionId
    const activeInputModel = actionDefinitionForId(activeInputActionDefinitionId)
    const activeInputCommandTag = activeInputModel?.actionTagName || null
    const activeAiActionAttrs = activeInputActionDefinitionId
        ? {
              ...(draftAiActionAttrsByActionDefinitionId[activeInputActionDefinitionId] || {}),
              ...(editingActionRequestAttrsRef.current?.actionDefinitionId ===
                  activeInputActionDefinitionId ||
              normalizeActionCommandTag(editingActionRequestAttrsRef.current?.tag) ===
                  normalizeActionCommandTag(activeInputCommandTag)
                  ? editingActionRequestAttrsRef.current || {}
                  : {}),
          }
        : {}
    const effectiveAiModelIdForActionAttrs = (
        model: ActionDefinition,
        attrs: Partial<ActionRequestFormState>,
    ) => {
        const actionKind = attrs.kind || model.kind || 'model'
        if (actionKind !== 'model') return null

        const baseAiModelId = aiModelIdForActionDefinition(model)
        const effectiveModel = findEffectiveModelForAction(
            aiModels,
            attrs.tag || model.actionTagName,
            {
                ...attrs,
                aiModelId: baseAiModelId,
            },
        )

        return effectiveModel?.id || baseAiModelId
    }
    const buildDirectPromptDraftCommitPayload = (
        nextText: string,
    ): DirectPromptDraftCommitPayload | null => {
        const actionDefinitionId = activeInputActionDefinitionId
        const updater = editingActionRequestUpdaterRef.current
        if (!actionDefinitionId || !directInputEdit || !isTextLikeSettingsFocused() || !updater) {
            return null
        }

        const model = actionDefinitionForId(actionDefinitionId)
        if (!model) return null

        const actionKind = model.kind || 'model'
        const attrs: Partial<ActionRequestFormState> = {
            ...activeAiActionAttrs,
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            actionDefinitionId: model.id,
            kind: actionKind,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            type: actionKind,
            modelType: model.modelType,
        }
        attrs.aiModelId = effectiveAiModelIdForActionAttrs(model, attrs)

        return {
            actionDefinitionId,
            attrs,
            existingAction: editingAiActionRef.current,
            nextText,
            updater,
        }
    }
    const isCurrentDirectPromptDraftCommitTarget = (payload: DirectPromptDraftCommitPayload) => {
        return (
            activeInputActionDefinitionId === payload.actionDefinitionId &&
            directInputEdit &&
            isTextLikeSettingsFocused() &&
            editingActionRequestUpdaterRef.current === payload.updater
        )
    }
    const isSameDirectPromptDraftCommitTarget = (
        left: DirectPromptDraftCommitPayload,
        right: DirectPromptDraftCommitPayload,
    ) => left.actionDefinitionId === right.actionDefinitionId && left.updater === right.updater
    const inputStateFromAttrs = (attrs: Partial<ActionRequestFormState>) => ({
        text: attrs.textInputValue || attrs.promptContent || '',
        lyrics: attrs.lyrics || '',
        image: attrs.imageInput?.url
            ? attrs.imageInput
            : attrs.imageInputUrl
            ? ({
                  id: `ai-action-image-${attrs.imageInputUrl}`,
                  url: attrs.imageInputUrl,
                  label: 'Image input',
              } as AiInputImage)
            : null,
        video: attrs.videoInput?.url
            ? attrs.videoInput
            : attrs.videoInputUrl
            ? ({
                  id: `ai-action-video-${attrs.videoInputUrl}`,
                  url: attrs.videoInputUrl,
                  label: 'Video input',
                  previewImageUrl: attrs.videoPreviewImageUrl || null,
                  actionTag: attrs.videoInputActionTag || null,
              } as AiInputVideo)
            : null,
        audio: attrs.audioInput?.url
            ? attrs.audioInput
            : attrs.hasAudioInput && attrs.audioInputUrl
            ? ({
                  id: `ai-action-audio-${attrs.audioInputUrl}`,
                  url: attrs.audioInputUrl,
                  label: attrs.audioInputLabel || 'Audio input',
                  contentType: attrs.audioInputContentType || undefined,
                  file: attrs.audioInputFile || null,
                  source: attrs.audioInputSource || null,
                  uploadId: attrs.audioInputUploadId || null,
                  signedId: attrs.audioInputSignedId || null,
              } as AiInputAudio)
            : null,
    })
    const activeModelInputs = inputStateFromAttrs(activeAiActionAttrs)
    const textInput = activeModelInputs.text || ''
    const lyricsInput = activeModelInputs.lyrics || ''
    const imageInput = activeModelInputs.image || null
    const stitchVideoInputs =
        activeInputCommandTag === 'stitch'
            ? activeAiActionAttrs.videoInputs || activeAiActionAttrs.referenceVideoInputs || []
            : []
    const stitchVideoInput =
        activeInputCommandTag === 'stitch' && stitchVideoInputSlot !== null
            ? stitchVideoInputs[stitchVideoInputSlot] || null
            : null
    const videoInput = stitchVideoInput || activeModelInputs.video || null
    const audioInput = activeModelInputs.audio || null
    const promptReferences = useMemo<PromptReferenceMention[]>(() => {
        const mentionItems: PromptReferenceMention[] = []
        const seenInputs = new Set<string>()
        const addReferenceMentions = (
            kind: 'image' | 'video' | 'audio',
            inputs: Array<{
                id?: string | null
                label?: string | null
                url?: string | null
                previewImageUrl?: string | null
                contentType?: string | null
            }> = [],
        ) => {
            inputs.forEach((input, index) => {
                const identity = input.id || input.url
                if (!identity || seenInputs.has(`${kind}:${identity}`)) return

                seenInputs.add(`${kind}:${identity}`)
                const label = `${kind}${mentionItems.filter(item => item.kind === kind).length + 1}`
                const sourceLabel = input.label?.replace(/^\[|\]$/g, '') || `${label} reference`

                mentionItems.push({
                    id: `ai-action-reference-${kind}-${input.id || input.url || index}`,
                    label,
                    kind,
                    url: input.url || null,
                    previewUrl: input.previewImageUrl || input.url || null,
                    contentType: input.contentType || null,
                    meta: sourceLabel,
                })
            })
        }

        addReferenceMentions('image', activeAiActionAttrs.referenceImageInputs || [])
        addReferenceMentions('video', activeAiActionAttrs.referenceVideoInputs || [])
        addReferenceMentions('audio', activeAiActionAttrs.referenceAudioInputs || [])

        return mentionItems
    }, [
        activeAiActionAttrs.referenceImageInputs,
        activeAiActionAttrs.referenceVideoInputs,
        activeAiActionAttrs.referenceAudioInputs,
    ])

    const addInputOrder = (
        attrs: Partial<ActionRequestFormState>,
        inputKey: string,
    ): Partial<ActionRequestFormState> => ({
        ...attrs,
        inputOrder: [...(attrs.inputOrder || []).filter(key => key !== inputKey), inputKey],
    })

    const removeInputOrder = (
        attrs: Partial<ActionRequestFormState>,
        inputKey: string,
    ): Partial<ActionRequestFormState> => ({
        ...attrs,
        inputOrder: (attrs.inputOrder || []).filter(key => key !== inputKey),
    })

    const textInputAttrsForManualEdit = (
        attrs: Partial<ActionRequestFormState>,
        nextText: string,
    ): Partial<ActionRequestFormState> => {
        const trimmedText = nextText.trim()
        const currentDraftText = getCurrentTextInput?.()?.trim() || ''
        const syncWithDraft = trimmedText !== currentDraftText ? false : attrs.syncWithDraft

        return {
            ...addInputOrder(attrs, 'text'),
            hasTextInput: !!trimmedText,
            textInputValue: trimmedText || null,
            promptContent: trimmedText || null,
            promptPlacement: attrs.promptPlacement || attrs.prompt_placement || null,
            syncWithDraft,
            ...(!trimmedText ? { inputOrder: removeInputOrder(attrs, 'text').inputOrder } : {}),
        }
    }

    const lyricsInputAttrsForManualEdit = (
        attrs: Partial<ActionRequestFormState>,
        nextLyrics: string,
    ): Partial<ActionRequestFormState> => {
        const trimmedLyrics = nextLyrics.trim()
        return {
            ...(trimmedLyrics ? addInputOrder(attrs, 'lyrics') : removeInputOrder(attrs, 'lyrics')),
            hasLyrics: !!trimmedLyrics,
            lyrics: trimmedLyrics || null,
        }
    }

    const textLikeInputAttrsForManualEdit = (
        attrs: Partial<ActionRequestFormState>,
        nextText: string,
    ): Partial<ActionRequestFormState> =>
        focusedSettingsInputKind === 'lyrics'
            ? lyricsInputAttrsForManualEdit(attrs, nextText)
            : textInputAttrsForManualEdit(attrs, nextText)

    const resolvedDraftAttrsForActionDefinitionId = (actionDefinitionId: string | null) => {
        const model = actionDefinitionForId(actionDefinitionId)
        if (!model) return {}
        const actionKind = model.kind || 'model'
        const attrs: Partial<ActionRequestFormState> = {
            ...(draftAiActionAttrsByActionDefinitionId[model.id] || {}),
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            actionDefinitionId: model.id,
            kind: actionKind,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            type: actionKind,
            modelType: model.modelType,
        }
        attrs.aiModelId = effectiveAiModelIdForActionAttrs(model, attrs)

        if (model.id === activeInputActionDefinitionId && isEditingTextInput) {
            return textLikeInputAttrsForManualEdit(
                attrs,
                readPromptEditorText(draftTextInput).trim(),
            )
        }

        return attrs
    }

    const getModelInputs = (actionDefinitionId: string) =>
        inputStateFromAttrs(resolvedDraftAttrsForActionDefinitionId(actionDefinitionId))

    const gifSourceVideo =
        settingsModel?.actionTagName === 'gif' && settingsModel.id
            ? getModelInputs(settingsModel.id).video || null
            : null

    useEffect(() => {
        if (!gifSourceVideo?.url) {
            setGifVideoMetadata(null)
            return
        }

        if (gifSourceVideo.durationSeconds || gifSourceVideo.width || gifSourceVideo.height) {
            setGifVideoMetadata({
                durationSeconds: gifSourceVideo.durationSeconds || null,
                width: gifSourceVideo.width || null,
                height: gifSourceVideo.height || null,
            })
            return
        }

        let isCancelled = false
        const video = document.createElement('video')
        video.preload = 'metadata'
        video.muted = true
        video.playsInline = true

        const handleLoadedMetadata = () => {
            if (isCancelled) return
            setGifVideoMetadata({
                durationSeconds:
                    Number.isFinite(video.duration) && video.duration > 0 ? video.duration : null,
                width:
                    Number.isFinite(video.videoWidth) && video.videoWidth > 0
                        ? video.videoWidth
                        : null,
                height:
                    Number.isFinite(video.videoHeight) && video.videoHeight > 0
                        ? video.videoHeight
                        : null,
            })
        }
        const handleError = () => {
            if (!isCancelled) setGifVideoMetadata(null)
        }

        video.addEventListener('loadedmetadata', handleLoadedMetadata)
        video.addEventListener('error', handleError)
        video.src = gifSourceVideo.url
        video.load()

        return () => {
            isCancelled = true
            video.removeEventListener('loadedmetadata', handleLoadedMetadata)
            video.removeEventListener('error', handleError)
            video.removeAttribute('src')
            video.load()
        }
    }, [
        gifSourceVideo?.durationSeconds,
        gifSourceVideo?.height,
        gifSourceVideo?.url,
        gifSourceVideo?.width,
    ])

    useEffect(() => {
        if (settingsModel?.actionTagName !== 'gif') return

        const currentFrameRate = draftModelSettings.gifFrameRate || DEFAULT_GIF_FRAME_RATE
        const currentMaxWidth = draftModelSettings.gifMaxWidth || DEFAULT_GIF_MAX_WIDTH
        if (gifOptionAllowed(gifVideoMetadata, currentFrameRate, currentMaxWidth)) return

        const { frameRate: safeFrameRate, maxWidth: safeMaxWidth } = downgradedGifSettings(
            gifVideoMetadata,
            currentFrameRate,
            currentMaxWidth,
        )
        if (safeFrameRate === currentFrameRate && safeMaxWidth === currentMaxWidth) return

        setDraftModelSettings(prev => ({
            ...prev,
            gifFrameRate: safeFrameRate,
            gifMaxWidth: safeMaxWidth,
        }))
    }, [
        draftModelSettings.gifFrameRate,
        draftModelSettings.gifMaxWidth,
        gifVideoMetadata,
        settingsModel?.actionTagName,
    ])

    const referenceInputsForActionDefinitionId = (actionDefinitionId: string | null) => {
        const attrs = actionDefinitionId
            ? resolvedDraftAttrsForActionDefinitionId(actionDefinitionId)
            : {}
        return {
            image: attrs.referenceImageInputs || [],
            video: attrs.referenceVideoInputs || [],
            audio: attrs.referenceAudioInputs || [],
        }
    }

    const videoInputsForActionDefinitionId = (actionDefinitionId: string | null) => {
        const model = actionDefinitionForId(actionDefinitionId)
        const attrs = actionDefinitionId
            ? resolvedDraftAttrsForActionDefinitionId(actionDefinitionId)
            : {}
        return (
            attrs.videoInputs ||
            (model?.actionTagName === 'stitch' ? attrs.referenceVideoInputs || [] : [])
        )
    }

    const updateDraftAiActionAttrs = (
        actionDefinitionId: string | null,
        updater: (attrs: Partial<ActionRequestFormState>) => Partial<ActionRequestFormState>,
        options: { commit?: boolean } = {},
    ) => {
        const model = actionDefinitionForId(actionDefinitionId)
        if (!model) return
        const actionKind = model.kind || 'model'
        const editingAttrsForAction =
            editingActionRequestAttrsRef.current?.actionDefinitionId === model.id ||
            normalizeActionCommandTag(editingActionRequestAttrsRef.current?.tag) ===
                normalizeActionCommandTag(model.actionTagName)
                ? editingActionRequestAttrsRef.current
                : null
        const currentAttrs =
            options.commit && editingAttrsForAction
                ? {
                      ...(draftAiActionAttrsByActionDefinitionId[model.id] || {}),
                      ...editingAttrsForAction,
                  }
                : draftAiActionAttrsByActionDefinitionId[model.id] || editingAttrsForAction || {}
        const nextAttrs: Partial<ActionRequestFormState> = {
            ...updater(currentAttrs),
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            actionDefinitionId: model.id,
            kind: actionKind,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            type: actionKind,
            modelType: model.modelType,
        }
        nextAttrs.aiModelId = effectiveAiModelIdForActionAttrs(model, nextAttrs)
        if (editingAttrsForAction) {
            editingActionRequestAttrsRef.current = nextAttrs
        }
        setDraftAiActionAttrsByActionDefinitionId(prev => ({
            ...prev,
            [model.id]: nextAttrs,
        }))
        if (options.commit && editingActionRequestUpdaterRef.current) {
            const nextAction = aiActionFromFormState(
                nextAttrs as ActionRequestFormState,
                editingAiActionRef.current,
            )
            if (nextAction) {
                editingAiActionRef.current = nextAction
                editingActionRequestUpdaterRef.current(nextAction)
            }
        }
    }

    const commitDirectPromptDraftText = (payload: DirectPromptDraftCommitPayload) => {
        const model = actionDefinitionForId(payload.actionDefinitionId)
        if (!model) return

        const trimmedText = payload.nextText.trim()
        const nextAttrs = textLikeInputAttrsForManualEdit(payload.attrs, trimmedText)
        const nextAction = aiActionFromFormState(
            nextAttrs as ActionRequestFormState,
            payload.existingAction,
        )
        if (!nextAction) return

        payload.updater(nextAction)

        if (!isCurrentDirectPromptDraftCommitTarget(payload)) return

        editingActionRequestAttrsRef.current = nextAttrs
        editingAiActionRef.current = nextAction
        selectSingleActionDefinitionId(payload.actionDefinitionId)
        setDraftAiActionAttrsByActionDefinitionId(prev => ({
            ...prev,
            [payload.actionDefinitionId]: nextAttrs,
        }))
        setPendingTextInput(
            trimmedText ? { id: 'custom-text', label: 'Custom', text: trimmedText } : null,
        )
    }

    promptDraftCommitCallbackRef.current = commitDirectPromptDraftText

    const scheduleDirectPromptDraftCommit = (nextText: string) => {
        const payload = buildDirectPromptDraftCommitPayload(nextText)
        if (!payload) return

        const pendingPayload = pendingPromptDraftCommitPayloadRef.current
        if (pendingPayload && !isSameDirectPromptDraftCommitTarget(pendingPayload, payload)) {
            flushDirectPromptDraftCommit()
        }

        pendingPromptDraftCommitPayloadRef.current = payload
        debouncedPromptDraftCommitRef.current?.(payload)
    }

    const flushDirectPromptDraftCommit = () => {
        debouncedPromptDraftCommitRef.current?.flush?.()
        pendingPromptDraftCommitPayloadRef.current = null
    }

    const cancelDirectPromptDraftCommit = () => {
        debouncedPromptDraftCommitRef.current?.cancel?.()
        pendingPromptDraftCommitPayloadRef.current = null
    }

    useEffect(() => {
        return () => {
            debouncedPromptDraftCommitRef.current?.flush?.()
        }
    }, [])

    const referenceInputKeyForKind = (
        kind: AiReferenceInputKind,
    ): 'referenceImageInputs' | 'referenceVideoInputs' | 'referenceAudioInputs' => {
        switch (kind) {
            case 'image':
                return 'referenceImageInputs'
            case 'video':
                return 'referenceVideoInputs'
            case 'audio':
                return 'referenceAudioInputs'
            default:
                return 'referenceImageInputs'
        }
    }

    const closeReferenceSettings = () => {
        const shouldCloseMenu = directReferencesEdit
        setReferencesActionDefinitionId(null)
        setReferenceInputEdit(null)
        setDirectReferencesEdit(false)
        setStitchVideoInputSlot(null)
        setFocusedSettingsInputKind(null)
        setPendingImageInput(null)
        setPendingVideoInput(null)
        setPendingAudioInput(null)
        setIsSelectingImageInput(false)
        setIsSelectingVideoInput(false)
        setIsSelectingAudioInput(false)
        if (shouldCloseMenu) setShowMenu(false)
    }

    const extendModelForCommandTag = (commandTag: string | null | undefined): IAiModel | null => {
        const selectedExtendCommandTag = latestExtendModelCommandTag(commandTag, aiModels)
        if (!selectedExtendCommandTag) return null

        return aiModels.find(model => model.actionTagName === selectedExtendCommandTag) || null
    }

    const selectedExtendModelForSettings = (
        settings: Pick<ModelSettings, 'extendAiModelId' | 'extendActionTag'> | null | undefined,
    ): IAiModel | null => {
        return (
            extendModelForModelId(settings?.extendAiModelId, aiModels) ||
            extendModelForCommandTag(settings?.extendActionTag)
        )
    }

    const closeExtendModelSettings = () => {
        setIsSelectingExtendModel(false)
        pendingExtendModelCommandTagRef.current = null
        setPendingExtendModelCommandTag(null)
        setSettingsActionDefinitionId(null)
        if (directExtendModelEdit) {
            setCurrentEditingActionRequestUpdater(null)
            editingAiActionRef.current = null
            setSettingsReadOnly(false)
            setDirectSettingsEdit(false)
            setDirectExtendModelEdit(false)
            setShowMenu(false)
        }
    }

    const togglePendingExtendModel = (commandTagName: string) => {
        const nextCommandTag =
            latestExtendModelCommandTag(commandTagName, aiModels) ||
            commandTagName ||
            FALLBACK_EXTEND_COMMAND_TAG

        const nextPendingCommandTag =
            pendingExtendModelCommandTagRef.current === nextCommandTag ? null : nextCommandTag
        pendingExtendModelCommandTagRef.current = nextPendingCommandTag
        setPendingExtendModelCommandTag(nextPendingCommandTag)

        const nextExtendModel = extendModelForCommandTag(nextPendingCommandTag)
        if (nextExtendModel) {
            setDraftModelSettings(prev =>
                settingsWithNearestDurationForModel(prev, nextExtendModel),
            )
        }
    }

    const applyExtendModelSelection = () => {
        const nextCommandTag =
            pendingExtendModelCommandTagRef.current ?? pendingExtendModelCommandTag
        const nextExtendModel = extendModelForCommandTag(nextCommandTag)
        const extendActionModel = extendActionDefinition
        if (!extendActionModel) return

        updateDraftAiActionAttrs(
            extendActionModel.id,
            attrs => {
                const nextSettings = settingsWithNearestDurationForModel(
                    {
                        ...settingsFromActionRequestAttrs(
                            extendActionModel,
                            attrs as ActionRequestFormState,
                        ),
                        ...draftModelSettings,
                        extendAiModelId: nextExtendModel?.id || undefined,
                        extendActionTag: nextCommandTag || undefined,
                        extendTimestamp: undefined,
                    },
                    nextExtendModel,
                )

                return {
                    ...attrs,
                    extendAiModelId: nextExtendModel?.id || null,
                    extendActionTag: null,
                    durationSeconds: nextSettings.durationSeconds ?? null,
                    extendTimestamp: null,
                    commandText: configuredActionCommand(extendActionModel, nextSettings),
                }
            },
            { commit: !!editingActionRequestUpdaterRef.current },
        )
        setModelSettingsByActionDefinitionId(prev => ({
            ...prev,
            [extendActionModel.id]: {
                ...(prev[extendActionModel.id] || {}),
                extendAiModelId: nextExtendModel?.id || undefined,
                extendActionTag: undefined,
                durationSeconds: settingsWithNearestDurationForModel(
                    { ...(prev[extendActionModel.id] || {}), ...draftModelSettings },
                    nextExtendModel,
                ).durationSeconds,
            },
        }))
        setDraftModelSettings(prev =>
            settingsWithNearestDurationForModel(
                {
                    ...prev,
                    extendAiModelId: nextExtendModel?.id || undefined,
                    extendActionTag: nextCommandTag || undefined,
                    extendTimestamp: undefined,
                },
                nextExtendModel,
            ),
        )
        closeExtendModelSettings()
    }

    const selectSingleActionDefinitionId = (actionDefinitionId: string | null) => {
        setSelectedActionDefinitionId(actionDefinitionId)
    }

    const openReferenceInputSettings = (model: ActionDefinition, kind: AiReferenceInputKind) => {
        selectSingleActionDefinitionId(model.id)
        setReferencesActionDefinitionId(model.id)
        setReferenceInputEdit({ actionDefinitionId: model.id, kind })
        setStitchVideoInputSlot(null)
        setFocusedSettingsInputKind(kind)
        setPendingImageInput(null)
        setPendingVideoInput(null)
        setPendingAudioInput(null)
        setPendingTextInput(null)
        setPromptEditorSeedText('')
        setIsEditingTextInput(false)
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
        setIsSelectingImageInput(kind === 'image')
        setIsSelectingVideoInput(kind === 'video')
        setIsSelectingAudioInput(kind === 'audio')
    }

    const closeReferenceInputSettings = () => {
        setReferenceInputEdit(null)
        setStitchVideoInputSlot(null)
        setFocusedSettingsInputKind(null)
        setPendingImageInput(null)
        setPendingVideoInput(null)
        setPendingAudioInput(null)
        setIsSelectingImageInput(false)
        setIsSelectingVideoInput(false)
        setIsSelectingAudioInput(false)
    }

    const addReferenceInput = (
        actionDefinitionId: string | null,
        kind: AiReferenceInputKind,
        input: AiInputImage | AiInputVideo | AiInputAudio,
    ) => {
        if (!actionDefinitionId) return
        const actionDefinition = actionDefinitionForId(actionDefinitionId)
        const model =
            actionDefinition?.actionTagName === 'extend'
                ? referenceLimitModel
                : referencesModel || actionDefinition
        const attrs = resolvedDraftAttrsForActionDefinitionId(actionDefinitionId)
        const isVideoKeyframeImage =
            kind === 'image' &&
            videoKeyframeModeForFormState(
                model?.actionTagName,
                !!attrs.imageInput,
                attrs.videoKeyframeMode === true,
            )
        const limit = isVideoKeyframeImage ? 1 : referenceInputLimitForModel(model, kind)
        if (limit <= 0) return
        const key = referenceInputKeyForKind(kind)
        updateDraftAiActionAttrs(
            actionDefinitionId,
            attrs => {
                const attrsRecord = attrs as Record<string, any>
                const currentInputs = Array.isArray(attrsRecord[key]) ? attrsRecord[key] || [] : []
                const nextInputs = isVideoKeyframeImage ? [] : [...currentInputs]
                const isSameInput =
                    kind === 'image'
                        ? areSameImageInput
                        : kind === 'video'
                        ? areSameVideoInput
                        : areSameAudioInput
                if (!nextInputs.some(existing => isSameInput(existing, input as any))) {
                    nextInputs.push(input as any)
                }

                return {
                    ...addInputOrder(attrs, `references-${kind}`),
                    [key]: nextInputs.slice(0, limit),
                    ...(isVideoKeyframeImage ? { videoKeyframeMode: true } : {}),
                }
            },
            { commit: directReferencesEdit },
        )
        closeReferenceInputSettings()
    }

    const removeReferenceInput = (
        actionDefinitionId: string | null,
        kind: AiReferenceInputKind,
        targetInput: AiInputImage | AiInputVideo | AiInputAudio,
    ) => {
        if (!actionDefinitionId) return
        const key = referenceInputKeyForKind(kind)
        const isSameInput =
            kind === 'image'
                ? areSameImageInput
                : kind === 'video'
                ? areSameVideoInput
                : areSameAudioInput
        updateDraftAiActionAttrs(
            actionDefinitionId,
            attrs => ({
                ...(() => {
                    const nextInputs = (
                        Array.isArray((attrs as Record<string, any>)[key])
                            ? (attrs as Record<string, any>)[key] || []
                            : []
                    ).filter(input => !isSameInput(input, targetInput as any))

                    return {
                        ...(nextInputs.length
                            ? attrs
                            : removeInputOrder(attrs, `references-${kind}`)),
                        [key]: nextInputs,
                    }
                })(),
            }),
            { commit: directReferencesEdit },
        )
    }

    const mediaPreviewIsReference = (target: AiMediaPreviewTarget) => {
        const actionDefinitionId =
            referenceInputEdit?.actionDefinitionId || referencesModel?.id || null
        if (!actionDefinitionId) return false

        const references = referenceInputsForActionDefinitionId(actionDefinitionId)
        return (references[target.kind as AiReferenceInputKind] || []).some(input => {
            if (target.kind === 'image') return areSameImageInput(input, target.input as any)
            if (target.kind === 'video') return areSameVideoInput(input, target.input as any)
            return areSameAudioInput(input, target.input as any)
        })
    }

    const removeMediaPreviewReference = () => {
        const target = mediaPreviewTarget
        const actionDefinitionId =
            referenceInputEdit?.actionDefinitionId || referencesModel?.id || null
        if (!target || !actionDefinitionId || !mediaPreviewIsReference(target)) return

        removeReferenceInput(
            actionDefinitionId,
            target.kind as AiReferenceInputKind,
            target.input as AiInputImage | AiInputVideo | AiInputAudio,
        )

        if (target.kind === 'image') setPendingImageInput(null)
        if (target.kind === 'video') setPendingVideoInput(null)
        if (target.kind === 'audio') setPendingAudioInput(null)
        setMediaPreviewTarget(null)
    }

    const selectMediaPreviewInput = async () => {
        const target = mediaPreviewTarget
        if (!target) return
        const isSelectingReference = !!referenceInputEdit

        if (target.kind === 'image') {
            applyImageInputWithInput(target.input as AiInputImage)
        } else if (target.kind === 'video') {
            await applyVideoInputWithInput(target.input as AiInputVideo)
        } else if (target.kind === 'audio') {
            applyAudioInputWithInput(target.input as AiInputAudio)
        }

        setMediaPreviewTarget(null)
        if (!isSelectingReference) closeMenu()
    }

    const getCurrentDraftInputSnapshot = () => {
        const text = getCurrentTextInput?.()?.trim() || ''

        return {
            text,
            image: getCurrentImageInput?.() || null,
            video: getCurrentVideoInput?.() || null,
            audio: getCurrentAudioInput?.() || null,
        }
    }

    const draftInputAttrsForModel = (
        model: ActionDefinition,
        draftInputs: AiMagicWandCurrentDraftInputs,
    ): Partial<ActionRequestFormState> => {
        const supportedInputs = supportedInputKindsForModel(model)
        const attrs = buildDraftInputAttrsForModel({
            model,
            draftInputs,
            supportedInputs,
            effectiveAiModelId: null,
        }) as Partial<ActionRequestFormState>
        attrs.aiModelId = effectiveAiModelIdForActionAttrs(model, attrs)

        return attrs
    }

    const buildDraftInputAttrsByActionDefinitionId = () => {
        const draftInputs = getCurrentDraftInputSnapshot()
        const nextAttrsByActionDefinitionId: Record<string, Partial<ActionRequestFormState>> = {}

        modelSections
            .flatMap(section => section.items)
            .forEach(model => {
                nextAttrsByActionDefinitionId[model.id] = draftInputAttrsForModel(
                    model,
                    draftInputs,
                )
            })

        return nextAttrsByActionDefinitionId
    }

    useEffect(() => {
        onMenuStateChange?.(showMenu)
    }, [showMenu, onMenuStateChange])

    useEffect(() => {
        if (
            !getThreadTreeMediaInputs ||
            !showMenu ||
            (!isSelectingTextInput &&
                !isSelectingImageInput &&
                !isSelectingVideoInput &&
                !isSelectingAudioInput)
        ) {
            return
        }

        let cancelled = false
        setThreadTreeMediaLoading(true)
        getThreadTreeMediaInputs()
            .then(media => {
                if (cancelled) return
                setThreadTreeMediaInputs({
                    texts: media?.texts || [],
                    images: media?.images || [],
                    videos: media?.videos || [],
                    audios: media?.audios || [],
                })
            })
            .catch(() => {
                if (!cancelled) {
                    setThreadTreeMediaInputs({ texts: [], images: [], videos: [], audios: [] })
                }
            })
            .finally(() => {
                if (!cancelled) setThreadTreeMediaLoading(false)
            })

        return () => {
            cancelled = true
        }
    }, [
        getThreadTreeMediaInputs,
        isSelectingTextInput,
        isSelectingAudioInput,
        isSelectingImageInput,
        isSelectingVideoInput,
        showMenu,
    ])

    useEffect(() => {
        return () => {
            onMenuStateChange?.(false)
        }
    }, [onMenuStateChange])

    useEffect(() => {
        if (!showMenu) return

        const models = modelSections.flatMap(section => section.items)
        if (models.length === 0) {
            setSelectedActionDefinitionId(null)
            setSelectedActionDefinitionIds([])
            return
        }

        const modelIds = new Set(models.map(model => model.id))
        const selectedModelStillPresent = models.some(
            model => model.id === selectedActionDefinitionId,
        )
        const selectedActionDefinitionIdsStillPresent = selectedActionDefinitionIds.filter(id =>
            modelIds.has(id),
        )

        if (selectedActionDefinitionId && !selectedModelStillPresent) {
            setSelectedActionDefinitionId(null)
        }
        if (selectedActionDefinitionIdsStillPresent.length !== selectedActionDefinitionIds.length) {
            setSelectedActionDefinitionIds(selectedActionDefinitionIdsStillPresent)
        }
    }, [modelSections, selectedActionDefinitionId, selectedActionDefinitionIds, showMenu])

    useEffect(() => {
        if (showMenu) {
            setExpandedMoreSections({})
        }
    }, [showMenu])

    useEffect(() => {
        if (!isModelSearchActive) return
        window.requestAnimationFrame(() => {
            modelSearchInputRef.current?.focus()
        })
    }, [isModelSearchActive])
    useLayoutEffect(() => {
        const shouldFocusPromptEditor =
            settingsActionDefinitionId &&
            (focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics') &&
            isSelectingTextInput

        if (!shouldFocusPromptEditor) return
        focusPromptEditor({ immediate: true })
    }, [
        focusedSettingsInputKind,
        isSelectingTextInput,
        settingsActionDefinitionId,
        textInputEditorRevision,
    ])

    useEffect(() => {
        const handleEditActionRequest = (event: Event) => {
            const detail = ((event as CustomEvent).detail || {}) as EditActionRequestDetail
            if (detail.__treechatAiActionEditHandled === true) return

            const aiAction = detail.aiAction as AiActionPayload | undefined
            const actionAttrs = aiAction ? aiActionFormStateFromAiAction(aiAction) : null
            const attrs = actionAttrs as ActionRequestFormState | null
            const eventEditor = detail.editor
            const updateAiAction = detail.updateAiAction as
                | ((action: AiActionPayload) => void)
                | undefined
            const readOnly = detail.readOnly === true
            const focusedInputKind = detail.focusedInputKind as AiInputKind | undefined
            const inputSlotIndex =
                typeof detail.inputSlotIndex === 'number' ? detail.inputSlotIndex : null
            const directInputEditFromAction = detail.directInputEdit === true
            const openModelPickerFromAction = detail.openModelPicker === true
            const openReferencesFromAction = detail.openReferences === true
            const openExtendModelFromAction = detail.openExtendModel === true
            const directSettingsEditFromAction =
                detail.directSettingsEdit === true ||
                (!!updateAiAction &&
                    !readOnly &&
                    !focusedInputKind &&
                    !directInputEditFromAction &&
                    !openModelPickerFromAction &&
                    !openReferencesFromAction &&
                    !openExtendModelFromAction)
            if (!attrs?.tag || (!updateAiAction && !readOnly)) return

            const localEditor = getEditor?.()
            const localEditorInstance = localEditor?.getEditor
                ? localEditor.getEditor()
                : localEditor
            if (eventEditor && localEditorInstance && eventEditor !== localEditorInstance) return
            const localEditorElement =
                localEditorInstance?.container || localEditorInstance?.root || null

            const model = actionDefinitionForAttrs(attrs)
            if (!model) return
            const actionKind = model.kind || 'model'
            const normalizedAttrs: ActionRequestFormState = {
                ...attrs,
                tag: model.actionTagName,
                displayName: actionTokenDisplayName(model),
                actionDefinitionId: model.id,
                kind: actionKind,
                toolKey: actionKind === 'tool' ? model.toolKey || null : null,
                type: actionKind,
                modelType: model.modelType,
            }
            normalizedAttrs.aiModelId = effectiveAiModelIdForActionAttrs(model, normalizedAttrs)

            const modelPickerScopeFromAction = modelFilterTabForModelType(
                detail.modelPickerModelType || normalizedAttrs.modelType || model.modelType,
            )
            const defaultPromptText =
                focusedInputKind === 'text' && aiAction?.prompt_is_default === true
                    ? normalizedAttrs.textInputValue?.trim() || ''
                    : ''
            const nextText =
                normalizedAttrs.hasTextInput && !defaultPromptText
                    ? normalizedAttrs.textInputValue?.trim() || ''
                    : ''
            const nextInputs = inputStateFromAttrs(normalizedAttrs)
            const pendingExtendModel =
                extendModelForModelId(normalizedAttrs.extendAiModelId, aiModels) ||
                extendModelForCommandTag(normalizedAttrs.extendActionTag)
            const shouldFocusPromptEditorFromAction =
                !openReferencesFromAction &&
                !readOnly &&
                directInputEditFromAction &&
                (focusedInputKind === 'text' || focusedInputKind === 'lyrics')
            const applyEditActionRequestState = () => {
                editingActionRequestAttrsRef.current = normalizedAttrs
                editingAiActionRef.current =
                    aiAction || aiActionFromFormState(normalizedAttrs) || null
                skipNextInputSyncRef.current = true
                setShowMenu(true)
                setSelectedActionDefinitionId(model.id)
                setActiveModelFilterTab(
                    openModelPickerFromAction ? modelPickerScopeFromAction : null,
                )
                setModelPickerScope(
                    openModelPickerFromAction && !readOnly ? modelPickerScopeFromAction : null,
                )
                setIsModelSearchActive(false)
                setModelSearchQuery('')
                setDraftAiActionAttrsByActionDefinitionId(prev => ({
                    ...prev,
                    [model.id]: {
                        ...normalizedAttrs,
                        textInputValue: nextText || null,
                    },
                }))
                setPromptEditorDefaultText(defaultPromptText)
                setPromptEditorSeedText(nextText, { preserveDefault: true })
                setPendingTextInput(
                    nextText
                        ? {
                              id: 'current-ai-action-text',
                              label: 'Current input',
                              text: nextText,
                          }
                        : null,
                )
                setPendingImageInput(nextInputs.image || null)
                setPendingVideoInput(nextInputs.video || null)
                setPendingAudioInput(nextInputs.audio || null)
                setIsEditingTextInput(false)
                setIsSelectingTextInput(false)
                setIsBrowsingTextPrompts(false)
                setDraftModelSettings(settingsFromActionRequestAttrs(model, normalizedAttrs))
                setSettingsActionDefinitionId(
                    openReferencesFromAction || openModelPickerFromAction ? null : model.id,
                )
                setReferencesActionDefinitionId(openReferencesFromAction ? model.id : null)
                setReferenceInputEdit(null)
                setStitchVideoInputSlot(model.actionTagName === 'stitch' ? inputSlotIndex : null)
                setFocusedSettingsInputKind(readOnly ? null : focusedInputKind || null)
                setCurrentEditingActionRequestUpdater(updateAiAction || null)
                setSettingsReadOnly(readOnly)
                setDirectInputEdit(
                    !openReferencesFromAction &&
                        !openModelPickerFromAction &&
                        !openExtendModelFromAction &&
                        !directSettingsEditFromAction &&
                        !readOnly &&
                        !!focusedInputKind &&
                        directInputEditFromAction,
                )
                setDirectSettingsEdit(directSettingsEditFromAction && !readOnly)
                setDirectModelPickerEdit(openModelPickerFromAction && !readOnly)
                setDirectExtendModelEdit(openExtendModelFromAction && !readOnly)
                setDirectReferencesEdit(openReferencesFromAction && !readOnly)

                setIsEditingTextInput(
                    !openReferencesFromAction && !readOnly && focusedInputKind === 'lyrics',
                )
                setIsSelectingTextInput(
                    !openReferencesFromAction &&
                        !readOnly &&
                        (focusedInputKind === 'text' || focusedInputKind === 'lyrics'),
                )
                setIsSelectingImageInput(
                    !openReferencesFromAction && !readOnly && focusedInputKind === 'image',
                )
                setIsSelectingVideoInput(
                    !openReferencesFromAction && !readOnly && focusedInputKind === 'video',
                )
                setIsSelectingAudioInput(
                    !openReferencesFromAction && !readOnly && focusedInputKind === 'audio',
                )
                setPendingExtendModelCommandTag(pendingExtendModel?.actionTagName || null)
                pendingExtendModelCommandTagRef.current = pendingExtendModel?.actionTagName || null
                setIsSelectingExtendModel(openExtendModelFromAction && !readOnly)
            }

            const applyEditActionRequest = () => {
                if (shouldFocusPromptEditorFromAction) {
                    flushSync(applyEditActionRequestState)
                    focusPromptEditor({ immediate: true })
                    return
                }

                applyEditActionRequestState()
            }

            if (!eventEditor) {
                const editSourceElement =
                    typeof Element !== 'undefined' && detail.editSourceElement instanceof Element
                        ? detail.editSourceElement
                        : null
                const sourceDistance = elementCenterDistance(editSourceElement, localEditorElement)
                const fallbackScore = Number.MAX_SAFE_INTEGER - editHostOrderRef.current

                queueEditActionRequestCandidate(detail, {
                    handle: applyEditActionRequest,
                    order: editHostOrderRef.current,
                    score: editSourceElement ? sourceDistance : fallbackScore,
                })
                return
            }

            detail.__treechatAiActionEditHandled = true
            applyEditActionRequest()
        }

        window.addEventListener('knov:edit-action-request', handleEditActionRequest)
        return () => window.removeEventListener('knov:edit-action-request', handleEditActionRequest)
    }, [aiModels, allChooserModels, getEditor])

    useEffect(() => {
        if (!showMenu) return
        if (skipNextInputSyncRef.current) {
            skipNextInputSyncRef.current = false
            return
        }

        setPromptEditorSeedText('')
        setPromptEditorDefaultText('')
        setPendingTextInput(null)
        setPendingImageInput(null)
        setIsEditingTextInput(false)
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
        setIsSelectingImageInput(false)
        setIsSelectingVideoInput(false)
        setIsSelectingAudioInput(false)
    }, [showMenu])

    useEffect(() => {
        if (!showMenu) return

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key !== 'Escape') return
            if (isSelectingExtendModel) {
                closeExtendModelSettings()
                return
            }
            if (referenceInputEdit) {
                closeReferenceInputSettings()
                return
            }
            if (directModelPickerEdit) {
                closeMenu()
                return
            }
            if (settingsActionDefinitionId) {
                if (directSettingsEdit) {
                    closeMenu()
                    return
                }
                if (directInputEdit) {
                    closeInputSettings()
                    return
                }
                setFocusedSettingsInputKind(null)
                setSettingsActionDefinitionId(null)
                setSettingsReadOnly(false)
                return
            }
            setShowMenu(false)
        }

        document.addEventListener('keydown', handleKeyDown)
        return () => document.removeEventListener('keydown', handleKeyDown)
    }, [
        directExtendModelEdit,
        directInputEdit,
        directSettingsEdit,
        directModelPickerEdit,
        isSelectingExtendModel,
        referenceInputEdit,
        settingsActionDefinitionId,
        showMenu,
    ])

    const toggleMenu = (event?: React.MouseEvent | React.KeyboardEvent) => {
        event?.stopPropagation()
        if (!showMenu) {
            setSelectedActionDefinitionId(null)
            setSelectedActionDefinitionIds([])
            setDraftAiActionAttrsByActionDefinitionId(buildDraftInputAttrsByActionDefinitionId())
            setDraftModelSettings({})
            setModelSettingsByActionDefinitionId({})
            setActiveModelFilterTab(null)
            setModelPickerScope(null)
            setIsModelSearchActive(false)
            setModelSearchQuery('')
            setPromptEditorSeedText('')
            setPromptEditorDefaultText('')
            setPendingTextInput(null)
            setPendingImageInput(null)
            setPendingAudioInput(null)
            setIsEditingTextInput(false)
            setIsSelectingTextInput(false)
            setIsBrowsingTextPrompts(false)
            setIsSelectingImageInput(false)
            setIsSelectingVideoInput(false)
            setIsSelectingAudioInput(false)
            setIsSelectingExtendModel(false)
            setPendingExtendModelCommandTag(null)
            setSettingsActionDefinitionId(null)
            setFocusedSettingsInputKind(null)
            setCurrentEditingActionRequestUpdater(null)
            editingAiActionRef.current = null
            setSettingsReadOnly(false)
            setDirectInputEdit(false)
            setDirectSettingsEdit(false)
            setDirectModelPickerEdit(false)
            setDirectExtendModelEdit(false)
            setDirectReferencesEdit(false)
            setReferencesActionDefinitionId(null)
            setReferenceInputEdit(null)
        }
        setShowMenu(prev => !prev)
    }

    const closeMenu = () => {
        flushDirectPromptDraftCommit()
        setShowMenu(false)
        setSelectedActionDefinitionIds([])
        setSettingsActionDefinitionId(null)
        setActiveModelFilterTab(null)
        setModelPickerScope(null)
        setIsModelSearchActive(false)
        setModelSearchQuery('')
        setFocusedSettingsInputKind(null)
        setCurrentEditingActionRequestUpdater(null)
        editingAiActionRef.current = null
        setSettingsReadOnly(false)
        setDirectInputEdit(false)
        setDirectSettingsEdit(false)
        setDirectModelPickerEdit(false)
        setDirectExtendModelEdit(false)
        setDirectReferencesEdit(false)
        setReferencesActionDefinitionId(null)
        setReferenceInputEdit(null)
        setPendingTextInput(null)
        setPendingImageInput(null)
        setPendingVideoInput(null)
        setPendingAudioInput(null)
        setIsSelectingExtendModel(false)
        setIsBrowsingTextPrompts(false)
        setPendingExtendModelCommandTag(null)
    }

    // toggleMenu and closeMenu are fresh closures each render (they read the
    // current draft inputs and model list), so the handle reaches them
    // through refs rather than capturing one render's copies.
    const menuHandleRef = useRef({ showMenu, toggleMenu, closeMenu })
    menuHandleRef.current = { showMenu, toggleMenu, closeMenu }
    useImperativeHandle(
        ref,
        () => ({
            open: () => {
                if (!menuHandleRef.current.showMenu) menuHandleRef.current.toggleMenu()
            },
            close: () => menuHandleRef.current.closeMenu(),
            isOpen: () => menuHandleRef.current.showMenu,
        }),
        [],
    )

    const closeModelSettings = () => {
        flushDirectPromptDraftCommit()
        const shouldCloseMenu = !!editingActionRequestUpdaterRef.current || settingsReadOnly
        setSettingsActionDefinitionId(null)
        setFocusedSettingsInputKind(null)
        setIsSelectingExtendModel(false)
        setPendingExtendModelCommandTag(null)
        setCurrentEditingActionRequestUpdater(null)
        editingActionRequestAttrsRef.current = null
        editingAiActionRef.current = null
        setSettingsReadOnly(false)
        setDirectInputEdit(false)
        setDirectSettingsEdit(false)
        setDirectModelPickerEdit(false)
        setModelPickerScope(null)
        setDirectExtendModelEdit(false)
        setDirectReferencesEdit(false)
        setPendingTextInput(null)
        setPendingImageInput(null)
        setPendingAudioInput(null)
        if (shouldCloseMenu) setShowMenu(false)
    }

    const closeInputSettings = () => {
        flushDirectPromptDraftCommit()
        if (referenceInputEdit) {
            closeReferenceInputSettings()
            return
        }
        if (directInputEdit) {
            closeMenu()
            return
        }
        setShowMenu(true)
        setSettingsActionDefinitionId(null)
        setFocusedSettingsInputKind(null)
        setCurrentEditingActionRequestUpdater(null)
        editingActionRequestAttrsRef.current = null
        editingAiActionRef.current = null
        setSettingsReadOnly(false)
        setDirectInputEdit(false)
        setDirectSettingsEdit(false)
        setDirectExtendModelEdit(false)
        setDirectReferencesEdit(false)
        setPromptEditorSeedText(currentTextLikeInput)
        setPendingTextInput(
            currentTextLikeInput
                ? {
                      id: 'current-ai-action-text',
                      label: currentTextLikeLabel,
                      text: currentTextLikeInput,
                  }
                : null,
        )
        setPendingImageInput(imageInput)
        setPendingAudioInput(audioInput)
        setIsEditingTextInput(false)
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
        setIsSelectingImageInput(false)
        setIsSelectingVideoInput(false)
        setIsSelectingAudioInput(false)
    }

    const currentTextLikeInput = focusedSettingsInputKind === 'lyrics' ? lyricsInput : textInput
    const currentTextLikeLabel = focusedSettingsInputKind === 'lyrics' ? 'Lyrics' : 'Current input'
    const currentTextLikePlaceholder =
        focusedSettingsInputKind === 'lyrics'
            ? 'Write lyrics...'
            : promptEditorDefaultText || 'Write prompt...'
    const promptCharacterLimit =
        focusedSettingsInputKind === 'text'
            ? promptCharacterLimitForAiAction(activeInputCommandTag, lyricsInput)
            : null
    const promptCharacterCount = draftTextInput.trim().length
    const promptEditorIsOverLimit =
        promptCharacterLimit !== null && promptCharacterCount > promptCharacterLimit
    const promptEditorIsActive =
        isSelectingTextInput && (isEditingTextInput || isTextLikeSettingsFocused())
    const selectedInputTextValue = promptEditorIsActive
        ? promptEditorHasText
            ? 'prompt'
            : ''
        : (pendingTextInput?.text || '').trim()
    const handlePromptEditorTextChange = (nextText: string) => {
        if (nextText.trim()) setPromptEditorDefaultText('')
        setDraftTextInput(nextText)
        setPromptEditorHasText(!!nextText.trim())
        scheduleDirectPromptDraftCommit(nextText)
    }

    const cancelTextInputSelection = () => {
        if (focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics') {
            closeInputSettings()
            return
        }
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
    }

    const cancelImageInputSelection = () => {
        setPendingImageInput(imageInput)
        if (focusedSettingsInputKind === 'image') {
            closeInputSettings()
            return
        }
        setIsSelectingImageInput(false)
    }

    const cancelVideoInputSelection = () => {
        setPendingVideoInput(videoInput)
        if (focusedSettingsInputKind === 'video') {
            closeInputSettings()
            return
        }
        setIsSelectingVideoInput(false)
    }

    const cancelAudioInputSelection = () => {
        setPendingAudioInput(audioInput)
        if (focusedSettingsInputKind === 'audio') {
            closeInputSettings()
            return
        }
        setIsSelectingAudioInput(false)
    }

    const startEditingTextInput = () => {
        setPromptEditorSeedText(pendingTextInput?.text || currentTextLikeInput || '')
        setIsSelectingTextInput(true)
        setIsEditingTextInput(true)
    }

    const startSelectingTextInput = () => {
        if (focusedSettingsInputKind === 'lyrics') {
            startEditingTextInput()
            return
        }
        setIsEditingTextInput(false)
        setIsSelectingTextInput(true)
    }

    const applyChosenTextInput = (input: AiInputText) => {
        const nextText = input.text.trim()
        setPendingTextInput({ ...input, text: nextText })
        setPromptEditorSeedText(nextText)
        setTextInputEditorRevision(revision => revision + 1)
        setIsEditingTextInput(true)
        setIsBrowsingTextPrompts(false)
    }

    const chooseTextInput = (input: AiInputText) => {
        const nextText = input.text.trim()
        const currentPromptText = readSelectedTextInput().trim()
        if (currentPromptText && currentPromptText !== nextText) {
            useStore
                .getState()
                .showGlobalNotice('Are you sure you want to overwrite the current prompt?', {
                    variant: 'info',
                    durationMs: 10000,
                    kind: 'ai-text-overwrite-confirm',
                    testId: 'toast-ai-text-overwrite-confirm',
                    actionLabel: 'Overwrite',
                    cancelLabel: 'Cancel',
                    replaceKind: true,
                    onAction: () => applyChosenTextInput(input),
                })
            return
        }

        applyChosenTextInput(input)
    }

    const applyTextInput = () => {
        cancelDirectPromptDraftCommit()
        const nextText = readSelectedTextInput().trim()
        if (promptCharacterLimit !== null && nextText.length > promptCharacterLimit) return

        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => textLikeInputAttrsForManualEdit(attrs, nextText),
            { commit: !!focusedSettingsInputKind },
        )
        setPromptEditorSeedText(nextText)
        setPendingTextInput(
            nextText ? { id: 'custom-text', label: 'Custom', text: nextText } : null,
        )
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
        setIsEditingTextInput(false)
        if (focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics') {
            closeModelSettings()
        }
    }

    const saveTextInput = () => {
        cancelDirectPromptDraftCommit()
        const nextText = readPromptEditorText(draftTextInput).trim()
        if (promptCharacterLimit !== null && nextText.length > promptCharacterLimit) return

        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => textLikeInputAttrsForManualEdit(attrs, nextText),
            { commit: !!focusedSettingsInputKind },
        )
        setPendingTextInput(
            nextText ? { id: 'custom-text', label: 'Custom', text: nextText } : null,
        )
        setPromptEditorSeedText(nextText)
        setIsEditingTextInput(false)
        if (focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics') {
            closeModelSettings()
        }
    }

    const clearTextInput = () => {
        cancelDirectPromptDraftCommit()
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => textLikeInputAttrsForManualEdit(attrs, ''),
            { commit: !!focusedSettingsInputKind },
        )
        setPromptEditorSeedText('')
        setPendingTextInput(null)
        setIsEditingTextInput(false)
        setIsSelectingTextInput(false)
        setIsBrowsingTextPrompts(false)
    }

    const chooseImageInput = (image: AiInputImage) => {
        setMediaPreviewTarget({ kind: 'image', input: image })
    }

    const applyImageInputWithInput = (selectedImage: AiInputImage | null) => {
        if (!selectedImage) return
        if (referenceInputEdit?.kind === 'image') {
            addReferenceInput(referenceInputEdit.actionDefinitionId, 'image', selectedImage)
            return
        }
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => ({
                ...addInputOrder(attrs, 'image'),
                hasImageInput: true,
                imageInputUrl: selectedImage.url,
                imageInput: selectedImage,
            }),
            { commit: !!focusedSettingsInputKind },
        )
        setIsSelectingImageInput(false)
        if (focusedSettingsInputKind === 'image') {
            closeModelSettings()
        }
    }

    const applyImageInput = () => applyImageInputWithInput(pendingImageInput)

    const attachImageInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const attachedImage = onImageInputAttached?.(event) || null
        if (event.target.value != null) {
            event.target.value = ''
        }
        if (!attachedImage) return

        setDeviceMediaInputs(prev => ({
            ...prev,
            images: dedupeImageInputs([attachedImage, ...prev.images]),
        }))
        setPendingImageInput(attachedImage)
    }

    const clearImageInput = () => {
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => {
                const clearsRequiredStartFrame = endFrameRequiresStartFrame(attrs.tag)
                return {
                    ...removeInputOrder(attrs, 'image'),
                    hasImageInput: false,
                    imageInputUrl: null,
                    imageInput: null,
                    ...(clearsRequiredStartFrame ? { referenceImageInputs: [] } : {}),
                }
            },
            { commit: !!focusedSettingsInputKind },
        )
        setPendingImageInput(null)
        setIsSelectingImageInput(false)
    }

    const chooseVideoInput = (video: AiInputVideo) => {
        setMediaPreviewTarget({ kind: 'video', input: video })
    }

    const applyVideoInputWithInput = async (selectedVideo: AiInputVideo | null) => {
        if (!selectedVideo) return
        const referenceVideoDurationAffectsPrice =
            referenceInputEdit?.kind === 'video' &&
            referenceLimitModel?.contextualPricingRules?.formula === 'duration_plus_reference_video'
        const selectedVideoWithMetadata = referenceVideoDurationAffectsPrice
            ? await aiInputVideoWithMetadata(selectedVideo)
            : selectedVideo

        if (referenceInputEdit?.kind === 'video') {
            addReferenceInput(
                referenceInputEdit.actionDefinitionId,
                'video',
                selectedVideoWithMetadata,
            )
            return
        }
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        if (activeInputCommandTag === 'stitch' && stitchVideoInputSlot !== null) {
            updateDraftAiActionAttrs(
                activeInputActionDefinitionId,
                attrs => {
                    const nextInputs = [...(attrs.videoInputs || attrs.referenceVideoInputs || [])]
                    nextInputs[stitchVideoInputSlot] = selectedVideoWithMetadata
                    return {
                        ...addInputOrder(attrs, `video-${stitchVideoInputSlot}`),
                        videoInputs: nextInputs.filter(Boolean).slice(0, 2),
                        referenceVideoInputs: null,
                    }
                },
                { commit: !!focusedSettingsInputKind },
            )
            setStitchVideoInputSlot(null)
            setIsSelectingVideoInput(false)
            if (focusedSettingsInputKind === 'video') {
                closeModelSettings()
            }
            return
        }
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => ({
                ...addInputOrder(attrs, 'video'),
                hasVideoInput: true,
                videoInputUrl: selectedVideoWithMetadata.url,
                videoPreviewImageUrl: selectedVideoWithMetadata.previewImageUrl || null,
                videoInputActionTag: selectedVideoWithMetadata.actionTag || null,
                videoInput: selectedVideoWithMetadata,
            }),
            { commit: !!focusedSettingsInputKind },
        )
        setIsSelectingVideoInput(false)
        if (focusedSettingsInputKind === 'video') {
            closeModelSettings()
        }
    }

    const applyVideoInput = () => applyVideoInputWithInput(pendingVideoInput)

    const attachVideoInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const attachedVideo = onVideoInputAttached?.(event) || null
        if (event.target.value != null) {
            event.target.value = ''
        }
        if (!attachedVideo) return

        setDeviceMediaInputs(prev => ({
            ...prev,
            videos: dedupeVideoInputs([attachedVideo, ...prev.videos]),
        }))
        setPendingVideoInput(attachedVideo)
    }

    const clearVideoInput = () => {
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => {
                if (activeInputCommandTag === 'stitch' && stitchVideoInputSlot !== null) {
                    const nextInputs = [...(attrs.videoInputs || attrs.referenceVideoInputs || [])]
                    const nextInputOrder = (attrs.inputOrder || []).filter(
                        key => key !== `video-${stitchVideoInputSlot}`,
                    )
                    nextInputs.splice(stitchVideoInputSlot, 1)
                    return {
                        ...attrs,
                        inputOrder: nextInputOrder,
                        videoInputs: nextInputs,
                        referenceVideoInputs: null,
                    }
                }

                return {
                    ...removeInputOrder(attrs, 'video'),
                    hasVideoInput: false,
                    videoInputUrl: null,
                    videoPreviewImageUrl: null,
                    videoInputActionTag: null,
                    videoInput: null,
                }
            },
            { commit: !!focusedSettingsInputKind },
        )
        setPendingVideoInput(null)
        setIsSelectingVideoInput(false)
    }

    const chooseAudioInput = (audio: AiInputAudio) => {
        setMediaPreviewTarget({ kind: 'audio', input: audio })
    }

    const applyAudioInputWithInput = (selectedAudio: AiInputAudio | null) => {
        if (!selectedAudio) return

        if (referenceInputEdit?.kind === 'audio') {
            addReferenceInput(referenceInputEdit.actionDefinitionId, 'audio', selectedAudio)
            return
        }
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => ({
                ...addInputOrder(attrs, 'audio'),
                hasAudioInput: true,
                audioInputUrl: selectedAudio.url,
                audioInputSource: selectedAudio.source || null,
                audioInputUploadId: selectedAudio.uploadId || null,
                audioInputSignedId: selectedAudio.signedId || null,
                audioInputLabel: selectedAudio.label || null,
                audioInputContentType: selectedAudio.contentType || null,
                audioInputFile: selectedAudio.file || null,
                audioInput: selectedAudio,
            }),
            { commit: !!focusedSettingsInputKind },
        )
        setIsSelectingAudioInput(false)
        if (focusedSettingsInputKind === 'audio') {
            closeModelSettings()
        }
    }

    const applyAudioInput = () => applyAudioInputWithInput(pendingAudioInput)

    const attachAudioInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const attachedAudio = onAudioInputAttached?.(event) || null
        if (event.target.value != null) {
            event.target.value = ''
        }
        if (!attachedAudio) return

        setDeviceMediaInputs(prev => ({
            ...prev,
            audios: dedupeAudioInputs([attachedAudio, ...prev.audios]),
        }))
        setPendingAudioInput(attachedAudio)
    }

    const clearAudioInput = () => {
        setPendingAudioInput(null)
        selectSingleActionDefinitionId(activeInputActionDefinitionId)
        updateDraftAiActionAttrs(
            activeInputActionDefinitionId,
            attrs => ({
                ...removeInputOrder(attrs, 'audio'),
                hasAudioInput: false,
                audioInputUrl: null,
                audioInputSource: null,
                audioInputUploadId: null,
                audioInputSignedId: null,
                audioInputLabel: null,
                audioInputContentType: null,
                audioInputFile: null,
                audioInput: null,
            }),
            { commit: !!focusedSettingsInputKind },
        )
        setIsSelectingAudioInput(false)
    }

    const updateDraftModelSetting = <TKey extends keyof ModelSettings>(
        key: TKey,
        value: ModelSettings[TKey],
    ) => {
        setDraftModelSettings(prev => ({
            ...prev,
            [key]: value,
        }))
    }

    const buildActionRequestSelection = (
        model: ActionDefinition,
        settings: ModelSettings | undefined,
    ): ActionRequestSelection => {
        const existingAttrs = resolvedDraftAttrsForActionDefinitionId(model.id)
        const inputs = getModelInputs(model.id)
        const rawSettings = {
            ...((settings ||
                (existingAttrs
                    ? settingsFromActionRequestAttrs(model, existingAttrs as ActionRequestFormState)
                    : undefined) ||
                defaultSettingsForModel(model)) as ModelSettings),
        }
        if (model.actionTagName === 'extend') {
            rawSettings.extendActionTag =
                latestExtendModelCommandTag(rawSettings.extendActionTag, aiModels) || undefined
        }
        const effectiveSettings = normalizeModelSettingsForModel(model, rawSettings)
        const selectedExtendModel =
            model.actionTagName === 'extend'
                ? selectedExtendModelForSettings(effectiveSettings) ||
                  extendModelForModelId(
                      (model as Partial<ActionDefinition>).defaultAiModelId,
                      aiModels,
                  ) ||
                  extendModelForCommandTag(FALLBACK_EXTEND_COMMAND_TAG)
                : null
        if (model.actionTagName === 'gif') {
            const metadata =
                inputs.video?.url === gifSourceVideo?.url
                    ? gifVideoMetadata
                    : {
                          durationSeconds: inputs.video?.durationSeconds || null,
                          width: inputs.video?.width || null,
                          height: inputs.video?.height || null,
                      }
            const currentFrameRate = effectiveSettings.gifFrameRate || DEFAULT_GIF_FRAME_RATE
            const currentMaxWidth = effectiveSettings.gifMaxWidth || DEFAULT_GIF_MAX_WIDTH
            if (!gifOptionAllowed(metadata, currentFrameRate, currentMaxWidth)) {
                const downgradedSettings = downgradedGifSettings(
                    metadata,
                    currentFrameRate,
                    currentMaxWidth,
                )
                effectiveSettings.gifFrameRate = downgradedSettings.frameRate
                effectiveSettings.gifMaxWidth = downgradedSettings.maxWidth
            }
        }
        const supportedInputs = new Set(supportedInputKindsForModel(model))
        const hasImageInput = supportedInputs.has('image') && !!inputs.image
        const requestAspectRatio = sourceAwareVideoAspectRatio(
            model.modelType,
            hasImageInput,
            effectiveSettings?.aspectRatio,
        )
        const commandText = configuredActionCommand(model, effectiveSettings, hasImageInput)
        const inputText = inputs.text?.trim() || ''
        const actionKind = model.kind || 'model'
        const baseAiModelId = actionKind === 'model' ? aiModelIdForActionDefinition(model) : null
        const formState: ActionRequestFormState = {
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            ai_action_id: existingAttrs.ai_action_id || existingAttrs.actionId || null,
            actionId: existingAttrs.actionId || null,
            promptContent: supportedInputs.has('text') ? inputText || null : null,
            promptPlacement:
                existingAttrs.promptPlacement || existingAttrs.prompt_placement || null,
            actionDefinitionId: model.id,
            kind: actionKind,
            aiModelId: baseAiModelId,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            aiRunId: existingAttrs.aiRunId || null,
            aiRunIds: existingAttrs.aiRunIds || null,
            type: actionKind,
            modelType: model.modelType,
            durationSeconds: effectiveSettings?.durationSeconds || null,
            aspectRatio: requestAspectRatio,
            generationCount: effectiveSettings?.generationCount || 1,
            gifFrameRate: effectiveSettings?.gifFrameRate || null,
            gifMaxWidth: effectiveSettings?.gifMaxWidth || null,
            screenshotTimestamp: effectiveSettings?.screenshotTimestamp || null,
            extendAiModelId: selectedExtendModel?.id || effectiveSettings?.extendAiModelId || null,
            extendActionTag:
                model.actionTagName === 'extend'
                    ? selectedExtendModel?.actionTagName ||
                      effectiveSettings?.extendActionTag ||
                      null
                    : null,
            extendTimestamp: null,
            smartStitch: effectiveSettings?.smartStitch === true,
            hasTextInput: supportedInputs.has('text') && !!inputText,
            textInputValue: supportedInputs.has('text') ? inputText || null : null,
            hasImageInput,
            imageInputUrl: supportedInputs.has('image') ? inputs.image?.url || null : null,
            imageInput: supportedInputs.has('image') ? inputs.image || null : null,
            referenceImageInputs: existingAttrs.referenceImageInputs || null,
            videoKeyframeMode:
                existingAttrs.videoKeyframeMode === true ||
                (supportsExplicitVideoKeyframes(model.actionTagName) && hasImageInput),
            hasVideoInput: supportedInputs.has('video') && !!inputs.video,
            videoInputUrl: supportedInputs.has('video') ? inputs.video?.url || null : null,
            videoPreviewImageUrl: supportedInputs.has('video')
                ? inputs.video?.previewImageUrl || null
                : null,
            videoInputActionTag: supportedInputs.has('video')
                ? inputs.video?.actionTag || null
                : null,
            videoInput: supportedInputs.has('video') ? inputs.video || null : null,
            videoInputs:
                model.actionTagName === 'stitch'
                    ? videoInputsForActionDefinitionId(model.id)
                    : existingAttrs.videoInputs || null,
            referenceVideoInputs: existingAttrs.referenceVideoInputs || null,
            hasAudioInput: supportedInputs.has('audio') && !!inputs.audio,
            audioInputUrl: supportedInputs.has('audio') ? inputs.audio?.url || null : null,
            audioInputSource: supportedInputs.has('audio') ? inputs.audio?.source || null : null,
            audioInputUploadId: supportedInputs.has('audio')
                ? inputs.audio?.uploadId || null
                : null,
            audioInputSignedId: supportedInputs.has('audio')
                ? inputs.audio?.signedId || null
                : null,
            audioInputLabel: supportedInputs.has('audio') ? inputs.audio?.label || null : null,
            audioInputContentType: supportedInputs.has('audio')
                ? inputs.audio?.contentType || null
                : null,
            audioInputFile: supportedInputs.has('audio') ? inputs.audio?.file || null : null,
            audioInput: supportedInputs.has('audio') ? inputs.audio || null : null,
            referenceAudioInputs: existingAttrs.referenceAudioInputs || null,
            hasLyrics: isMusicActionTag(model.actionTagName) && !!inputs.lyrics?.trim(),
            lyrics: isMusicActionTag(model.actionTagName) ? inputs.lyrics?.trim() || null : null,
            inputOrder: existingAttrs.inputOrder || null,
            syncWithDraft: existingAttrs.syncWithDraft === true,
            commandText,
            analyticsSource: 'magic_wand_modal',
        }
        formState.aiModelId = effectiveAiModelIdForActionAttrs(model, formState)
        const aiModelId = formState.aiModelId
        const aiAction = aiActionFromFormState(formState, editingAiActionRef.current)
        const promptContent =
            (aiAction?.inputs?.text?.value || aiAction?.prompt || inputText || '').trim() || null
        const attrs: ActionRequestFormState = {
            ...formState,
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            ai_action_id:
                aiAction?.id ||
                aiAction?.client_id ||
                formState.ai_action_id ||
                formState.actionId ||
                null,
            promptContent,
            promptPlacement: formState.promptPlacement || null,
            supportsPromptInput: supportedInputs.has('text'),
            actionDefinitionId: model.id,
            kind: actionKind,
            aiModelId,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            extendActionTag:
                model.actionTagName === 'extend'
                    ? formState.extendActionTag ||
                      selectedExtendModel?.actionTagName ||
                      effectiveSettings?.extendActionTag ||
                      null
                    : null,
            analyticsSource: 'magic_wand_modal',
        }

        return { tag: model.actionTagName, commandText, aiAction: aiAction || undefined, attrs }
    }

    const buildDirectModelSwitchAction = (model: ActionDefinition): AiActionPayload | null => {
        const sourceAttrs =
            editingActionRequestAttrsRef.current ||
            aiActionFormStateFromAiAction(editingAiActionRef.current)
        if (!sourceAttrs) return null

        const sourceInputs = inputStateFromAttrs(sourceAttrs)
        const supportedInputs = new Set(supportedInputKindsForModel(model))
        const defaults = defaultSettingsForModel(model)
        const rawNextSettings: ModelSettings = {
            ...defaults,
            generationCount: sourceAttrs.generationCount ?? defaults.generationCount,
            durationSeconds:
                sourceAttrs.durationSeconds ??
                sourceAttrs.defaultDurationSeconds ??
                defaults.durationSeconds,
            aspectRatio:
                sourceAttrs.aspectRatio ?? sourceAttrs.defaultAspectRatio ?? defaults.aspectRatio,
            gifFrameRate: sourceAttrs.gifFrameRate ?? defaults.gifFrameRate,
            gifMaxWidth: sourceAttrs.gifMaxWidth ?? defaults.gifMaxWidth,
            screenshotTimestamp: sourceAttrs.screenshotTimestamp ?? defaults.screenshotTimestamp,
            lyrics: isMusicActionTag(model.actionTagName)
                ? sourceAttrs.lyrics ?? defaults.lyrics
                : defaults.lyrics,
            extendAiModelId:
                model.actionTagName === 'extend'
                    ? sourceAttrs.extendAiModelId ||
                      selectedExtendModelForSettings(sourceAttrs)?.id ||
                      null
                    : null,
            extendActionTag:
                model.actionTagName === 'extend' && !sourceAttrs.extendAiModelId
                    ? latestExtendModelCommandTag(sourceAttrs.extendActionTag, aiModels) ||
                      defaults.extendActionTag
                    : undefined,
            smartStitch:
                model.actionTagName === 'extend'
                    ? sourceAttrs.smartStitch ?? defaults.smartStitch
                    : defaults.smartStitch,
        }
        const nextSettings = normalizeModelSettingsForModel(model, rawNextSettings)
        const nextReferences = supportsReferenceInputs(model)
            ? {
                  image: (sourceAttrs.referenceImageInputs || []).slice(
                      0,
                      referenceInputLimitForModel(model, 'image'),
                  ),
                  video: (sourceAttrs.referenceVideoInputs || []).slice(
                      0,
                      referenceInputLimitForModel(model, 'video'),
                  ),
                  audio: (sourceAttrs.referenceAudioInputs || []).slice(
                      0,
                      referenceInputLimitForModel(model, 'audio'),
                  ),
              }
            : { image: [], video: [], audio: [] }
        const sourceInputOrder = Array.isArray(sourceAttrs.inputOrder) ? sourceAttrs.inputOrder : []
        const fallbackInputOrder = [
            'text',
            'lyrics',
            'image',
            'video',
            'video-0',
            'video-1',
            'audio',
            'references-image',
            'references-video',
            'references-audio',
        ]
        const allowedInputOrder = new Set<string>()
        if (supportedInputs.has('text') && sourceInputs.text) allowedInputOrder.add('text')
        if (isMusicActionTag(model.actionTagName) && sourceInputs.lyrics) {
            allowedInputOrder.add('lyrics')
        }
        if (supportedInputs.has('image') && sourceInputs.image?.url) allowedInputOrder.add('image')
        if (supportedInputs.has('video') && sourceInputs.video?.url) allowedInputOrder.add('video')
        if (model.actionTagName === 'stitch' && sourceInputs.video?.url) {
            allowedInputOrder.add('video-0')
        }
        if (supportedInputs.has('audio') && sourceInputs.audio?.url) allowedInputOrder.add('audio')
        if (nextReferences.image.length > 0) allowedInputOrder.add('references-image')
        if (nextReferences.video.length > 0) allowedInputOrder.add('references-video')
        if (nextReferences.audio.length > 0) allowedInputOrder.add('references-audio')
        const nextInputOrder = [...sourceInputOrder, ...fallbackInputOrder].filter(
            (key, index, allKeys) => allowedInputOrder.has(key) && allKeys.indexOf(key) === index,
        )
        const actionKind = model.kind || 'model'
        const baseAiModelId = actionKind === 'model' ? aiModelIdForActionDefinition(model) : null
        const nextAttrs: ActionRequestFormState = {
            tag: model.actionTagName,
            displayName: actionTokenDisplayName(model),
            ai_action_id: sourceAttrs.ai_action_id || sourceAttrs.actionId || null,
            actionId: sourceAttrs.actionId || sourceAttrs.ai_action_id || null,
            promptContent: supportedInputs.has('text') ? sourceInputs.text || null : null,
            promptPlacement: sourceAttrs.promptPlacement || sourceAttrs.prompt_placement || null,
            actionDefinitionId: model.id,
            kind: actionKind,
            aiModelId: baseAiModelId,
            toolKey: actionKind === 'tool' ? model.toolKey || null : null,
            type: actionKind,
            modelType: model.modelType,
            durationSeconds: nextSettings.durationSeconds ?? null,
            aspectRatio: sourceAwareVideoAspectRatio(
                model.modelType,
                supportedInputs.has('image') && !!sourceInputs.image?.url,
                nextSettings.aspectRatio,
            ),
            generationCount: nextSettings.generationCount ?? 1,
            gifFrameRate: nextSettings.gifFrameRate ?? null,
            gifMaxWidth: nextSettings.gifMaxWidth ?? null,
            screenshotTimestamp: nextSettings.screenshotTimestamp ?? null,
            extendAiModelId:
                model.actionTagName === 'extend'
                    ? selectedExtendModelForSettings(nextSettings)?.id ||
                      nextSettings.extendAiModelId ||
                      null
                    : null,
            extendActionTag:
                model.actionTagName === 'extend' && !nextSettings.extendAiModelId
                    ? nextSettings.extendActionTag || null
                    : null,
            extendTimestamp: null,
            smartStitch: model.actionTagName === 'extend' && nextSettings.smartStitch === true,
            hasTextInput: supportedInputs.has('text') && !!sourceInputs.text,
            textInputValue: supportedInputs.has('text') ? sourceInputs.text || null : null,
            hasImageInput: supportedInputs.has('image') && !!sourceInputs.image?.url,
            imageInputUrl: supportedInputs.has('image') ? sourceInputs.image?.url || null : null,
            imageInput: supportedInputs.has('image') ? sourceInputs.image : null,
            referenceImageInputs: nextReferences.image,
            hasVideoInput: supportedInputs.has('video') && !!sourceInputs.video?.url,
            videoInputUrl: supportedInputs.has('video') ? sourceInputs.video?.url || null : null,
            videoPreviewImageUrl: supportedInputs.has('video')
                ? sourceInputs.video?.previewImageUrl || null
                : null,
            videoInputActionTag: supportedInputs.has('video')
                ? sourceInputs.video?.actionTag || null
                : null,
            videoInput: supportedInputs.has('video') ? sourceInputs.video : null,
            videoInputs:
                model.actionTagName === 'stitch' && sourceInputs.video?.url
                    ? [sourceInputs.video]
                    : null,
            referenceVideoInputs: nextReferences.video,
            hasAudioInput: supportedInputs.has('audio') && !!sourceInputs.audio?.url,
            audioInputUrl: supportedInputs.has('audio') ? sourceInputs.audio?.url || null : null,
            audioInputSource: supportedInputs.has('audio')
                ? sourceInputs.audio?.source || null
                : null,
            audioInputUploadId: supportedInputs.has('audio')
                ? sourceInputs.audio?.uploadId || null
                : null,
            audioInputSignedId: supportedInputs.has('audio')
                ? sourceInputs.audio?.signedId || null
                : null,
            audioInputLabel: supportedInputs.has('audio')
                ? sourceInputs.audio?.label || null
                : null,
            audioInputContentType: supportedInputs.has('audio')
                ? sourceInputs.audio?.contentType || null
                : null,
            audioInputFile: supportedInputs.has('audio') ? sourceInputs.audio?.file || null : null,
            audioInput: supportedInputs.has('audio') ? sourceInputs.audio : null,
            referenceAudioInputs: nextReferences.audio,
            hasLyrics: isMusicActionTag(model.actionTagName) && !!sourceInputs.lyrics,
            lyrics: isMusicActionTag(model.actionTagName) ? sourceInputs.lyrics || null : null,
            inputOrder: nextInputOrder,
            syncWithDraft: false,
            commandText: configuredActionCommand(
                model,
                nextSettings,
                supportedInputs.has('image') && !!sourceInputs.image?.url,
            ),
            analyticsSource: 'magic_wand_modal',
        }
        nextAttrs.aiModelId = effectiveAiModelIdForActionAttrs(model, nextAttrs)
        const nextAction = aiActionFromFormState(nextAttrs, editingAiActionRef.current)
        if (!nextAction) return null

        const actionWithoutCachedLabels: AiActionPayload = { ...nextAction }
        delete actionWithoutCachedLabels.display_name
        delete actionWithoutCachedLabels.displayName
        delete actionWithoutCachedLabels.price_label
        delete actionWithoutCachedLabels.priceLabel

        return actionWithoutCachedLabels
    }

    const saveModelSettings = () => {
        if (!settingsModel) return
        const normalizedDraftSettings = normalizeModelSettingsForModel(
            settingsModel,
            draftModelSettings,
        )
        const actionRequestSelection = buildActionRequestSelection(
            settingsModel,
            normalizedDraftSettings,
        )

        setModelSettingsByActionDefinitionId(prev => ({
            ...prev,
            [settingsModel.id]: normalizedDraftSettings,
        }))
        setDraftAiActionAttrsByActionDefinitionId(prev => ({
            ...prev,
            [settingsModel.id]:
                aiActionFormStateFromAiAction(actionRequestSelection.aiAction) ||
                actionRequestSelection.attrs,
        }))
        if (actionRequestSelection.aiAction) {
            editingAiActionRef.current = actionRequestSelection.aiAction
            editingActionRequestUpdaterRef.current?.(actionRequestSelection.aiAction)
        }
        closeModelSettings()
    }

    const buildMenuActionRequestSelection = (model: ActionDefinition) => {
        const settings = modelSettingsByActionDefinitionId[model.id]
        const normalizedSettings = settings
            ? normalizeModelSettingsForModel(model, settings)
            : settings
        const actionRequestSelection = buildActionRequestSelection(model, normalizedSettings)
        return {
            normalizedSettings,
            actionRequestSelection: {
                ...actionRequestSelection,
                buildOptions: {
                    syncDraftInputs: false,
                    useComposerPromptFallback: false,
                    focusPromptOnInsert: actionRequestSelection.attrs.supportsPromptInput !== false,
                },
            },
        }
    }

    const chooseModel = (model: ActionDefinition) => {
        if (directModelPickerEdit && editingActionRequestUpdaterRef.current) {
            const nextAction = buildDirectModelSwitchAction(model)
            if (nextAction) {
                editingAiActionRef.current = nextAction
                editingActionRequestUpdaterRef.current(nextAction)
            }
            closeMenu()
            return
        }

        const { actionRequestSelection, normalizedSettings } =
            buildMenuActionRequestSelection(model)
        if (onActionRequestSelected) {
            onActionRequestSelected(actionRequestSelection)
        } else {
            insertActionRequestIntoEditor(getEditor, actionRequestSelection)
        }

        window.logEv('SELECT_AI_MODELS', {
            model_ids: [model.id],
            model_names: [model.name],
            action_tags: [actionRequestSelection.commandText],
            model_settings: [normalizedSettings || null],
            model_count: 1,
            from_location: 'magic_wand_button',
        })

        closeMenu()
    }

    const toggleModelSelection = (model: ActionDefinition) => {
        setSelectedActionDefinitionId(model.id)
        setSelectedActionDefinitionIds(prev => {
            if (prev.includes(model.id)) return prev.filter(id => id !== model.id)
            if (prev.length >= MAX_ACTION_REQUEST_SELECTIONS) return prev
            return [...prev, model.id]
        })
    }

    const handleModelRowClick = (model: ActionDefinition) => {
        if (directModelPickerEdit && editingActionRequestUpdaterRef.current) {
            chooseModel(model)
            return
        }

        toggleModelSelection(model)
    }

    const addSelectedModels = () => {
        const selections = selectedActionDefinitions
            .slice(0, MAX_ACTION_REQUEST_SELECTIONS)
            .map(model => ({
                model,
                ...buildMenuActionRequestSelection(model),
            }))

        if (selections.length === 0) return

        const actionRequestSelections = selections.map(
            ({ actionRequestSelection }) => actionRequestSelection,
        )
        if (onActionRequestSelected) {
            onActionRequestSelected(actionRequestSelections)
        } else {
            insertActionRequestIntoEditor(getEditor, actionRequestSelections)
        }

        window.logEv('SELECT_AI_MODELS', {
            model_ids: selections.map(({ model }) => model.id),
            model_names: selections.map(({ model }) => model.name),
            action_tags: selections.map(
                ({ actionRequestSelection }) => actionRequestSelection.commandText,
            ),
            model_settings: selections.map(({ normalizedSettings }) => normalizedSettings || null),
            model_count: selections.length,
            from_location: 'magic_wand_button',
        })

        closeMenu()
    }

    const renderChooseInputs = (inputKinds: AiInputKind[]) => {
        const currentText = getCurrentTextInput?.()?.trim() || ''
        const threadTexts = getThreadTextInputs?.() || []
        const treeTexts = excludeTextInputs(threadTreeMediaInputs.texts || [], threadTexts)
        const threadImages = getThreadImageInputs?.() || []
        const threadVideos = getThreadVideoInputs?.() || []
        const threadAudios = getThreadAudioInputs?.() || []
        const visibleInputKinds = inputKinds
        const textKind: Extract<AiInputKind, 'text' | 'lyrics'> | null = visibleInputKinds.includes(
            'lyrics',
        )
            ? 'lyrics'
            : visibleInputKinds.includes('text')
            ? 'text'
            : null
        const showTextInput = !!textKind
        const showImageInput = visibleInputKinds.includes('image')
        const keyframeImageInput = supportsExplicitVideoKeyframes(activeInputCommandTag)
        const showVideoInput = visibleInputKinds.includes('video')
        const showAudioInput = visibleInputKinds.includes('audio')
        const textPanelTitle = textKind === 'lyrics' ? 'Lyrics' : 'Text'
        const textPanelEmptyLabel = textKind === 'lyrics' ? 'No lyrics' : 'No text'
        const isFocusedTextLikeInput =
            focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics'
        const deviceImageChoices = dedupeImageInputs(deviceMediaInputs.images || [])
        const deviceVideoChoices = dedupeVideoInputs(deviceMediaInputs.videos || [])
        const deviceAudioChoices = dedupeAudioInputs(deviceMediaInputs.audios || [])
        const imageChoices = dedupeImageInputs(threadImages)
        const videoChoices = dedupeVideoInputs(threadVideos)
        const audioChoices = dedupeAudioInputs(threadAudios)
        const treeImageChoices = dedupeImageInputs(
            excludeImageInputs(threadTreeMediaInputs.images || [], imageChoices),
        )
        const treeVideoChoices = dedupeVideoInputs(
            excludeVideoInputs(threadTreeMediaInputs.videos || [], videoChoices),
        )
        const treeAudioChoices = dedupeAudioInputs(
            excludeAudioInputs(threadTreeMediaInputs.audios || [], audioChoices),
        )
        const mediaSectionsFor = <TMedia,>(
            deviceItems: TMedia[],
            threadItems: TMedia[],
            treeItems: TMedia[],
            treeLoadingLabel: string,
        ) => {
            return [
                { id: 'device', label: 'From Device', items: deviceItems, emptyLabel: null },
                { id: 'thread', label: 'From Thread', items: threadItems, emptyLabel: null },
                {
                    id: 'thread-tree',
                    label: 'From Thread Tree',
                    items: treeItems,
                    emptyLabel: threadTreeMediaLoading ? treeLoadingLabel : null,
                },
            ].filter(section => section.items.length > 0 || section.emptyLabel)
        }
        const setMediaLayout = (kind: AiReferenceInputKind, layout: AiMediaInputLayout) => {
            setMediaInputLayouts(prev => ({ ...prev, [kind]: layout }))
        }
        const renderLayoutToggle = (kind: AiReferenceInputKind) => (
            <div className={styles.aiInputLayoutToggle} aria-label={`${kind} picker layout`}>
                <button
                    className={cn(
                        styles.aiInputLayoutButton,
                        mediaInputLayouts[kind] === 'list' && styles.aiInputLayoutButtonActive,
                    )}
                    type="button"
                    aria-label="Show as list"
                    onClick={() => setMediaLayout(kind, 'list')}
                >
                    <span className={styles.aiInputLayoutListIcon} />
                </button>
                <button
                    className={cn(
                        styles.aiInputLayoutButton,
                        mediaInputLayouts[kind] === 'grid' && styles.aiInputLayoutButtonActive,
                    )}
                    type="button"
                    aria-label="Show as grid"
                    onClick={() => setMediaLayout(kind, 'grid')}
                >
                    <span className={styles.aiInputLayoutGridIcon}>
                        <i />
                        <i />
                        <i />
                        <i />
                    </span>
                </button>
            </div>
        )
        const isTextInputSelected = (input: AiInputText) => {
            const selectedText = (
                pendingTextInput?.text ||
                draftTextInput ||
                currentTextLikeInput ||
                ''
            ).trim()
            const inputText = input.text?.trim()
            return !!(
                (pendingTextInput?.id && pendingTextInput.id === input.id) ||
                (selectedText && inputText && selectedText === inputText)
            )
        }
        const selectedReferencesFor = (kind: AiReferenceInputKind) => {
            const actionDefinitionId =
                referenceInputEdit?.actionDefinitionId || referencesActionDefinitionId
            if (!actionDefinitionId) return []
            return referenceInputsForActionDefinitionId(actionDefinitionId)[kind] || []
        }
        const isImageInputSelected = (image: AiInputImage) =>
            !!(
                (pendingImageInput && areSameImageInput(pendingImageInput, image)) ||
                (!referenceInputEdit && imageInput && areSameImageInput(imageInput, image)) ||
                (referenceInputEdit?.kind === 'image' &&
                    selectedReferencesFor('image').some(input => areSameImageInput(input, image)))
            )
        const isVideoInputSelected = (video: AiInputVideo) =>
            !!(
                (pendingVideoInput && areSameVideoInput(pendingVideoInput, video)) ||
                (!referenceInputEdit && videoInput && areSameVideoInput(videoInput, video)) ||
                (referenceInputEdit?.kind === 'video' &&
                    selectedReferencesFor('video').some(input => areSameVideoInput(input, video)))
            )
        const isAudioInputSelected = (audio: AiInputAudio) =>
            !!(
                (pendingAudioInput && areSameAudioInput(pendingAudioInput, audio)) ||
                (!referenceInputEdit && audioInput && areSameAudioInput(audioInput, audio)) ||
                (referenceInputEdit?.kind === 'audio' &&
                    selectedReferencesFor('audio').some(input => areSameAudioInput(input, audio)))
            )
        const renderPromptTextEditor = (syncPendingTextInput: boolean) => (
            <div className={styles.aiInputPromptEditor} ref={promptEditorContainerRef}>
                <PromptEditor
                    key={`${textKind || 'text'}-${
                        syncPendingTextInput ? 'select' : 'edit'
                    }-${textInputEditorRevision}`}
                    value={draftTextInput}
                    placeholder={currentTextLikePlaceholder}
                    references={textKind === 'text' ? promptReferences : []}
                    onValidityChange={setPromptEditorHasText}
                    onTextChange={handlePromptEditorTextChange}
                    onModEnter={() => {
                        if (syncPendingTextInput) {
                            const nextText = readPromptEditorText(draftTextInput).trim()
                            if (!nextText) return
                            applyTextInput()
                            return
                        }

                        saveTextInput()
                    }}
                    onEditorLoad={editor => {
                        promptEditorRef.current = editor
                        if (editor) focusPromptEditor()
                    }}
                    type="answer"
                    editorTestId="ai-prompt-input-editor"
                />
                {promptCharacterLimit !== null && (
                    <div
                        className={cn(
                            styles.aiInputPromptCharacterCount,
                            promptEditorIsOverLimit && styles.aiInputPromptCharacterCountInvalid,
                        )}
                        aria-live="polite"
                    >
                        {promptCharacterCount}/{promptCharacterLimit} characters
                    </div>
                )}
            </div>
        )
        const renderPromptBrowserOption = (input: AiInputText) => (
            <button
                className={cn(
                    styles.aiInputTextSourceOption,
                    isTextInputSelected(input) && styles.aiInputTextSourceOptionSelected,
                )}
                type="button"
                key={input.id}
                onClick={() => chooseTextInput(input)}
            >
                {input.label && (
                    <span className={styles.aiInputTextSourceLabel}>{input.label}</span>
                )}
                <span className={styles.aiInputTextSourcePreview}>{input.text}</span>
            </button>
        )
        const renderPromptBrowserDialog = () => {
            if (!isBrowsingTextPrompts || textKind !== 'text') return null

            // Crafted Illustrate/Film prompts stay browsable here even after the user edits
            // the pill or switches media type; entries matching a live action prompt are
            // hidden because those already appear under the action's own thread entry.
            const craftedTexts = excludeTextInputs(getCraftedPromptInputs?.() || [], threadTexts)

            const promptSections = [
                {
                    id: 'draft',
                    label: 'Message Draft',
                    inputs: currentText
                        ? [
                              {
                                  id: 'current-draft',
                                  label: 'Message Draft',
                                  text: currentText,
                              },
                          ]
                        : [],
                },
                { id: 'crafted', label: 'Crafted Prompts', inputs: craftedTexts },
                { id: 'thread', label: 'From Current Thread', inputs: threadTexts },
                { id: 'thread-tree', label: 'From Thread Tree', inputs: treeTexts },
            ].filter(section => section.inputs.length > 0)

            return renderAiActionModalPortal(
                <div
                    className={styles.aiPromptBrowserOverlay}
                    onMouseDown={event => {
                        if (event.target === event.currentTarget) {
                            setIsBrowsingTextPrompts(false)
                        }
                    }}
                >
                    <div
                        className={styles.aiPromptBrowserDialog}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="ai-prompt-browser-title"
                        onMouseDown={event => event.stopPropagation()}
                    >
                        <div className={styles.aiPromptBrowserHeader}>
                            <div>
                                <span className={styles.aiPromptBrowserEyebrow}>
                                    Previous Prompts
                                </span>
                                <h3 id="ai-prompt-browser-title">Browse Prompts</h3>
                            </div>
                            <button
                                className={styles.aiModelModalClose}
                                type="button"
                                aria-label="Close prompt browser"
                                onClick={() => setIsBrowsingTextPrompts(false)}
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <div className={styles.aiPromptBrowserBody}>
                            {promptSections.length > 0 ? (
                                promptSections.map(section => (
                                    <div className={styles.aiInputMediaSection} key={section.id}>
                                        <div className={styles.aiInputMediaSectionHeading}>
                                            {section.label}
                                        </div>
                                        {section.inputs.map(renderPromptBrowserOption)}
                                    </div>
                                ))
                            ) : (
                                <div className={styles.aiInputEmpty}>
                                    {threadTreeMediaLoading
                                        ? 'Loading thread tree prompts...'
                                        : 'No previous prompts available'}
                                </div>
                            )}
                        </div>
                    </div>
                </div>,
                'ai-action-prompt-browser-dialog',
            )
        }

        if (visibleInputKinds.length === 0) return null

        return (
            <div
                className={cn(
                    styles.aiModelSettingsGroup,
                    styles.aiInputSettingsGroup,
                    focusedSettingsInputKind && styles.aiInputFocusedSettingsGroup,
                )}
            >
                {!focusedSettingsInputKind && (
                    <div className={styles.aiModelSettingsLabel}>
                        <span>Inputs</span>
                    </div>
                )}

                <div className={cn(styles.aiInputList, settingsReadOnly && styles.aiInputReadOnly)}>
                    {showTextInput && (
                        <div
                            className={cn(
                                styles.aiInputPanel,
                                isFocusedTextLikeInput && styles.aiInputTextModalPanel,
                            )}
                        >
                            {!isFocusedTextLikeInput && (
                                <div className={styles.aiInputPanelHeader}>
                                    <span className={styles.aiInputPanelTitle}>
                                        <Terminal size={15} />
                                        <span>{textPanelTitle}</span>
                                        {!currentTextLikeInput &&
                                            !isEditingTextInput &&
                                            !isSelectingTextInput && (
                                                <span className={styles.aiInputInlineEmpty}>
                                                    {textPanelEmptyLabel}
                                                </span>
                                            )}
                                    </span>
                                    {!currentTextLikeInput &&
                                        !isEditingTextInput &&
                                        !isSelectingTextInput && (
                                            <button
                                                className={styles.aiInputAddButton}
                                                type="button"
                                                aria-label={`Add ${
                                                    textKind === 'lyrics' ? 'lyrics' : 'text'
                                                } input`}
                                                onClick={startSelectingTextInput}
                                            >
                                                <Plus size={14} />
                                            </button>
                                        )}
                                </div>
                            )}

                            {isFocusedTextLikeInput ? (
                                <div className={styles.aiInputTextModalEditor}>
                                    {renderPromptTextEditor(true)}
                                </div>
                            ) : isSelectingTextInput ? (
                                <div className={styles.aiInputTextSourceList}>
                                    {isEditingTextInput ? (
                                        renderPromptTextEditor(true)
                                    ) : (
                                        <button
                                            className={cn(
                                                styles.aiInputTextSourceOption,
                                                styles.aiInputTextPromptOption,
                                                (
                                                    pendingTextInput?.text ||
                                                    draftTextInput ||
                                                    currentTextLikeInput ||
                                                    ''
                                                ).trim() && styles.aiInputTextSourceOptionSelected,
                                            )}
                                            type="button"
                                            onClick={startEditingTextInput}
                                        >
                                            <span
                                                className={cn(
                                                    styles.aiInputTextSourcePreview,
                                                    !pendingTextInput?.text &&
                                                        styles.aiInputTextPromptPlaceholder,
                                                )}
                                            >
                                                {pendingTextInput?.text ||
                                                    currentTextLikePlaceholder}
                                            </span>
                                        </button>
                                    )}
                                    {textKind === 'text' &&
                                        (currentText ||
                                            threadTexts.length > 0 ||
                                            treeTexts.length > 0) && (
                                            <div className={styles.aiInputTextThreadHeader}>
                                                Choose a prompt from current thread
                                            </div>
                                        )}
                                    {textKind === 'text' && currentText && (
                                        <button
                                            className={cn(
                                                styles.aiInputTextSourceOption,
                                                isTextInputSelected({
                                                    id: 'current-draft',
                                                    label: 'Message Draft',
                                                    text: currentText,
                                                }) && styles.aiInputTextSourceOptionSelected,
                                            )}
                                            type="button"
                                            onClick={() =>
                                                chooseTextInput({
                                                    id: 'current-draft',
                                                    label: 'Message Draft',
                                                    text: currentText,
                                                })
                                            }
                                        >
                                            <span className={styles.aiInputTextSourceLabel}>
                                                Message Draft
                                            </span>
                                            <span className={styles.aiInputTextSourcePreview}>
                                                {currentText}
                                            </span>
                                        </button>
                                    )}
                                    {textKind === 'text' &&
                                        threadTexts.map(input => (
                                            <button
                                                className={cn(
                                                    styles.aiInputTextSourceOption,
                                                    isTextInputSelected(input) &&
                                                        styles.aiInputTextSourceOptionSelected,
                                                )}
                                                type="button"
                                                key={input.id}
                                                onClick={() => chooseTextInput(input)}
                                            >
                                                {input.label && (
                                                    <span className={styles.aiInputTextSourceLabel}>
                                                        {input.label}
                                                    </span>
                                                )}
                                                <span className={styles.aiInputTextSourcePreview}>
                                                    {input.text}
                                                </span>
                                            </button>
                                        ))}
                                    {textKind === 'text' && treeTexts.length > 0 && (
                                        <div className={styles.aiInputMediaSection}>
                                            <div className={styles.aiInputMediaSectionHeading}>
                                                From Thread Tree
                                            </div>
                                            {treeTexts.map(input => (
                                                <button
                                                    className={cn(
                                                        styles.aiInputTextSourceOption,
                                                        isTextInputSelected(input) &&
                                                            styles.aiInputTextSourceOptionSelected,
                                                    )}
                                                    type="button"
                                                    key={input.id}
                                                    onClick={() => chooseTextInput(input)}
                                                >
                                                    {input.label && (
                                                        <span
                                                            className={
                                                                styles.aiInputTextSourceLabel
                                                            }
                                                        >
                                                            {input.label}
                                                        </span>
                                                    )}
                                                    <span
                                                        className={styles.aiInputTextSourcePreview}
                                                    >
                                                        {input.text}
                                                    </span>
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    {textKind === 'text' &&
                                        !currentText &&
                                        threadTexts.length === 0 &&
                                        treeTexts.length === 0 && (
                                            <div className={styles.aiInputEmpty}>
                                                {threadTreeMediaLoading
                                                    ? 'Loading thread tree prompts...'
                                                    : 'No thread text available'}
                                            </div>
                                        )}
                                    <div
                                        className={cn(
                                            styles.aiInputEditorActions,
                                            focusedSettingsInputKind &&
                                                styles.aiInputInlineActionsHidden,
                                        )}
                                    >
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={cancelTextInputSelection}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.aiInputPrimaryButton}
                                            type="button"
                                            disabled={!selectedInputTextValue}
                                            onClick={applyTextInput}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ) : isEditingTextInput ? (
                                <div className={styles.aiInputTextEditor}>
                                    {renderPromptTextEditor(false)}
                                    <div className={styles.aiInputEditorActions}>
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={() => {
                                                cancelDirectPromptDraftCommit()
                                                setPromptEditorSeedText(currentTextLikeInput)
                                                setIsEditingTextInput(false)
                                            }}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.aiInputPrimaryButton}
                                            type="button"
                                            disabled={promptEditorIsOverLimit}
                                            onClick={saveTextInput}
                                        >
                                            Save
                                        </button>
                                    </div>
                                </div>
                            ) : currentTextLikeInput ? (
                                <div className={styles.aiInputValueRow}>
                                    <div className={styles.aiInputTextPreview}>
                                        {currentTextLikeInput}
                                    </div>
                                    <div className={styles.aiInputValueActions}>
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={startEditingTextInput}
                                        >
                                            Edit
                                        </button>
                                        <button
                                            className={styles.aiInputIconButton}
                                            type="button"
                                            aria-label={`Clear ${
                                                textKind === 'lyrics' ? 'lyrics' : 'text'
                                            } input`}
                                            onClick={clearTextInput}
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                !currentTextLikeInput && null
                            )}
                        </div>
                    )}

                    {showImageInput && (
                        <div className={styles.aiInputPanel}>
                            <div className={styles.aiInputPanelHeader}>
                                <span className={styles.aiInputPanelTitle}>
                                    <ImageIcon size={15} />
                                    <span>{keyframeImageInput ? 'Start Frame' : 'Image'}</span>
                                    {!imageInput && !isSelectingImageInput && (
                                        <span className={styles.aiInputInlineEmpty}>
                                            {keyframeImageInput ? 'No start frame' : 'No image'}
                                        </span>
                                    )}
                                </span>
                                {isSelectingImageInput ? (
                                    renderLayoutToggle('image')
                                ) : !imageInput ? (
                                    <button
                                        className={styles.aiInputAddButton}
                                        type="button"
                                        aria-label={
                                            keyframeImageInput
                                                ? 'Add start frame'
                                                : 'Add image input'
                                        }
                                        onClick={() => setIsSelectingImageInput(true)}
                                    >
                                        <Plus size={14} />
                                    </button>
                                ) : null}
                            </div>

                            {imageInput && !isSelectingImageInput ? (
                                <div className={styles.aiInputImageValue}>
                                    <img
                                        src={imageInput.url}
                                        alt={imageInput.label || 'Selected'}
                                    />
                                    <div className={styles.aiInputImageMeta}>
                                        <span>
                                            {imageInput.label ||
                                                (keyframeImageInput
                                                    ? 'Selected start frame'
                                                    : 'Selected image')}
                                        </span>
                                        <div className={styles.aiInputValueActions}>
                                            <button
                                                className={styles.aiInputSecondaryButton}
                                                type="button"
                                                onClick={() => setIsSelectingImageInput(true)}
                                            >
                                                Change
                                            </button>
                                            <button
                                                className={styles.aiInputIconButton}
                                                type="button"
                                                aria-label={
                                                    keyframeImageInput
                                                        ? 'Clear start frame'
                                                        : 'Clear image input'
                                                }
                                                onClick={clearImageInput}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : isSelectingImageInput ? (
                                <div className={styles.aiInputImageSelector}>
                                    <input
                                        ref={attachImageInputRef}
                                        className={styles.aiInputHiddenFileInput}
                                        data-testid="ai-action-image-file-input"
                                        type="file"
                                        accept="image/*"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        style={{ display: 'none' }}
                                        onChange={attachImageInput}
                                    />
                                    <div className={styles.aiInputPickerTopRow}>
                                        <button
                                            className={styles.aiInputDeviceButton}
                                            type="button"
                                            onClick={() => attachImageInputRef.current?.click()}
                                        >
                                            <Plus size={16} />
                                            <span className={styles.aiInputAudioText}>
                                                <span>Add from Device</span>
                                            </span>
                                        </button>
                                    </div>
                                    {mediaSectionsFor(
                                        deviceImageChoices,
                                        imageChoices,
                                        treeImageChoices,
                                        'Loading thread tree images...',
                                    ).map(section => (
                                        <div
                                            className={styles.aiInputMediaSection}
                                            key={section.id}
                                        >
                                            <div className={styles.aiInputMediaSectionHeading}>
                                                {section.label}
                                            </div>
                                            <div
                                                className={cn(
                                                    styles.aiInputImageGrid,
                                                    mediaInputLayouts.image === 'list' &&
                                                        styles.aiInputMediaList,
                                                )}
                                            >
                                                {section.items.map(image => (
                                                    <button
                                                        className={cn(
                                                            styles.aiInputImageOption,
                                                            isImageInputSelected(image) &&
                                                                styles.aiInputImageOptionSelected,
                                                        )}
                                                        key={image.id}
                                                        type="button"
                                                        aria-label="Choose thread image"
                                                        onClick={() => chooseImageInput(image)}
                                                    >
                                                        <img src={image.url} alt="" />
                                                    </button>
                                                ))}
                                            </div>
                                            {section.emptyLabel && (
                                                <div className={styles.aiInputEmpty}>
                                                    {section.emptyLabel}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {deviceImageChoices.length === 0 &&
                                        imageChoices.length === 0 &&
                                        treeImageChoices.length === 0 &&
                                        !threadTreeMediaLoading && (
                                            <div className={styles.aiInputEmpty}>
                                                No thread images available
                                            </div>
                                        )}
                                    <div
                                        className={cn(
                                            styles.aiInputEditorActions,
                                            focusedSettingsInputKind &&
                                                styles.aiInputInlineActionsHidden,
                                        )}
                                    >
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={cancelImageInputSelection}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.aiInputPrimaryButton}
                                            type="button"
                                            disabled={!pendingImageInput}
                                            onClick={applyImageInput}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                !imageInput && null
                            )}
                        </div>
                    )}

                    {showVideoInput && (
                        <div className={styles.aiInputPanel}>
                            <div className={styles.aiInputPanelHeader}>
                                <span className={styles.aiInputPanelTitle}>
                                    <VideoIcon size={15} />
                                    <span>Video</span>
                                    {!videoInput && !isSelectingVideoInput && (
                                        <span className={styles.aiInputInlineEmpty}>No video</span>
                                    )}
                                </span>
                                {isSelectingVideoInput ? (
                                    renderLayoutToggle('video')
                                ) : !videoInput ? (
                                    <button
                                        className={styles.aiInputAddButton}
                                        type="button"
                                        aria-label="Add video input"
                                        onClick={() => setIsSelectingVideoInput(true)}
                                    >
                                        <Plus size={14} />
                                    </button>
                                ) : null}
                            </div>

                            {videoInput && !isSelectingVideoInput ? (
                                <div className={styles.aiInputImageValue}>
                                    <VideoInputPreview
                                        video={videoInput}
                                        className={styles.aiInputSelectedVideoPreview}
                                    />
                                    <div className={styles.aiInputImageMeta}>
                                        <span>{videoInput.label || 'Selected video'}</span>
                                        <div className={styles.aiInputValueActions}>
                                            <button
                                                className={styles.aiInputSecondaryButton}
                                                type="button"
                                                onClick={() => setIsSelectingVideoInput(true)}
                                            >
                                                Change
                                            </button>
                                            <button
                                                className={styles.aiInputIconButton}
                                                type="button"
                                                aria-label="Clear video input"
                                                onClick={clearVideoInput}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : isSelectingVideoInput ? (
                                <div className={styles.aiInputImageSelector}>
                                    <input
                                        ref={attachVideoInputRef}
                                        className={styles.aiInputHiddenFileInput}
                                        type="file"
                                        accept="video/*"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        style={{ display: 'none' }}
                                        onChange={attachVideoInput}
                                    />
                                    <div className={styles.aiInputPickerTopRow}>
                                        <button
                                            className={styles.aiInputDeviceButton}
                                            type="button"
                                            onClick={() => attachVideoInputRef.current?.click()}
                                        >
                                            <Plus size={16} />
                                            <span className={styles.aiInputAudioText}>
                                                <span>Add from Device</span>
                                            </span>
                                        </button>
                                    </div>
                                    {mediaSectionsFor(
                                        deviceVideoChoices,
                                        videoChoices,
                                        treeVideoChoices,
                                        'Loading thread tree videos...',
                                    ).map(section => (
                                        <div
                                            className={styles.aiInputMediaSection}
                                            key={section.id}
                                        >
                                            <div className={styles.aiInputMediaSectionHeading}>
                                                {section.label}
                                            </div>
                                            <div
                                                className={cn(
                                                    styles.aiInputImageGrid,
                                                    styles.aiInputVideoGrid,
                                                    mediaInputLayouts.video === 'list' &&
                                                        styles.aiInputMediaList,
                                                )}
                                            >
                                                {section.items.map(video => (
                                                    <button
                                                        className={cn(
                                                            styles.aiInputImageOption,
                                                            styles.aiInputVideoOption,
                                                            isVideoInputSelected(video) &&
                                                                styles.aiInputVideoOptionSelected,
                                                        )}
                                                        key={video.id}
                                                        type="button"
                                                        aria-label="Choose thread video"
                                                        onClick={() => chooseVideoInput(video)}
                                                    >
                                                        <VideoInputPreview video={video} />
                                                    </button>
                                                ))}
                                            </div>
                                            {section.emptyLabel && (
                                                <div className={styles.aiInputEmpty}>
                                                    {section.emptyLabel}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {deviceVideoChoices.length === 0 &&
                                        videoChoices.length === 0 &&
                                        treeVideoChoices.length === 0 &&
                                        !threadTreeMediaLoading && (
                                            <div className={styles.aiInputEmpty}>
                                                No thread videos available
                                            </div>
                                        )}
                                    <div
                                        className={cn(
                                            styles.aiInputEditorActions,
                                            focusedSettingsInputKind &&
                                                styles.aiInputInlineActionsHidden,
                                        )}
                                    >
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={cancelVideoInputSelection}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.aiInputPrimaryButton}
                                            type="button"
                                            disabled={!pendingVideoInput}
                                            onClick={applyVideoInput}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                !videoInput && null
                            )}
                        </div>
                    )}

                    {showAudioInput && (
                        <div className={styles.aiInputPanel}>
                            <div className={styles.aiInputPanelHeader}>
                                <span className={styles.aiInputPanelTitle}>
                                    <MusicIcon size={15} />
                                    <span>Audio</span>
                                    {!audioInput && !isSelectingAudioInput && (
                                        <span className={styles.aiInputInlineEmpty}>No audio</span>
                                    )}
                                </span>
                                {isSelectingAudioInput ? (
                                    renderLayoutToggle('audio')
                                ) : !audioInput ? (
                                    <button
                                        className={styles.aiInputAddButton}
                                        type="button"
                                        aria-label="Add audio input"
                                        onClick={() => setIsSelectingAudioInput(true)}
                                    >
                                        <Plus size={14} />
                                    </button>
                                ) : null}
                            </div>

                            {audioInput && !isSelectingAudioInput ? (
                                <div className={styles.aiInputAudioValue}>
                                    <span className={styles.aiInputAudioIcon}>
                                        <MusicIcon size={18} />
                                    </span>
                                    <div className={styles.aiInputImageMeta}>
                                        <span>{audioInput.label || 'Selected audio'}</span>
                                        <div className={styles.aiInputValueActions}>
                                            <button
                                                className={styles.aiInputSecondaryButton}
                                                type="button"
                                                onClick={() => setIsSelectingAudioInput(true)}
                                            >
                                                Change
                                            </button>
                                            <button
                                                className={styles.aiInputIconButton}
                                                type="button"
                                                aria-label="Clear audio input"
                                                onClick={clearAudioInput}
                                            >
                                                <X size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ) : isSelectingAudioInput ? (
                                <div className={styles.aiInputImageSelector}>
                                    <input
                                        ref={attachAudioInputRef}
                                        className={styles.aiInputHiddenFileInput}
                                        type="file"
                                        accept="audio/*"
                                        tabIndex={-1}
                                        aria-hidden="true"
                                        style={{ display: 'none' }}
                                        onChange={attachAudioInput}
                                    />
                                    <div className={styles.aiInputPickerTopRow}>
                                        <button
                                            className={styles.aiInputDeviceButton}
                                            type="button"
                                            onClick={() => attachAudioInputRef.current?.click()}
                                        >
                                            <Plus size={16} />
                                            <span className={styles.aiInputAudioText}>
                                                <span>Add from Device</span>
                                            </span>
                                        </button>
                                    </div>
                                    {mediaSectionsFor(
                                        deviceAudioChoices,
                                        audioChoices,
                                        treeAudioChoices,
                                        'Loading thread tree audio...',
                                    ).map(section => (
                                        <div
                                            className={styles.aiInputMediaSection}
                                            key={section.id}
                                        >
                                            <div className={styles.aiInputMediaSectionHeading}>
                                                {section.label}
                                            </div>
                                            <div
                                                className={cn(
                                                    styles.aiInputAudioList,
                                                    mediaInputLayouts.audio === 'grid' &&
                                                        styles.aiInputAudioGrid,
                                                )}
                                            >
                                                {section.items.map(audio => (
                                                    <button
                                                        className={cn(
                                                            styles.aiInputAudioOption,
                                                            isAudioInputSelected(audio) &&
                                                                styles.aiInputAudioOptionSelected,
                                                        )}
                                                        key={audio.id}
                                                        type="button"
                                                        onClick={() => chooseAudioInput(audio)}
                                                    >
                                                        <MusicIcon size={16} />
                                                        <span className={styles.aiInputAudioText}>
                                                            <span>
                                                                {audio.label || 'Thread audio'}
                                                            </span>
                                                            {audio.metaLabel && (
                                                                <span
                                                                    className={
                                                                        styles.aiInputAudioMeta
                                                                    }
                                                                >
                                                                    {audio.metaLabel}
                                                                </span>
                                                            )}
                                                        </span>
                                                    </button>
                                                ))}
                                            </div>
                                            {section.emptyLabel && (
                                                <div className={styles.aiInputEmpty}>
                                                    {section.emptyLabel}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    {deviceAudioChoices.length === 0 &&
                                        audioChoices.length === 0 &&
                                        treeAudioChoices.length === 0 &&
                                        !threadTreeMediaLoading && (
                                            <div className={styles.aiInputEmpty}>
                                                No thread audio available
                                            </div>
                                        )}
                                    <div
                                        className={cn(
                                            styles.aiInputEditorActions,
                                            focusedSettingsInputKind &&
                                                styles.aiInputInlineActionsHidden,
                                        )}
                                    >
                                        <button
                                            className={styles.aiInputSecondaryButton}
                                            type="button"
                                            onClick={cancelAudioInputSelection}
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            className={styles.aiInputPrimaryButton}
                                            type="button"
                                            disabled={!pendingAudioInput}
                                            onClick={applyAudioInput}
                                        >
                                            Add
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                !audioInput && null
                            )}
                        </div>
                    )}
                </div>
                {renderPromptBrowserDialog()}
            </div>
        )
    }

    const renderMediaPreviewModal = () => {
        if (!mediaPreviewTarget) return null

        const canRemove = mediaPreviewIsReference(mediaPreviewTarget)
        const canAdd = !canRemove
        const addLabel = `Add ${
            mediaPreviewTarget.kind[0].toUpperCase() + mediaPreviewTarget.kind.slice(1)
        }`

        return (
            <AiMediaPreviewModal
                target={mediaPreviewTarget}
                footerMode="select"
                canRemove={canRemove}
                canAdd={canAdd}
                addLabel={addLabel}
                onClose={() => setMediaPreviewTarget(null)}
                onRemove={removeMediaPreviewReference}
                onAdd={selectMediaPreviewInput}
            />
        )
    }

    const renderAiActionModalPortal = (content: React.ReactNode, key: string) => {
        if (typeof document === 'undefined') return content
        return createPortal(content, document.body, key)
    }

    const renderReferenceInputGlyph = (count?: number) => (
        <span className={styles.aiModelReferenceGlyph} aria-hidden="true">
            {typeof count === 'number' && count > 0 ? `${count}R` : 'R'}
        </span>
    )

    const renderReferenceSettingsDialog = () => {
        if (!referencesModel) return null

        const references = referenceInputsForActionDefinitionId(referencesModel.id)
        const frameActionTag = normalizeActionCommandTag(referenceLimitModel?.actionTagName)
        const startFrame = getModelInputs(referencesModel.id).image
        const referenceAttrs = resolvedDraftAttrsForActionDefinitionId(referencesModel.id)
        const isFrameMode = videoKeyframeModeForFormState(
            frameActionTag,
            !!startFrame,
            referenceAttrs.videoKeyframeMode === true,
        )
        const hasReferenceInputs = REFERENCE_INPUT_KINDS.some(
            kind => (references[kind] || []).length > 0,
        )
        const canToggleH3EndFrameMode =
            frameActionTag === 'h3' && !startFrame && !hasReferenceInputs
        const referenceKinds = isFrameMode
            ? (['image'] as AiReferenceInputKind[])
            : referenceLimitModel
            ? referenceInputKindsForModel(referenceLimitModel)
            : []
        const editingReferenceKind = referenceInputEdit?.kind || null
        const ReferencesModelIcon = iconForModelType(referencesModel.modelType)

        return renderAiActionModalPortal(
            <div
                className={styles.aiModelSettingsOverlay}
                onMouseDown={event => {
                    if (event.target !== event.currentTarget) return
                    if (editingReferenceKind) {
                        closeReferenceInputSettings()
                        return
                    }
                    closeReferenceSettings()
                }}
            >
                <div
                    className={styles.aiModelSettingsDialog}
                    data-testid="ai-action-reference-dialog"
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ai-reference-settings-title"
                    onMouseDown={event => event.stopPropagation()}
                >
                    <div className={styles.aiModelSettingsHeader}>
                        <div className={styles.aiModelSettingsHeading}>
                            <AiModelBrandAvatar
                                model={referencesModel}
                                size={36}
                                className={styles.aiModelSettingsAvatar}
                            />
                            <div className={styles.aiModelSettingsHeadingText}>
                                <div
                                    className={[
                                        styles.aiModelSettingsTypeRow,
                                        styles.aiModelSettingsModelRow,
                                    ].join(' ')}
                                >
                                    <span className={styles.aiModelSettingsTypeIcon}>
                                        <ReferencesModelIcon size={12} />
                                    </span>
                                    <span>{modelDisplayName(referencesModel)}</span>
                                </div>
                                <h3 id="ai-reference-settings-title">
                                    {isFrameMode
                                        ? editingReferenceKind
                                            ? 'Choose End Frame'
                                            : 'End Frame'
                                        : referenceInputDialogTitle(editingReferenceKind)}
                                </h3>
                            </div>
                        </div>
                        <button
                            className={styles.aiModelModalClose}
                            type="button"
                            aria-label="Close references"
                            onClick={
                                editingReferenceKind
                                    ? closeReferenceInputSettings
                                    : closeReferenceSettings
                            }
                        >
                            <X size={18} />
                        </button>
                    </div>
                    <div className={styles.aiModelSettingsBody}>
                        {!editingReferenceKind && canToggleH3EndFrameMode && (
                            <button
                                className={styles.aiInputSecondaryButton}
                                type="button"
                                onClick={() =>
                                    updateDraftAiActionAttrs(
                                        referencesModel.id,
                                        attrs => ({
                                            ...attrs,
                                            videoKeyframeMode: !isFrameMode,
                                        }),
                                        { commit: directReferencesEdit },
                                    )
                                }
                            >
                                {isFrameMode ? 'Use References' : 'Use End Frame'}
                            </button>
                        )}
                        {editingReferenceKind
                            ? renderChooseInputs([editingReferenceKind])
                            : referenceKinds.map(kind => {
                                  const inputs = references[kind] || []
                                  const referenceLimit = referenceInputLimitForModel(
                                      referenceLimitModel,
                                      kind,
                                  )
                                  const effectiveLimit =
                                      isFrameMode && kind === 'image' ? 1 : referenceLimit
                                  const canAddMore =
                                      inputs.length < effectiveLimit &&
                                      (!isFrameMode ||
                                          !endFrameRequiresStartFrame(frameActionTag) ||
                                          !!startFrame)
                                  return (
                                      <div className={styles.aiReferenceRow} key={kind}>
                                          <div className={styles.aiModelSettingsLabel}>
                                              <span>
                                                  {isFrameMode && kind === 'image'
                                                      ? 'END FRAME'
                                                      : `${
                                                            inputs.length
                                                        }/${effectiveLimit} ${referenceKindLabel(
                                                            kind,
                                                            inputs.length,
                                                        )}`}
                                              </span>
                                          </div>
                                          <div className={styles.aiReferenceRowItems}>
                                              {inputs.map((input, index) => (
                                                  <div
                                                      className={styles.aiReferenceInputItem}
                                                      data-testid={`ai-action-reference-${kind}-item`}
                                                      key={input.url}
                                                  >
                                                      <div
                                                          className={cn(
                                                              styles.aiModelInputBlock,
                                                              styles.aiModelInputBlockButton,
                                                              styles.aiReferenceInputPreviewButton,
                                                          )}
                                                          role="button"
                                                          tabIndex={0}
                                                          title={input.label || `Reference ${kind}`}
                                                          onClick={() =>
                                                              setMediaPreviewTarget({
                                                                  kind,
                                                                  input: input as
                                                                      | AiInputImage
                                                                      | AiInputVideo
                                                                      | AiInputAudio,
                                                              })
                                                          }
                                                          onKeyDown={event => {
                                                              if (
                                                                  event.key === 'Enter' ||
                                                                  event.key === ' '
                                                              ) {
                                                                  event.preventDefault()
                                                                  setMediaPreviewTarget({
                                                                      kind,
                                                                      input: input as
                                                                          | AiInputImage
                                                                          | AiInputVideo
                                                                          | AiInputAudio,
                                                                  })
                                                              }
                                                          }}
                                                      >
                                                          {kind === 'image' &&
                                                              ((input as AiInputImage).url ? (
                                                                  <img
                                                                      src={
                                                                          (input as AiInputImage)
                                                                              .url
                                                                      }
                                                                      alt=""
                                                                  />
                                                              ) : (
                                                                  <ImageIcon size={18} />
                                                              ))}
                                                          {kind === 'video' && (
                                                              <VideoInputPreview
                                                                  video={input as AiInputVideo}
                                                              />
                                                          )}
                                                          {kind === 'audio' && (
                                                              <AudioLines size={18} />
                                                          )}
                                                      </div>
                                                      <span
                                                          className={styles.aiReferenceInputLabel}
                                                      >
                                                          {isFrameMode && kind === 'image'
                                                              ? 'End frame'
                                                              : `[${kind} ${index + 1}]`}
                                                      </span>
                                                  </div>
                                              ))}
                                              {canAddMore && inputs.length === 0 && (
                                                  <button
                                                      className={styles.aiReferenceEmptyAddButton}
                                                      type="button"
                                                      onClick={() =>
                                                          openReferenceInputSettings(
                                                              referencesModel,
                                                              kind,
                                                          )
                                                      }
                                                  >
                                                      <Plus size={16} />
                                                      <span>
                                                          {isFrameMode && kind === 'image'
                                                              ? 'Add end frame'
                                                              : `Add ${kind}`}
                                                      </span>
                                                  </button>
                                              )}
                                              {!canAddMore &&
                                                  isFrameMode &&
                                                  endFrameRequiresStartFrame(frameActionTag) &&
                                                  !startFrame &&
                                                  inputs.length === 0 && (
                                                      <div className={styles.aiInputEmpty}>
                                                          Add a Start Frame first
                                                      </div>
                                                  )}
                                              {canAddMore && inputs.length > 0 && (
                                                  <button
                                                      className={styles.aiReferenceAddIconButton}
                                                      type="button"
                                                      aria-label={
                                                          isFrameMode && kind === 'image'
                                                              ? 'Change end frame'
                                                              : `Add ${kind} reference`
                                                      }
                                                      onClick={() =>
                                                          openReferenceInputSettings(
                                                              referencesModel,
                                                              kind,
                                                          )
                                                      }
                                                  >
                                                      <Plus size={18} />
                                                  </button>
                                              )}
                                          </div>
                                      </div>
                                  )
                              })}
                    </div>
                    {!editingReferenceKind && (
                        <div className={styles.aiModelSettingsFooter}>
                            <button
                                className={styles.aiInputPrimaryButton}
                                type="button"
                                onClick={closeReferenceSettings}
                            >
                                Done
                            </button>
                        </div>
                    )}
                    {editingReferenceKind && renderInputDialogFooter()}
                </div>
            </div>,
            'ai-action-reference-settings-dialog',
        )
    }

    const renderModelRow = (model: ActionDefinition) => {
        const modelDraftAttrs = resolvedDraftAttrsForActionDefinitionId(model.id)
        const modelPricingModel =
            findEffectiveModelForAction(aiModels, model.actionTagName, {
                ...modelDraftAttrs,
                tag: model.actionTagName,
            }) || model
        const pricePresentation = modelChooserPricePresentation(modelPricingModel, questIsPublic)
        const modelDraftSettings = modelSettingsByActionDefinitionId[model.id] || {}
        const selectedDefinition = actionDefinitionForId(selectedActionDefinitionId)
        const hideInputMetaForStitchModelPicker =
            directModelPickerEdit &&
            (editingActionRequestAttrsRef.current?.tag === 'stitch' ||
                selectedDefinition?.actionTagName === 'stitch')
        const selectedExtendModel =
            model.actionTagName === 'extend'
                ? selectedExtendModelForSettings({
                      extendAiModelId:
                          modelDraftAttrs.extendAiModelId ||
                          modelDraftSettings.extendAiModelId ||
                          draftModelSettings.extendAiModelId,
                      extendActionTag:
                          modelDraftAttrs.extendActionTag ||
                          modelDraftSettings.extendActionTag ||
                          draftModelSettings.extendActionTag,
                  })
                : null
        const referenceSupportModel = model.actionTagName === 'extend' ? selectedExtendModel : model
        const inputIconForKind = (kind: AiInputKind) => {
            switch (kind) {
                case 'text':
                    return <PenLine />
                case 'lyrics':
                    return <Mic2 />
                case 'image':
                    return <ImageIcon />
                case 'video':
                    return <VideoIcon />
                case 'audio':
                    return <AudioLines />
                default:
                    return null
            }
        }
        const referenceIconForKind = (kind: AiReferenceInputKind) => {
            switch (kind) {
                case 'image':
                    return <ImageIcon />
                case 'video':
                    return <VideoIcon />
                case 'audio':
                    return <AudioLines />
                default:
                    return null
            }
        }
        const inputItems =
            model.actionTagName === 'stitch'
                ? [
                      { key: 'video-1', label: 'Video 1', icon: <VideoIcon /> },
                      { key: 'video-2', label: 'Video 2', icon: <VideoIcon /> },
                  ]
                : supportedInputKindsForModel(model).map(kind => ({
                      key: kind,
                      label: labelForInputKind(kind),
                      icon: inputIconForKind(kind),
                  }))
        const visibleInputItems = hideInputMetaForStitchModelPicker ? [] : inputItems
        const referenceInputItems =
            !hideInputMetaForStitchModelPicker &&
            referenceSupportModel &&
            supportsReferenceInputs(referenceSupportModel)
                ? referenceInputKindsForModel(referenceSupportModel).map(kind => {
                      const limit = referenceInputLimitForModel(referenceSupportModel, kind)
                      return {
                          key: kind,
                          label: kind,
                          limit,
                          icon: referenceIconForKind(kind),
                      }
                  })
                : []
        const modelDescription =
            (model.descriptionShort || model.description || '').trim() ||
            `${labelForModelType(model)} model.`
        const durationValues = durationValuesForModel(
            model.actionTagName === 'extend' && selectedExtendModel ? selectedExtendModel : model,
        )
        const aspectRatioValues = aspectRatioOptionsForModel(model).map(option => option.value)
        const settingsItems = [
            durationValues.length > 0
                ? {
                      key: 'duration',
                      label: 'DUR',
                      values: durationValues.map(duration => ({
                          value: `${duration}`,
                          unit: 's',
                      })),
                  }
                : null,
            aspectRatioValues.length > 0
                ? {
                      key: 'aspect-ratio',
                      label: 'AR',
                      values: aspectRatioValues,
                  }
                : null,
            model.actionTagName === 'gif'
                ? {
                      key: 'frame-rate',
                      label: 'FPS',
                      values: GIF_FRAME_RATE_OPTIONS.map(option => `${option.value}`),
                  }
                : null,
            model.actionTagName === 'gif'
                ? {
                      key: 'max-width',
                      label: 'Width',
                      values: GIF_MAX_WIDTH_OPTIONS.map(option => ({
                          value: `${option.value}`,
                          unit: 'px',
                      })),
                  }
                : null,
            model.actionTagName === 'extend' && selectedExtendModel
                ? {
                      key: 'extend-model',
                      label: 'Model',
                      values: [modelDisplayName(selectedExtendModel)],
                  }
                : null,
            model.actionTagName === 'extend'
                ? {
                      key: 'extend-output',
                      label: 'Smart Stitch',
                      values: [],
                  }
                : null,
            model.actionTagName === 'screenshot'
                ? {
                      key: 'time',
                      label: 'Time',
                      values: ['Frame'],
                  }
                : null,
            model.actionTagName === 'stitch'
                ? {
                      key: 'output',
                      label: 'Smart Stitch',
                      values: [],
                  }
                : null,
        ].filter(Boolean) as Array<{
            key: string
            label: string
            values: ModelSettingTileValue[]
        }>
        const visibleSettingsItems = hideInputMetaForStitchModelPicker ? [] : settingsItems
        const modelOutput = outputForModel(model)
        const isSelectedForAdd = selectedActionDefinitionIds.includes(model.id)
        const isModelRowSelected = directModelPickerEdit
            ? selectedActionDefinitionId === model.id
            : isSelectedForAdd
        const brandName = aiModelBrandNameFor(model)

        return (
            <div
                key={model.id}
                className={cn(styles.aiModelRow, isModelRowSelected && styles.aiModelRowSelected)}
            >
                <div className={styles.aiModelMain}>
                    <div className={styles.aiModelTopRow}>
                        <button
                            className={styles.aiModelContentButton}
                            data-testid="ai-action-model-option"
                            data-action-definition-id={model.id}
                            data-action-kind={model.kind || 'model'}
                            data-action-tag={model.actionTagName}
                            data-ai-model-id={aiModelIdForActionDefinition(model)}
                            data-tool-key={model.kind === 'tool' ? model.toolKey : null}
                            type="button"
                            aria-pressed={!directModelPickerEdit ? isSelectedForAdd : undefined}
                            onClick={() => handleModelRowClick(model)}
                        >
                            <AiModelBrandAvatar
                                model={model}
                                className={styles.aiModelAvatar}
                                selected={isModelRowSelected}
                                halo
                            />
                            <span className={styles.aiModelText}>
                                <span className={styles.aiModelNameLine}>
                                    <span className={styles.aiModelName}>
                                        {modelDisplayName(model)}
                                    </span>
                                    <span
                                        className={cn(
                                            styles.aiModelPrice,
                                            pricePresentation.free && styles.aiModelPriceFree,
                                        )}
                                    >
                                        <span className={styles.aiModelPriceAmount}>
                                            {pricePresentation.amount}
                                        </span>
                                        {pricePresentation.unit && (
                                            <span className={styles.aiModelPriceUnit}>
                                                {pricePresentation.unit}
                                            </span>
                                        )}
                                    </span>
                                </span>
                                <span className={styles.aiModelBrandLine}>
                                    {brandName && (
                                        <span className={styles.aiModelBrandName}>{brandName}</span>
                                    )}
                                    {brandName && (
                                        <span className={styles.aiModelBrandDot} aria-hidden="true">
                                            ·
                                        </span>
                                    )}
                                    <span className={styles.aiModelCommandTag}>
                                        !{model.actionTagName}
                                    </span>
                                </span>
                                <span className={styles.aiModelDescription}>
                                    {modelDescription}
                                </span>
                                {(visibleInputItems.length > 0 ||
                                    referenceInputItems.length > 0 ||
                                    visibleSettingsItems.length > 0) && (
                                    <span className={styles.aiModelCapabilities}>
                                        {(visibleInputItems.length > 0 ||
                                            referenceInputItems.length > 0) && (
                                            <span className={styles.aiModelFlowLine}>
                                                {visibleInputItems.map(input => (
                                                    <span
                                                        className={styles.aiModelPill}
                                                        key={input.key}
                                                    >
                                                        <span className={styles.aiModelPillIcon}>
                                                            {input.icon}
                                                        </span>
                                                        <span>{input.label}</span>
                                                    </span>
                                                ))}
                                                {referenceInputItems.length > 0 && (
                                                    <span
                                                        className={styles.aiModelPill}
                                                        title={`Up to ${referenceInputItems
                                                            .map(
                                                                reference =>
                                                                    `${reference.limit} ${reference.label}`,
                                                            )
                                                            .join(', ')} references`}
                                                    >
                                                        <span className={styles.aiModelPillIcon}>
                                                            <Paperclip />
                                                        </span>
                                                        <span>Refs</span>
                                                    </span>
                                                )}
                                                <span className={styles.aiModelFlowOutput}>
                                                    <ArrowRight
                                                        className={styles.aiModelFlowArrow}
                                                        aria-hidden="true"
                                                    />
                                                    <span
                                                        className={cn(
                                                            styles.aiModelPill,
                                                            styles.aiModelPillOutput,
                                                        )}
                                                    >
                                                        <span className={styles.aiModelPillIcon}>
                                                            {modelOutput.icon}
                                                        </span>
                                                        <span>{modelOutput.label}</span>
                                                    </span>
                                                </span>
                                            </span>
                                        )}
                                        {visibleSettingsItems.length > 0 && (
                                            <span className={styles.aiModelFactLine}>
                                                {visibleSettingsItems.map(setting => {
                                                    const fact = settingFact(setting)
                                                    return (
                                                        <span
                                                            className={styles.aiModelFact}
                                                            key={setting.key}
                                                        >
                                                            {fact.icon && (
                                                                <span
                                                                    className={
                                                                        styles.aiModelFactIcon
                                                                    }
                                                                >
                                                                    {fact.icon}
                                                                </span>
                                                            )}
                                                            <span>{fact.text}</span>
                                                        </span>
                                                    )
                                                })}
                                            </span>
                                        )}
                                    </span>
                                )}
                            </span>
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    const renderSettingGroup = <TValue extends string | number>(
        label: string,
        options: ModelSettingOption<TValue>[],
        selectedValue: TValue | undefined,
        onSelect: (value: TValue) => void,
        labelMeta?: string | null,
        renderOption?: (option: ModelSettingOption<TValue>) => React.ReactNode,
        optionClassName?: string,
    ) => {
        if (options.length === 0) return null

        return (
            <div className={styles.aiModelSettingsGroup}>
                <div className={styles.aiModelSettingsLabel}>
                    <span>{label}</span>
                    {labelMeta && <span>{labelMeta}</span>}
                </div>
                <div className={styles.aiModelSettingsOptions}>
                    {options.map(option => (
                        <button
                            key={option.value}
                            className={cn(
                                styles.aiModelSettingsOption,
                                optionClassName,
                                selectedValue === option.value &&
                                    styles.aiModelSettingsOptionActive,
                                option.disabled && styles.aiModelSettingsOptionDisabled,
                            )}
                            type="button"
                            disabled={settingsReadOnly || option.disabled}
                            title={option.disabledReason}
                            onClick={() => {
                                if (option.disabled) return
                                onSelect(option.value)
                            }}
                        >
                            {renderOption ? (
                                renderOption(option)
                            ) : (
                                <>
                                    <span>{option.label}</span>
                                    <span>{option.detail || option.value}</span>
                                </>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        )
    }

    const renderGenerationOption = (option: ModelSettingOption<number>) => {
        const count = Math.max(1, Math.min(4, Number(option.value) || 1))
        const previewClassName = cn(
            styles.aiGenerationPreview,
            count === 1 && styles.aiGenerationPreviewOne,
            count === 2 && styles.aiGenerationPreviewTwo,
            count === 3 && styles.aiGenerationPreviewThree,
            count === 4 && styles.aiGenerationPreviewFour,
        )

        return (
            <div className={styles.aiGenerationOptionContent}>
                <div className={styles.aiGenerationPreviewCount}>{count}</div>
                <div
                    className={previewClassName}
                    aria-label={`${count} generation${count === 1 ? '' : 's'}`}
                >
                    {Array.from({ length: count }, (_, index) => (
                        <div className={styles.aiGenerationPreviewSquare} key={index} />
                    ))}
                </div>
            </div>
        )
    }

    const renderExtendModelDialog = () => {
        if (!settingsModel || settingsModel.actionTagName !== 'extend') return null

        const SettingsModelIcon = iconForModelType(settingsModel.modelType)
        const selectedExtendCommandTag =
            latestExtendModelCommandTag(pendingExtendModelCommandTag, aiModels) ||
            pendingExtendModelCommandTag
        const extendModels = preferredExtendModels(
            aiModels,
            (settingsModel as Partial<ActionDefinition>).defaultAiModelId,
        )
        const selectedExtendModel = extendModelForCommandTag(selectedExtendCommandTag)

        if (
            selectedExtendModel &&
            !extendModels.some(model => model.actionTagName === selectedExtendModel.actionTagName)
        ) {
            extendModels.unshift(selectedExtendModel)
        }

        return renderAiActionModalPortal(
            <div
                className={styles.aiModelSettingsOverlay}
                style={keyboardAwareModalStyle}
                onMouseDown={event => {
                    if (event.target === event.currentTarget) closeExtendModelSettings()
                }}
            >
                <div
                    className={styles.aiModelSettingsDialog}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ai-extend-model-title"
                    onMouseDown={event => event.stopPropagation()}
                >
                    <div className={styles.aiModelSettingsHeader}>
                        <div className={styles.aiModelSettingsHeading}>
                            <AiModelBrandAvatar
                                model={settingsModel}
                                size={36}
                                className={styles.aiModelSettingsAvatar}
                            />
                            <div className={styles.aiModelSettingsHeadingText}>
                                <div className={styles.aiModelSettingsTypeRow}>
                                    <span className={styles.aiModelSettingsTypeIcon}>
                                        <SettingsModelIcon size={12} />
                                    </span>
                                    <span>{modelDisplayName(settingsModel)}</span>
                                </div>
                                <h3 id="ai-extend-model-title">Choose Model</h3>
                            </div>
                        </div>
                        <button
                            className={styles.aiModelModalClose}
                            type="button"
                            aria-label="Close choose model"
                            onClick={closeExtendModelSettings}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div className={styles.aiModelSettingsBody}>
                        <div className={styles.aiModelSettingsGroup}>
                            <div className={styles.aiModelSettingsOptions}>
                                {extendModels.map(model => (
                                    <button
                                        className={cn(
                                            styles.aiModelSettingsOption,
                                            styles.aiModelSettingsModelOption,
                                            selectedExtendCommandTag === model.actionTagName &&
                                                styles.aiModelSettingsOptionActive,
                                        )}
                                        type="button"
                                        key={model.actionTagName}
                                        onClick={() =>
                                            togglePendingExtendModel(model.actionTagName)
                                        }
                                    >
                                        <span className={styles.aiModelSettingsModelOptionName}>
                                            <AiModelBrandAvatar model={model} size={18} />
                                            <span>{modelDisplayName(model)}</span>
                                        </span>
                                        <span>
                                            {modelPricePerSecond(model, questIsPublic)
                                                ? `${formatPrice(
                                                      modelPricePerSecond(model, questIsPublic),
                                                  )}/sec`
                                                : formatPrice(
                                                      displayModelPrice(model, questIsPublic),
                                                  )}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className={styles.aiModelSettingsFooter}>
                        <button
                            className={styles.aiInputSecondaryButton}
                            type="button"
                            onClick={closeExtendModelSettings}
                        >
                            Cancel
                        </button>
                        <button
                            className={styles.aiInputPrimaryButton}
                            type="button"
                            onClick={applyExtendModelSelection}
                        >
                            Done
                        </button>
                    </div>
                </div>
            </div>,
            'ai-action-extend-model-dialog',
        )
    }

    const renderInputDialogFooter = () => {
        if (!focusedSettingsInputKind) return null

        const footerConfig =
            (focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics') &&
            isSelectingTextInput
                ? {
                      cancel: cancelTextInputSelection,
                      add: applyTextInput,
                      disabled: !selectedInputTextValue || promptEditorIsOverLimit,
                  }
                : focusedSettingsInputKind === 'image' && isSelectingImageInput
                ? {
                      cancel: cancelImageInputSelection,
                      add: applyImageInput,
                      disabled: !pendingImageInput,
                  }
                : focusedSettingsInputKind === 'video' && isSelectingVideoInput
                ? {
                      cancel: cancelVideoInputSelection,
                      add: applyVideoInput,
                      disabled: !pendingVideoInput,
                  }
                : focusedSettingsInputKind === 'audio' && isSelectingAudioInput
                ? {
                      cancel: cancelAudioInputSelection,
                      add: applyAudioInput,
                      disabled: !pendingAudioInput,
                  }
                : null

        if (!footerConfig) return null
        const showBrowsePrompts =
            focusedSettingsInputKind === 'text' && isSelectingTextInput && !settingsReadOnly

        return (
            <div className={styles.aiModelSettingsFooter}>
                {showBrowsePrompts && (
                    <button
                        className={cn(
                            styles.aiInputSecondaryButton,
                            styles.aiInputBrowsePromptsButton,
                        )}
                        type="button"
                        onClick={() => setIsBrowsingTextPrompts(true)}
                    >
                        <Search size={15} />
                        Browse Prompts
                    </button>
                )}
                <button
                    className={styles.aiInputSecondaryButton}
                    type="button"
                    onClick={footerConfig.cancel}
                >
                    Cancel
                </button>
                <button
                    className={styles.aiInputPrimaryButton}
                    type="button"
                    disabled={footerConfig.disabled}
                    onClick={footerConfig.add}
                >
                    Add
                </button>
            </div>
        )
    }

    const renderModelSettingsDialog = () => {
        if (!settingsModel) return null
        const SettingsModelIcon = iconForModelType(settingsModel.modelType)

        const aspectRatioOptions = aspectRatioOptionsForModel(settingsModel)
        const showGifSettings = settingsModel.actionTagName === 'gif'
        const showExtendSettings = settingsModel.actionTagName === 'extend'
        const showScreenshotSettings = settingsModel.actionTagName === 'screenshot'
        const inputOnlySettings = !!focusedSettingsInputKind
        const textInputOnlySettings =
            focusedSettingsInputKind === 'text' || focusedSettingsInputKind === 'lyrics'
        if (settingsModel.actionTagName === 'stitch' && !inputOnlySettings) return null
        const settingsHeaderTitle =
            inputOnlySettings && focusedSettingsInputKind
                ? focusedSettingsInputKind === 'image' &&
                  supportsExplicitVideoKeyframes(settingsModel.actionTagName)
                    ? 'Choose Start Frame'
                    : modelInputDialogTitle(focusedSettingsInputKind)
                : modelDisplayName(settingsModel)
        const selectedExtendModel = showExtendSettings
            ? selectedExtendModelForSettings(draftModelSettings) ||
              extendModelForModelId(
                  (settingsModel as Partial<ActionDefinition>).defaultAiModelId,
                  aiModels,
              ) ||
              aiModels.find(model => model.actionTagName === FALLBACK_EXTEND_COMMAND_TAG) ||
              preferredExtendModels(
                  aiModels,
                  (settingsModel as Partial<ActionDefinition>).defaultAiModelId,
              )[0] ||
              settingsModel
            : null
        const settingsActionAttrs = settingsActionDefinitionId
            ? draftAiActionAttrsByActionDefinitionId[settingsActionDefinitionId] || {}
            : {}
        const settingsEffectiveModel =
            findEffectiveModelForAction(aiModels, settingsModel.actionTagName, {
                ...settingsActionAttrs,
                tag: settingsModel.actionTagName,
            }) || settingsModel
        const settingsPricingModel = selectedExtendModel || settingsEffectiveModel
        const settingsPricingContext = getActionPricingContext(settingsPricingModel, {
            ...settingsActionAttrs,
            durationSeconds: draftModelSettings.durationSeconds,
        })
        const pricePerSecond = modelPricePerSecond(settingsPricingModel, questIsPublic)
        const durationPresentation = musicDurationControlPresentation(
            settingsPricingModel.actionTagName,
        )
        const durationOptions = durationOptionsForModel(
            settingsPricingModel,
            questIsPublic,
            settingsPricingContext,
        )
        const headerPrice = draftModelSettings.durationSeconds
            ? durationPriceForModel(
                  settingsPricingModel,
                  questIsPublic,
                  draftModelSettings.durationSeconds,
                  settingsPricingContext,
              )
            : displayModelPrice(settingsPricingModel, questIsPublic, settingsPricingContext)
        const selectedGifFrameRate = draftModelSettings.gifFrameRate || DEFAULT_GIF_FRAME_RATE
        const selectedGifMaxWidth = draftModelSettings.gifMaxWidth || DEFAULT_GIF_MAX_WIDTH
        const gifFrameRateOptions = GIF_FRAME_RATE_OPTIONS.map(option => {
            const allowed = gifOptionAllowed(gifVideoMetadata, option.value, selectedGifMaxWidth)
            return {
                ...option,
                disabled: !allowed,
                disabledReason: allowed ? undefined : 'Too large',
                detail: allowed ? option.detail : 'Too large',
            }
        })
        const gifMaxWidthOptions = GIF_MAX_WIDTH_OPTIONS.map(option => {
            const allowed = gifOptionAllowed(gifVideoMetadata, selectedGifFrameRate, option.value)
            return {
                ...option,
                disabled: !allowed,
                disabledReason: allowed ? undefined : 'Too large',
                detail: allowed ? option.detail : 'Too large',
            }
        })
        const gifWorkEstimate = showGifSettings
            ? estimateGifWork(gifVideoMetadata, selectedGifFrameRate, selectedGifMaxWidth)
            : null
        const gifSettingsMeta = gifWorkEstimate
            ? `${gifWorkEstimate.frames} frames, ${gifWorkEstimate.outputWidth}x${gifWorkEstimate.outputHeight}`
            : null
        return renderAiActionModalPortal(
            <div
                className={styles.aiModelSettingsOverlay}
                style={keyboardAwareModalStyle}
                onMouseDown={event => {
                    if (event.target !== event.currentTarget) return
                    if (inputOnlySettings) {
                        closeInputSettings()
                        return
                    }
                    closeModelSettings()
                }}
            >
                <div
                    className={cn(
                        styles.aiModelSettingsDialog,
                        textInputOnlySettings && styles.aiTextInputSettingsDialog,
                    )}
                    data-testid={
                        inputOnlySettings ? 'ai-action-input-dialog' : 'ai-action-settings-dialog'
                    }
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ai-model-settings-title"
                    onMouseDown={event => event.stopPropagation()}
                >
                    <div className={styles.aiModelSettingsHeader}>
                        <div className={styles.aiModelSettingsHeading}>
                            <AiModelBrandAvatar
                                model={settingsModel}
                                size={36}
                                className={styles.aiModelSettingsAvatar}
                            />
                            <div className={styles.aiModelSettingsHeadingText}>
                                <div
                                    className={[
                                        styles.aiModelSettingsTypeRow,
                                        inputOnlySettings ? styles.aiModelSettingsModelRow : '',
                                    ]
                                        .filter(Boolean)
                                        .join(' ')}
                                >
                                    <span className={styles.aiModelSettingsTypeIcon}>
                                        <SettingsModelIcon size={12} />
                                    </span>
                                    <span>
                                        {inputOnlySettings
                                            ? modelDisplayName(settingsModel)
                                            : labelForModelType(settingsModel)}
                                    </span>
                                </div>
                                <h3 id="ai-model-settings-title">{settingsHeaderTitle}</h3>
                                {!inputOnlySettings && <p>{formatPrice(headerPrice)}</p>}
                            </div>
                        </div>
                        <button
                            className={styles.aiModelModalClose}
                            type="button"
                            aria-label="Close model settings"
                            onClick={inputOnlySettings ? closeInputSettings : closeModelSettings}
                        >
                            <X size={18} />
                        </button>
                    </div>

                    <div
                        className={cn(
                            styles.aiModelSettingsBody,
                            textInputOnlySettings && styles.aiTextInputSettingsBody,
                        )}
                    >
                        {inputOnlySettings && renderChooseInputs([focusedSettingsInputKind])}
                        {!inputOnlySettings &&
                            renderSettingGroup(
                                'Generations',
                                GENERATION_COUNT_OPTIONS,
                                draftModelSettings.generationCount || 1,
                                value => updateDraftModelSetting('generationCount', value),
                                null,
                                renderGenerationOption,
                                styles.aiGenerationSettingsOption,
                            )}
                        {!inputOnlySettings &&
                            !showExtendSettings &&
                            renderSettingGroup(
                                durationPresentation.label,
                                durationOptions,
                                draftModelSettings.durationSeconds,
                                value => updateDraftModelSetting('durationSeconds', value),
                                durationPresentation.detail ||
                                    (pricePerSecond ? `${formatPrice(pricePerSecond)}/sec` : null),
                            )}
                        {!inputOnlySettings &&
                            !showExtendSettings &&
                            renderSettingGroup(
                                'Aspect Ratio',
                                aspectRatioOptions,
                                draftModelSettings.aspectRatio,
                                value => updateDraftModelSetting('aspectRatio', value),
                            )}
                        {!inputOnlySettings &&
                            showGifSettings &&
                            renderSettingGroup(
                                'Frame Rate',
                                gifFrameRateOptions,
                                draftModelSettings.gifFrameRate,
                                value => updateDraftModelSetting('gifFrameRate', value),
                                gifSettingsMeta,
                            )}
                        {!inputOnlySettings &&
                            showGifSettings &&
                            renderSettingGroup(
                                'Max Width',
                                gifMaxWidthOptions,
                                draftModelSettings.gifMaxWidth,
                                value => updateDraftModelSetting('gifMaxWidth', value),
                            )}
                        {!inputOnlySettings &&
                            showExtendSettings &&
                            renderSettingGroup(
                                'Duration',
                                durationOptions,
                                draftModelSettings.durationSeconds,
                                value => updateDraftModelSetting('durationSeconds', value),
                                pricePerSecond ? `${formatPrice(pricePerSecond)}/sec` : null,
                            )}
                        {!inputOnlySettings && showExtendSettings && (
                            <div className={styles.aiModelSettingsGroup}>
                                <div className={styles.aiModelSettingsLabel}>
                                    <span>Smart Stitch</span>
                                </div>
                                <div className={styles.aiModelSettingsOptions}>
                                    <button
                                        className={cn(
                                            styles.aiModelSettingsOption,
                                            draftModelSettings.smartStitch &&
                                                styles.aiModelSettingsOptionActive,
                                        )}
                                        type="button"
                                        disabled={settingsReadOnly}
                                        onClick={() =>
                                            updateDraftModelSetting(
                                                'smartStitch',
                                                !draftModelSettings.smartStitch,
                                            )
                                        }
                                    >
                                        <span>Smart Stitch</span>
                                        <span>Combine input + output</span>
                                    </button>
                                </div>
                            </div>
                        )}
                        {!inputOnlySettings && showScreenshotSettings && (
                            <div className={styles.aiModelSettingsGroup}>
                                <div className={styles.aiModelSettingsLabel}>
                                    <span>Time</span>
                                </div>
                                <VideoFramePicker
                                    video={videoInput}
                                    timestamp={draftModelSettings.screenshotTimestamp}
                                    placeholder={DEFAULT_SCREENSHOT_TIMESTAMP}
                                    emptyText="Choose a video to preview a frame"
                                    onTimestampChange={value =>
                                        updateDraftModelSetting('screenshotTimestamp', value)
                                    }
                                />
                            </div>
                        )}
                    </div>

                    {inputOnlySettings && !settingsReadOnly && renderInputDialogFooter()}

                    {!inputOnlySettings && !settingsReadOnly && (
                        <div className={styles.aiModelSettingsFooter}>
                            <button
                                className={styles.aiInputSecondaryButton}
                                type="button"
                                onClick={closeModelSettings}
                            >
                                Cancel
                            </button>
                            <button
                                className={styles.aiInputPrimaryButton}
                                type="button"
                                onClick={saveModelSettings}
                            >
                                {inputOnlySettings ? 'Done' : 'Apply'}
                            </button>
                        </div>
                    )}
                </div>
            </div>,
            'ai-action-model-settings-dialog',
        )
    }

    const renderSectionModels = (section: ModelSection) => {
        if (modelSearchQuery.trim()) {
            return <>{section.items.map(renderModelRow)}</>
        }

        const renderMoreModels = (models: ActionDefinition[]) => {
            if (models.length === 0) return null

            const isExpanded = !!expandedMoreSections[section.id]

            return (
                <>
                    <button
                        className={styles.aiModelMoreButton}
                        type="button"
                        aria-expanded={isExpanded}
                        onClick={() =>
                            setExpandedMoreSections(prev => ({
                                ...prev,
                                [section.id]: !prev[section.id],
                            }))
                        }
                    >
                        <span className={styles.aiModelMoreMeta}>
                            <ChevronDown
                                size={16}
                                className={cn(
                                    styles.aiModelMoreIcon,
                                    isExpanded && styles.aiModelMoreIconOpen,
                                )}
                            />
                        </span>
                        <span className={styles.aiModelMoreLabel}>
                            <span className={styles.aiModelMoreCount}>{models.length}</span>
                            <span>More</span>
                        </span>
                    </button>
                    {isExpanded && models.map(renderModelRow)}
                </>
            )
        }

        const partitionedModels = partitionActionDefinitionsByFold(section.id, section.items)
        if (!partitionedModels) {
            return <>{section.items.map(renderModelRow)}</>
        }

        return (
            <>
                {partitionedModels.topTier.map(renderModelRow)}
                {renderMoreModels(partitionedModels.more)}
            </>
        )
    }

    const renderModelCategory = (section: ModelSection) => {
        const SectionIcon = section.icon
        const sectionTitle =
            section.id === 'image'
                ? 'Create Image'
                : section.id === 'video'
                ? 'Create Video'
                : section.id === 'audio'
                ? 'Create Audio'
                : 'Media Tools'

        return (
            <section className={styles.aiModelCategory} key={section.id}>
                <h3 className={styles.aiModelCategoryLabel}>
                    <SectionIcon size={16} />
                    <span>{sectionTitle}</span>
                </h3>
                <div className={styles.aiModelList}>{renderSectionModels(section)}</div>
            </section>
        )
    }

    const modelFilterTabs: Array<{
        id: ModelFilterTab
        label: string
        icon: React.ElementType
    }> = [
        { id: 'image', label: 'Image models', icon: ImageIcon },
        { id: 'video', label: 'Video models', icon: VideoIcon },
        { id: 'audio', label: 'Audio models', icon: MusicIcon },
        { id: 'tools', label: 'Tools', icon: Wrench },
    ]

    const selectModelFilterTab = (
        tabId: ModelFilterTab,
        event?: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setIsModelSearchActive(false)
        setModelSearchQuery('')
        setActiveModelFilterTab(prev => (prev === tabId ? null : tabId))
        setExpandedMoreSections({})
        event?.currentTarget.blur()
    }

    const startModelSearch = () => {
        setActiveModelFilterTab(null)
        setModelSearchQuery('')
        setIsModelSearchActive(true)
        setExpandedMoreSections({})
    }

    const closeModelSearch = () => {
        setIsModelSearchActive(false)
        setModelSearchQuery('')
    }

    const renderModelFilterControls = () => {
        if (isModelSearchActive) {
            return (
                <div className={styles.aiModelSearchWrap}>
                    <Search size={16} />
                    <input
                        ref={modelSearchInputRef}
                        className={styles.aiModelSearchInput}
                        type="search"
                        value={modelSearchQuery}
                        placeholder="Search models..."
                        aria-label="Search AI models"
                        onChange={event => setModelSearchQuery(event.target.value)}
                        onBlur={() => window.setTimeout(closeModelSearch, 120)}
                        onKeyDown={event => {
                            if (event.key === 'Escape') {
                                closeModelSearch()
                            }
                        }}
                    />
                </div>
            )
        }

        const visibleFilterTabs = modelPickerScope
            ? modelFilterTabs.filter(tab => tab.id === modelPickerScope)
            : modelFilterTabs

        return (
            <div className={styles.aiModelFilterBlock}>
                <div className={styles.aiModelFilterHeader}>Filter</div>
                <div className={styles.aiModelFilterTabs} aria-label="Filter AI models">
                    {visibleFilterTabs.map(tab => {
                        const TabIcon = tab.icon
                        const isActive = modelPickerScope
                            ? modelPickerScope === tab.id
                            : activeModelFilterTab === tab.id
                        return (
                            <button
                                key={tab.id}
                                className={cn(
                                    styles.aiModelFilterTab,
                                    isActive && styles.aiModelFilterTabActive,
                                )}
                                type="button"
                                aria-label={tab.label}
                                aria-pressed={isActive}
                                onClick={event => {
                                    if (modelPickerScope) {
                                        event.currentTarget.blur()
                                        return
                                    }
                                    selectModelFilterTab(tab.id, event)
                                }}
                            >
                                <TabIcon size={18} />
                            </button>
                        )
                    })}
                    <button
                        className={styles.aiModelFilterTab}
                        type="button"
                        aria-label="Search models"
                        onClick={startModelSearch}
                    >
                        <Search size={18} />
                    </button>
                </div>
            </div>
        )
    }

    const renderModelModal = () => {
        if (!showMenu) return null

        if (directInputEdit && settingsModel && focusedSettingsInputKind) {
            return renderModelSettingsDialog()
        }

        const isEditingExistingActionSettings =
            !!editingActionRequestUpdaterRef.current &&
            settingsModel &&
            !focusedSettingsInputKind &&
            !directModelPickerEdit &&
            !directReferencesEdit &&
            !directExtendModelEdit &&
            !isSelectingExtendModel &&
            !referenceInputEdit

        if ((directSettingsEdit || isEditingExistingActionSettings) && settingsModel) {
            return renderModelSettingsDialog()
        }

        if (directReferencesEdit && referencesModel) {
            return renderReferenceSettingsDialog()
        }

        if (
            directExtendModelEdit &&
            isSelectingExtendModel &&
            settingsModel?.actionTagName === 'extend'
        ) {
            return renderExtendModelDialog()
        }

        const scopedModelLabel = modelPickerScope
            ? modelSections.find(section => section.id === modelPickerScope)?.label
            : null
        const modelModalTitle =
            directModelPickerEdit && scopedModelLabel
                ? `Choose ${scopedModelLabel} Model`
                : directModelPickerEdit
                ? 'Choose Model'
                : 'Choose Models'
        const selectedModelCount = selectedActionDefinitionIds.length

        return createPortal(
            <div
                className={styles.aiModelModalBackdrop}
                onMouseDown={event => {
                    keepComposerFocusOnPhoneTap(event)
                    if (event.target === event.currentTarget) closeMenu()
                }}
            >
                <div
                    className={styles.aiModelModal}
                    data-testid={menuTestId}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby="ai-model-modal-title"
                    onMouseDown={event => {
                        keepComposerFocusOnPhoneTap(event)
                        event.stopPropagation()
                    }}
                >
                    <div className={styles.aiModelModalHeader}>
                        <h2 id="ai-model-modal-title" className={styles.aiModelModalTitle}>
                            {modelModalTitle}
                        </h2>
                        <button
                            className={styles.aiModelModalClose}
                            type="button"
                            aria-label="Close"
                            onClick={closeMenu}
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className={styles.aiModelModalBody}>
                        <section className={styles.aiModelSection}>
                            {renderModelFilterControls()}

                            {visibleModelSections.length > 0 ? (
                                <div className={styles.aiModelCategoryList}>
                                    {visibleModelSections.map(section =>
                                        renderModelCategory(section),
                                    )}
                                </div>
                            ) : (
                                <div className={styles.aiMagicWandMenuEmpty}>
                                    {modelSearchQuery.trim()
                                        ? 'No matching models'
                                        : 'No models available'}
                                </div>
                            )}
                        </section>
                    </div>
                    {!directModelPickerEdit && (
                        <div className={styles.aiModelModalFooter}>
                            <span className={styles.aiModelSelectionCount}>
                                Selected {selectedModelCount}/{MAX_ACTION_REQUEST_SELECTIONS}
                            </span>
                            <div className={styles.aiModelModalFooterActions}>
                                <button
                                    className={styles.aiInputSecondaryButton}
                                    type="button"
                                    onClick={closeMenu}
                                >
                                    Cancel
                                </button>
                                <button
                                    className={styles.aiInputPrimaryButton}
                                    type="button"
                                    disabled={selectedModelCount === 0}
                                    onClick={addSelectedModels}
                                >
                                    {selectedModelCount > 0 ? `Add ${selectedModelCount}` : 'Add'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {renderModelSettingsDialog()}
                {renderReferenceSettingsDialog()}
                {isSelectingExtendModel &&
                    settingsModel?.actionTagName === 'extend' &&
                    renderExtendModelDialog()}
            </div>,
            document.body,
        )
    }

    return (
        <div
            className={cn(
                styles.aiMagicWandMenuButtonContainer,
                hideTrigger && styles.aiMagicWandMenuButtonContainerHidden,
                contextStyles,
            )}
        >
            {!hideTrigger && (
                <WithTooltip tip="AI Actions">
                    <div
                        className={styles.aiMagicWandMenuButtonUIMainComp}
                        data-testid={triggerTestId}
                        onClick={toggleMenu}
                        role="button"
                        tabIndex={0}
                        onKeyDown={event => {
                            if (event.key === 'Enter' || event.key === ' ') toggleMenu(event)
                        }}
                    >
                        {/* Single glyph, no ⋮ — the whole pill is one tap target
                            and both halves opened the same menu. */}
                        <div className={styles.toggleAiMagicWandMenuButton}>
                            <AiImageButtonIconSvg
                                className={cn(styles.aiMagicWandMenuButtonIcon, imageStyles)}
                                color="currentColor"
                            />
                        </div>
                    </div>
                </WithTooltip>
            )}
            {renderModelModal()}
            {renderMediaPreviewModal()}
        </div>
    )
}

const AiMagicWandMenuButton = forwardRef(AiMagicWandMenuButtonWithRef)

export default AiMagicWandMenuButton
